import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AutoComplete,
  Button,
  Checkbox,
  Col,
  Collapse,
  Divider,
  Flex,
  Form,
  Image,
  Input,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
  Switch,
  Typography,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import type { FormInstance, Rule } from 'antd/es/form';
import {
  CustomButton,
  CustomDatePicker,
  CustomInput,
  CustomSelect,
  CustomTimePicker,
  CustomTitle,
} from '../../components';
import CryoShipperFormBlock from './partials/CryoShipperFormBlock';
import {
  CRYO_FIXED_ACCESSORIES,
  CRYO_HIGH_VOLUME_RACK_ID,
  CRYO_RACK_OPTIONS,
  CRYO_SAFEPAK_900,
} from './utils/cryoportOptions';
import { useToken } from '../../common/helper';
import type { ApiResponse, SingleShipmentDataExtended } from '../../apis/types';
import {
  CreateCryoportBooking,
  CreateCryoportLocationTemplate,
  CreateNewShipment,
  CreateShipmentBooking,
  CreateWorldCourierBooking,
  CreateWorldCourierBookingTemplate,
  DeleteCryoportLocationTemplate,
  DeleteWorldCourierBookingTemplate,
  GetCryoportBookingById,
  GetCryoportLocationTemplates,
  GetShipmentBookingById,
  GetShipmentBookingTemplates,
  GetSingleShipment,
  GetQuickstatCompanyBookingProfile,
  type QuickstatCompanyBookingProfile,
  GetWorldCourierBookingById,
  GetWorldCourierBookingTemplates,
  GetWorldCourierProjectOptions,
  LookupShipmentByReference,
  PostCryoportBookingAction,
  PostShipmentBookingAction,
  PostWorldCourierBookingAction,
  PromoteShipment,
} from '../../apis/shipment';
import {
  BookSpecialtyShipment,
  GetSpecialtyOptions,
  type SpecialtyOptionsResponse,
  unwrapSpecialtyPayload,
} from '../../apis/specialtyBooking';
import useSpecialtyProviders from '../../hooks/useSpecialtyProviders';
import { formatQuoteApiError } from './QuoteShipmentDrawer/formatQuoteApiError';
import {
  attemptBooking,
  bookingFailureMessage,
  needsConfirmation,
  type BookingFailure,
  type BookingKeyRef,
} from './QuoteShipmentDrawer/bookingGuard';
import { warnFirstFormValidationError } from './bookFormValidation';
import { findSavedBooking } from './QuoteShipmentDrawer/savedBooking';
import SpecialtyBookingFields from './partials/SpecialtyBookingFields';
import SpecialtyZoomlogiFields from './partials/SpecialtyZoomlogiFields';
import {
  buildSpecialtyBookPayload,
  isIncompatibleProductTemperatureError,
  loadUnsavedSpecialtyBooking,
  parseSpecialtyTransportProvider,
  shellToSpecialtyFormValues,
  specialtyProviderOptions,
  storeUnsavedSpecialtyBooking,
  type SpecialtyFormValues,
  type UnsavedSpecialtyBooking,
} from './specialtyBookingHelpers';
import useFetchCompanyCouriers from '../../hooks/useFetchCompanyCouriers';
import {
  getCompanyCouriersDDL,
  getTeamsDDLOFFeature,
} from '../../common/utils/dropDown';
import {
  CRYOPORT_TRANSPORT_PROVIDER_ID,
  isCryoportCompany,
} from '../../common/constants/cryoport';
import { filterQuickStatCompanyCouriers } from '../../common/utils/quickStatCompanyCouriers';
import {
  commodityTemperatureSelectOptions,
  matchQuickstatOptionLabel,
  packagingSelectOptions,
  QUICKSTAT_COMMODITY_TEMPERATURE_LABELS,
  QUICKSTAT_PACKAGING_LABELS,
} from '../../constants/quickstatBookingFormOptions';
import {
  emailNotificationPreferencesForApi,
  emptyEmailNotificationPreferences,
  QUICKSTAT_EMAIL_NOTIFICATION_ROWS,
  type EmailNotificationPreferences,
} from '../../constants/quickstatEmailNotificationPreferences';
import {
  canBookWorldCourier,
  WORLD_COURIER_TRANSPORT_PROVIDER_ID,
} from '../../common/constants/worldCourier';
import { worldCourierItemNameSelectOptions } from '../../constants/worldCourierItemNameSelectOptions';
import {
  WC_BUNDLED_PACKAGE_DRY_ICE_SUPPLY,
  WC_TEMPERATURES_REQUIRING_DRY_ICE,
  wcPackageHasBundledDryIce,
  getValidExtraSupplyValuesForTemperature,
  getValidPackageNameValuesForTemperature,
  worldCourierDryIceSupplySelectOptions,
  worldCourierExtraSuppliesSelectOptions,
  worldCourierPackageNameSelectOptions,
  worldCourierTemperatureRangeSelectOptions,
} from '../../constants/worldCourierPackageFormOptions';
import type { WcProjectSelectOption } from '../../constants/worldCourierProjectSelectOptions';

import './BookShipmentDrawerWcSelect.css';
import styles from './BookShipmentDrawer.module.css';
import CustomDeleteModal from '../Modals/CustomDeleteModal';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
} from '../../assets/icons/icons';

import {
  buildWcDestTemplatePostBody,
  buildWcOriginTemplatePostBody,
  shouldAutoSaveDestTemplate,
  shouldAutoSaveOriginTemplate,
} from './utils/wcBookingTemplateAutosave';
import { coerceLegacyCountryCode } from './utils/wcBookingTemplateCountry';
import {
  filterWcTemplatesByProject,
  isWcTemplateCompatibleWithProject,
} from './utils/wcBookingTemplateFilter';
import {
  buildCryoDestLocationPostBody,
  buildCryoOriginLocationPostBody,
  type CryoLocationTemplateRowForAutosave,
  shouldAutoSaveCryoDestLocation,
  shouldAutoSaveCryoOriginLocation,
} from './utils/cryoLocationTemplateAutosave';
import QuickstatBookingCompletedModal from './QuickstatBookingCompletedModal';
import WorldCourierBookingCompletedModal from './WorldCourierBookingCompletedModal';
import CryoportBookingCompletedModal from './CryoportBookingCompletedModal';
import SpecialtyBookingCompletedModal from './SpecialtyBookingCompletedModal';
import {
  applyProductQuantityDelta,
  findBestWcTemplateForLocation,
  productsPayloadFromSelectedRows,
  quotePromoteTargetId,
  resolveCourierIdForTransportProvider,
  shellToCryoFormValues,
  shellToWcAddressOnlyFormValues,
  shouldShowQuoteTeamsProducts,
} from './shellShipmentHelpers';
import {
  MAX_QUANTITY_MESSAGE,
  MIN_QUANTITY_MESSAGE,
} from '@/utils/formatQuantity';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';
import { useSelector } from 'react-redux';
import type { TeamDropdownSelectTeam } from '../../components/TeamDropdownSelect';
import { usePaginatedProductDropdown } from '../../hooks/usePaginatedProductDropdown';
import type { SelectedProduct } from './components/ProductMultiSelectField';

type SpecialtyRefStatus = 'idle' | 'checking' | 'shell' | 'booked' | 'none';

/** Ant Select `popupClassName` for WC temperature / package lists (see CSS file). */
const WC_LONG_OPTION_SELECT_POPUP_CLASS = 'wc-select-long-options-popup';

/** AutoComplete option value: full Pickup/Delivery reset (not stored as company name). */
const WC_ENTER_NEW_ADDRESS_SENTINEL = '__WC_ENTER_NEW_ADDRESS__';

/** Prefix for profile rows so option values stay unique (stripped in `onSelect`; never stored as company name). */
const WC_TEMPLATE_AUTOCOMPLETE_PREFIX = 'wc-tpl:';

/** Cryoport sentinel for the "Enter new address" autocomplete option. */
const CRYO_ENTER_NEW_ADDRESS_SENTINEL = '__CRYO_ENTER_NEW_ADDRESS__';

function wcCompanyNameAutocompleteNormalize(
  val: unknown,
  prev: unknown
): unknown {
  if (
    val === WC_ENTER_NEW_ADDRESS_SENTINEL ||
    (typeof val === 'string' && val.trim() === WC_ENTER_NEW_ADDRESS_SENTINEL)
  ) {
    return undefined;
  }
  if (
    typeof val === 'string' &&
    val.startsWith(WC_TEMPLATE_AUTOCOMPLETE_PREFIX)
  ) {
    return prev;
  }
  return val;
}

/** AutoComplete may push option.value into the field before normalize; strip sentinel early. */
function wcCompanyNameAutocompleteGetValueFromEvent(
  ...args: unknown[]
): unknown {
  const v = args[0];
  if (
    v === WC_ENTER_NEW_ADDRESS_SENTINEL ||
    (typeof v === 'string' && v.trim() === WC_ENTER_NEW_ADDRESS_SENTINEL)
  ) {
    return undefined;
  }
  return v;
}

const WC_BOOKING_COLLAPSE_CLASS = 'wc-booking-collapse';
const WC_BOOKING_COLLAPSE_NESTED_CLASS =
  'wc-booking-collapse wc-booking-collapse--nested';

/** Same merge as submit `mergeDatetimeInputText` — used to compare WC schedule windows. */
function wcMergedScheduleMs(
  date: Dayjs | null | undefined,
  time: Dayjs | null | undefined
): number | null {
  if (date == null || typeof date !== 'object') return null;
  const base = date.startOf('day');
  const merged =
    time != null && typeof time === 'object'
      ? base.hour(time.hour()).minute(time.minute()).second(0)
      : base.hour(0).minute(0).second(0);
  return merged.valueOf();
}

const WC_PICKUP_SCHEDULE_ORDER_DEPS = [
  'wc_specific_pickup_enabled',
  'wc_earliest_pickup_date',
  'wc_earliest_pickup_time',
  'wc_latest_pickup_date',
  'wc_latest_pickup_time',
] as const;

const WC_DELIVERY_SCHEDULE_ORDER_DEPS = [
  'wc_specific_delivery_enabled',
  'wc_earliest_delivery_date',
  'wc_earliest_delivery_time',
  'wc_latest_delivery_date',
  'wc_latest_delivery_time',
] as const;

function wcPickupWindowOrderRule(): Rule {
  return ({ getFieldValue }) => ({
    validator() {
      if (getFieldValue('wc_specific_pickup_enabled')) {
        return Promise.resolve();
      }
      const earliest = wcMergedScheduleMs(
        getFieldValue('wc_earliest_pickup_date'),
        getFieldValue('wc_earliest_pickup_time')
      );
      const latest = wcMergedScheduleMs(
        getFieldValue('wc_latest_pickup_date'),
        getFieldValue('wc_latest_pickup_time')
      );
      if (earliest == null || latest == null) return Promise.resolve();
      if (earliest > latest) {
        return Promise.reject(
          new Error('Earliest pick-up must be on or before latest pick-up')
        );
      }
      return Promise.resolve();
    },
  });
}

function wcDeliveryWindowOrderRule(): Rule {
  return ({ getFieldValue }) => ({
    validator() {
      if (getFieldValue('wc_specific_delivery_enabled')) {
        return Promise.resolve();
      }
      const earliest = wcMergedScheduleMs(
        getFieldValue('wc_earliest_delivery_date'),
        getFieldValue('wc_earliest_delivery_time')
      );
      const latest = wcMergedScheduleMs(
        getFieldValue('wc_latest_delivery_date'),
        getFieldValue('wc_latest_delivery_time')
      );
      if (earliest == null || latest == null) return Promise.resolve();
      if (earliest > latest) {
        return Promise.reject(
          new Error('Earliest delivery must be on or before latest delivery')
        );
      }
      return Promise.resolve();
    },
  });
}

const { TextArea } = Input;
const { Text, Link } = Typography;

/** Parse GET /world-courier-bookings/:id/ envelope for House Waybill PDF URL. */
function extractWcHouseWaybillPdfUrl(
  res: ApiResponse | undefined
): string | null {
  const wrap = res?.data?.data as
    | {
        data?: { wc_portal_house_waybill_pdf_url?: string | null };
        wc_portal_house_waybill_pdf_url?: string | null;
      }
    | undefined;
  const nested = wrap?.data;
  const fromNested =
    typeof nested?.wc_portal_house_waybill_pdf_url === 'string'
      ? nested.wc_portal_house_waybill_pdf_url.trim()
      : '';
  const fromWrap =
    typeof wrap?.wc_portal_house_waybill_pdf_url === 'string'
      ? wrap.wc_portal_house_waybill_pdf_url.trim()
      : '';
  const url = fromNested || fromWrap;
  return url || null;
}

function extractCryoportPrepareState(res: ApiResponse | undefined): {
  bookingStatus: string | null;
  portalOrderNumber: string | null;
  reviewScreenshotUrl: string | null;
} {
  const wrap = res?.data?.data as
    | {
        data?: {
          booking_status?: string | null;
          cryo_portal_order_number?: string | null;
          cryo_review_screenshot_url?: string | null;
        };
        booking_status?: string | null;
        cryo_portal_order_number?: string | null;
        cryo_review_screenshot_url?: string | null;
      }
    | undefined;
  const nested = wrap?.data;
  const status = (nested?.booking_status ?? wrap?.booking_status ?? null) as
    string | null;
  const order = (nested?.cryo_portal_order_number ??
    wrap?.cryo_portal_order_number ??
    null) as string | null;
  const screenshot = (nested?.cryo_review_screenshot_url ??
    wrap?.cryo_review_screenshot_url ??
    null) as string | null;
  return {
    bookingStatus: status ? String(status) : null,
    portalOrderNumber: order ? String(order).trim() : null,
    reviewScreenshotUrl: screenshot ? String(screenshot).trim() : null,
  };
}
const { Panel } = Collapse;

interface BookShipmentDrawerProps {
  onClose: () => void;
  shellShipment?: SingleShipmentDataExtended;
  onPromoteSuccess?: () => void;
}

/** Saved individual location rows from `/shipment/cryoport-location-templates/`. */
interface CryoLocationTemplateRow {
  id: string;
  location_label: string;
  cryo_name?: string | null;
  cryo_nickname?: string | null;
  cryo_site_template_id?: number | null;
  cryo_location_id?: number | string | null;
  cryo_street_address_line_one?: string | null;
  cryo_street_address_line_two?: string | null;
  cryo_city?: string | null;
  cryo_state?: string | null;
  cryo_zip_code?: string | null;
  cryo_country_code?: string | null;
  cryo_time_zone?: string | null;
  cryo_residential?: boolean;
  cryo_contact_id?: number | string | null;
  cryo_contact_first_name?: string | null;
  cryo_contact_last_name?: string | null;
  cryo_contact_email?: string | null;
  cryo_contact_phone_number?: string | null;
  cryo_site_process?: string | null;
}

interface WorldCourierBookingTemplateRow {
  id: string;
  company_name: string;
  contact_name?: string | null;
  phone_number?: string | null;
  extension?: string | null;
  email?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state_province?: string | null;
  postal_code?: string | null;
  country?: string | null;
  pickup_instructions?: string | null;
  delivery_instructions?: string | null;
  wc_project?: string | null;
}

function buildWcCompanyProfileAutocompleteOptions(
  side: 'origin' | 'destination',
  templates: WorldCourierBookingTemplateRow[],
  onArchive: (templateId: string, side: 'origin' | 'destination') => void
) {
  const profileOpts = templates.map((t) => {
    const prefixed = `${WC_TEMPLATE_AUTOCOMPLETE_PREFIX}${t.id}`;
    return {
      value: prefixed,
      wcFilterLabel: t.company_name,
      label: (
        <div className={styles.optionRow}>
          <span className={styles.optionText}>{t.company_name}</span>
          <button
            type='button'
            aria-label='Archive saved profile'
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              onArchive(t.id, side);
            }}
            className={styles.optionIconButton}
          >
            <DeleteOutlined />
          </button>
        </div>
      ),
    };
  });
  return [
    { label: 'Enter new address', value: WC_ENTER_NEW_ADDRESS_SENTINEL },
    ...profileOpts,
  ];
}

/** Prefix distinguishing individual location templates from paired route templates. */
const CRYO_LOCATION_TEMPLATE_PREFIX = 'cryo-loc:';

function buildCryoCompanyProfileAutocompleteOptions(
  locationTemplates: CryoLocationTemplateRow[],
  onDeleteLocation: (templateId: string) => void
) {
  const locationOpts = [...locationTemplates]
    .sort((a, b) =>
      (a.cryo_name ?? a.location_label).localeCompare(
        b.cryo_name ?? b.location_label,
        undefined,
        { sensitivity: 'base' }
      )
    )
    .map((t) => {
      const name = t.cryo_name ?? t.location_label;
      return {
        value: `${CRYO_LOCATION_TEMPLATE_PREFIX}${t.id}`,
        cryoFilterLabel: name,
        label: (
          <div className={styles.optionRow}>
            <span className={styles.optionTextNowrap}>{name}</span>
            <button
              type='button'
              aria-label='Delete saved location'
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteLocation(t.id);
              }}
              className={styles.optionIconButton}
            >
              <DeleteOutlined />
            </button>
          </div>
        ),
      };
    });

  return [
    { label: 'Enter new address', value: CRYO_ENTER_NEW_ADDRESS_SENTINEL },
    ...locationOpts,
  ];
}

interface CryoCommodityFormRow {
  commodity_id?: number | string;
  lot_number?: string;
  quantity?: number | string;
  price_currency?: string;
  price_per_unit?: number | string;
  price_total?: number | string;
  primary_container_type?: string;
  primary_container_type_description?: string;
  primary_container_volume?: string;
  name?: string;
  description?: string;
  commodity_group_id?: number | string;
  commodity_category_id?: number | string;
  harmonized_code?: string;
  country_of_origin?: string;
  leg_2?: boolean;
}

interface CryoAccessoryFormRow {
  accessory_id?: number | string;
  quantity?: number | string;
  label?: string;
}

const CRYO_PICKUP_TIME_OPTIONS = [
  { value: '00:00', label: '12:00 AM' },
  { value: '00:30', label: '12:30 AM' },
  { value: '01:00', label: ' 1:00 AM' },
  { value: '01:30', label: ' 1:30 AM' },
  { value: '02:00', label: ' 2:00 AM' },
  { value: '02:30', label: ' 2:30 AM' },
  { value: '03:00', label: ' 3:00 AM' },
  { value: '03:30', label: ' 3:30 AM' },
  { value: '04:00', label: ' 4:00 AM' },
  { value: '04:30', label: ' 4:30 AM' },
  { value: '05:00', label: ' 5:00 AM' },
  { value: '05:30', label: ' 5:30 AM' },
  { value: '06:00', label: ' 6:00 AM' },
  { value: '06:30', label: ' 6:30 AM' },
  { value: '07:00', label: ' 7:00 AM' },
  { value: '07:30', label: ' 7:30 AM' },
  { value: '08:00', label: ' 8:00 AM' },
  { value: '08:30', label: ' 8:30 AM' },
  { value: '09:00', label: ' 9:00 AM' },
  { value: '09:30', label: ' 9:30 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '10:30', label: '10:30 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '11:30', label: '11:30 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '12:30', label: '12:30 PM' },
  { value: '13:00', label: ' 1:00 PM' },
  { value: '13:30', label: ' 1:30 PM' },
  { value: '14:00', label: ' 2:00 PM' },
  { value: '14:30', label: ' 2:30 PM' },
  { value: '15:00', label: ' 3:00 PM' },
  { value: '15:30', label: ' 3:30 PM' },
  { value: '16:00', label: ' 4:00 PM' },
  { value: '16:30', label: ' 4:30 PM' },
  { value: '17:00', label: ' 5:00 PM' },
  { value: '17:30', label: ' 5:30 PM' },
  { value: '18:00', label: ' 6:00 PM' },
  { value: '18:30', label: ' 6:30 PM' },
  { value: '19:00', label: ' 7:00 PM' },
  { value: '19:30', label: ' 7:30 PM' },
  { value: '20:00', label: ' 8:00 PM' },
  { value: '20:30', label: ' 8:30 PM' },
  { value: '21:00', label: ' 9:00 PM' },
  { value: '21:30', label: ' 9:30 PM' },
  { value: '22:00', label: '10:00 PM' },
  { value: '22:30', label: '10:30 PM' },
  { value: '23:00', label: '11:00 PM' },
  { value: '23:30', label: '11:30 PM' },
];

interface BookShipmentFormValues {
  transport_provider?: string;
  account_selection?: string;
  template_selection?: string;
  /** Set when origin company is chosen from WC templates list (not sent to booking API). */
  wc_origin_template_id?: string;
  wc_dest_template_id?: string;
  wc_origin_company_name?: string;
  wc_origin_contact_name?: string;
  wc_origin_phone_number?: string;
  wc_origin_extension?: string;
  wc_origin_email?: string;
  wc_origin_address1?: string;
  wc_origin_address2?: string;
  wc_origin_city?: string;
  wc_origin_state_province?: string;
  wc_origin_postal_code?: string;
  wc_origin_country?: string;
  wc_pickup_instructions?: string;
  wc_earliest_pickup_date?: Dayjs | null;
  wc_earliest_pickup_time?: Dayjs | null;
  wc_latest_pickup_date?: Dayjs | null;
  wc_latest_pickup_time?: Dayjs | null;
  wc_specific_pickup_enabled?: boolean;
  wc_specific_pickup_date?: Dayjs | null;
  wc_specific_pickup_time?: Dayjs | null;
  wc_origin_all_email_notifications?: boolean;
  wc_dest_company_name?: string;
  wc_dest_contact_name?: string;
  wc_dest_phone_number?: string;
  wc_dest_extension?: string;
  wc_dest_email?: string;
  wc_dest_address1?: string;
  wc_dest_address2?: string;
  wc_dest_city?: string;
  wc_dest_state_province?: string;
  wc_dest_postal_code?: string;
  wc_dest_country?: string;
  wc_delivery_instructions?: string;
  wc_dest_all_email_notifications?: boolean;
  wc_earliest_delivery_date?: Dayjs | null;
  wc_earliest_delivery_time?: Dayjs | null;
  wc_latest_delivery_date?: Dayjs | null;
  wc_latest_delivery_time?: Dayjs | null;
  wc_specific_delivery_enabled?: boolean;
  wc_specific_delivery_date?: Dayjs | null;
  wc_specific_delivery_time?: Dayjs | null;
  wc_charge_code?: string;
  wc_study?: string;
  wc_project?: string;
  wc_reference_code?: string;
  wc_additional_notes?: string;
  pickup_name?: string;
  pickup_address?: string;
  pickup_address2?: string;
  pickup_address3?: string;
  pickup_city?: string;
  pickup_state?: string;
  pickup_zip?: string;
  pickup_country?: string;
  pickup_person?: string;
  pickup_phone?: string;
  pickup_email?: string;
  pickup_special_instructions?: string;
  shipment_ready_date?: Dayjs | null;
  shipment_ready_time?: Dayjs | null;
  desired_delivery_type?: 'asap' | 'custom';
  desired_delivery_date?: Dayjs | null;
  desired_delivery_time?: Dayjs | null;
  package_packout_date?: Dayjs | null;
  package_packout_time?: Dayjs | null;
  service_type?: string;
  commodity?: string;
  commodity_temperature?: string;
  packaging?: string;
  shipment_reference?: string;
  emergency_contact_number?: string;
  email_notification_preferences?: EmailNotificationPreferences;
  delivery_name?: string;
  delivery_address?: string;
  delivery_address2?: string;
  delivery_address3?: string;
  delivery_city?: string;
  delivery_state?: string;
  delivery_zip?: string;
  delivery_country?: string;
  delivery_person?: string;
  delivery_phone?: string;
  delivery_email?: string;
  delivery_special_instructions?: string;
  wc_packages?: Array<{
    temperature_range_refrigerant?: string;
    external_boxes_count?: string;
    package_name?: string;
    dry_ice_supply?: string;
    dry_ice_weight_lbs?: string | number;
    extra_supplies?: string[];
    send_temperature_readings_to?: string;
    temperature_probe_instructions?: string;
    items?: Array<{
      item_name?: string;
      quantity?: string;
      description?: string;
      item_weight_lbs?: string;
      temperature_range_refrigerant?: string;
      harmonized_tariff_code?: string;
      declared_value_usd?: string;
      contains_dangerous_goods?: boolean;
    }>;
  }>;
  cryo_origin_site_template_id?: number;
  cryo_dest_site_template_id?: number;
  cryo_origin_name?: string;
  cryo_origin_nickname?: string;
  cryo_origin_location_id?: number | string;
  cryo_origin_latitude?: string | number;
  cryo_origin_longitude?: string | number;
  cryo_origin_geocode_accuracy?: string;
  cryo_origin_street_address_line_one?: string;
  cryo_origin_street_address_line_two?: string;
  cryo_origin_city?: string;
  cryo_origin_state?: string;
  cryo_origin_zip_code?: string;
  cryo_origin_country_code?: string;
  cryo_origin_time_zone?: string;
  cryo_origin_residential?: boolean;
  cryo_origin_contact_id?: number | string;
  cryo_origin_contact_first_name?: string;
  cryo_origin_contact_last_name?: string;
  cryo_origin_contact_email?: string;
  cryo_origin_contact_phone_number?: string;
  cryo_origin_site_process?: string;
  cryo_dest_name?: string;
  cryo_dest_nickname?: string;
  cryo_dest_location_id?: number | string;
  cryo_dest_latitude?: string | number;
  cryo_dest_longitude?: string | number;
  cryo_dest_geocode_accuracy?: string;
  cryo_dest_street_address_line_one?: string;
  cryo_dest_street_address_line_two?: string;
  cryo_dest_city?: string;
  cryo_dest_state?: string;
  cryo_dest_zip_code?: string;
  cryo_dest_country_code?: string;
  cryo_dest_time_zone?: string;
  cryo_dest_residential?: boolean;
  cryo_dest_contact_id?: number | string;
  cryo_dest_contact_first_name?: string;
  cryo_dest_contact_last_name?: string;
  cryo_dest_contact_email?: string;
  cryo_dest_contact_phone_number?: string;
  cryo_dest_site_process?: string;
  cryo_commodities?: CryoCommodityFormRow[];
  cryo_accessories?: CryoAccessoryFormRow[];
  cryo_rack_id?: number | null;
  cryo_delivery_date?: Dayjs | null;
  cryo_pickup_date?: Dayjs | null;
  cryo_pickup_start_time?: string;
  cryo_pickup_end_time?: string;
  cryo_leg3_pickup_date?: Dayjs | null;
  cryo_leg3_pickup_start_time?: string;
  cryo_leg3_pickup_end_time?: string;
  cryo_po_number?: string;
  cryo_study_number?: string;
  cryo_leg2_shipment_reference?: string;
  specialty_shipper?: SpecialtyFormValues['specialty_shipper'];
  specialty_recipient?: SpecialtyFormValues['specialty_recipient'];
  specialty_ready_date?: SpecialtyFormValues['specialty_ready_date'];
  specialty_ready_time?: SpecialtyFormValues['specialty_ready_time'];
  specialty_reference?: string;
  specialty_contents?: string;
  specialty_special_instructions?: string;
  specialty_notify_email?: string;
  specialty_pieces?: SpecialtyFormValues['specialty_pieces'];
  specialty_provider_fields?: SpecialtyFormValues['specialty_provider_fields'];
}
const _CRYO_SITE_PROCESS_SELECT_OPTIONS = [
  { label: 'Update selected site with new information', value: 'update' },
  { label: 'Save as new site for future use', value: 'new' },
  {
    label: 'One-off for this shipment (do not save as site)',
    value: 'no_site',
  },
];

function cryoStrOrNull(v: unknown): string | null {
  if (v == null) return null;
  const t = String(v).trim();
  return t || null;
}

/** Positive integers only (site template, shipper, contact id, location id). */
function cryoPosIntOrNull(v: unknown): number | null {
  if (v === '' || v == null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 1) return null;
  return Math.trunc(n);
}

function cryoValidSiteProcess(v: unknown): string | null {
  const s = cryoStrOrNull(v);
  if (!s) return null;
  const allowed = new Set(['update', 'new', 'no_site']);
  return allowed.has(s) ? s : null;
}

function appendCryoportRouteFieldsToPayload(
  payload: Record<string, unknown>,
  values: BookShipmentFormValues
): void {
  payload.cryo_origin_site_template_id = cryoPosIntOrNull(
    values.cryo_origin_site_template_id
  );
  payload.cryo_dest_site_template_id = cryoPosIntOrNull(
    values.cryo_dest_site_template_id
  );

  payload.cryo_origin_name = cryoStrOrNull(values.cryo_origin_name);
  payload.cryo_origin_nickname = cryoStrOrNull(values.cryo_origin_nickname);
  payload.cryo_origin_location_id = cryoPosIntOrNull(
    values.cryo_origin_location_id
  );
  payload.cryo_origin_latitude = cryoStrOrNull(values.cryo_origin_latitude);
  payload.cryo_origin_longitude = cryoStrOrNull(values.cryo_origin_longitude);
  payload.cryo_origin_geocode_accuracy = cryoStrOrNull(
    values.cryo_origin_geocode_accuracy
  );
  payload.cryo_origin_street_address_line_one = cryoStrOrNull(
    values.cryo_origin_street_address_line_one
  );
  payload.cryo_origin_street_address_line_two = cryoStrOrNull(
    values.cryo_origin_street_address_line_two
  );
  payload.cryo_origin_city = cryoStrOrNull(values.cryo_origin_city);
  payload.cryo_origin_state = cryoStrOrNull(values.cryo_origin_state);
  payload.cryo_origin_zip_code = cryoStrOrNull(values.cryo_origin_zip_code);
  payload.cryo_origin_country_code = cryoStrOrNull(
    values.cryo_origin_country_code
  );
  payload.cryo_origin_time_zone = cryoStrOrNull(values.cryo_origin_time_zone);
  payload.cryo_origin_residential = Boolean(values.cryo_origin_residential);
  payload.cryo_origin_contact_id = cryoPosIntOrNull(
    values.cryo_origin_contact_id
  );
  payload.cryo_origin_contact_first_name = cryoStrOrNull(
    values.cryo_origin_contact_first_name
  );
  payload.cryo_origin_contact_last_name = cryoStrOrNull(
    values.cryo_origin_contact_last_name
  );
  payload.cryo_origin_contact_email = cryoStrOrNull(
    values.cryo_origin_contact_email
  );
  payload.cryo_origin_contact_phone_number = cryoStrOrNull(
    values.cryo_origin_contact_phone_number
  );
  payload.cryo_origin_site_process = cryoValidSiteProcess(
    values.cryo_origin_site_process
  );

  payload.cryo_dest_name = cryoStrOrNull(values.cryo_dest_name);
  payload.cryo_dest_nickname = cryoStrOrNull(values.cryo_dest_nickname);
  payload.cryo_dest_location_id = cryoPosIntOrNull(
    values.cryo_dest_location_id
  );
  payload.cryo_dest_latitude = cryoStrOrNull(values.cryo_dest_latitude);
  payload.cryo_dest_longitude = cryoStrOrNull(values.cryo_dest_longitude);
  payload.cryo_dest_geocode_accuracy = cryoStrOrNull(
    values.cryo_dest_geocode_accuracy
  );
  payload.cryo_dest_street_address_line_one = cryoStrOrNull(
    values.cryo_dest_street_address_line_one
  );
  payload.cryo_dest_street_address_line_two = cryoStrOrNull(
    values.cryo_dest_street_address_line_two
  );
  payload.cryo_dest_city = cryoStrOrNull(values.cryo_dest_city);
  payload.cryo_dest_state = cryoStrOrNull(values.cryo_dest_state);
  payload.cryo_dest_zip_code = cryoStrOrNull(values.cryo_dest_zip_code);
  payload.cryo_dest_country_code = cryoStrOrNull(values.cryo_dest_country_code);
  payload.cryo_dest_time_zone = cryoStrOrNull(values.cryo_dest_time_zone);
  payload.cryo_dest_residential = Boolean(values.cryo_dest_residential);
  payload.cryo_dest_contact_id = cryoPosIntOrNull(values.cryo_dest_contact_id);
  payload.cryo_dest_contact_first_name = cryoStrOrNull(
    values.cryo_dest_contact_first_name
  );
  payload.cryo_dest_contact_last_name = cryoStrOrNull(
    values.cryo_dest_contact_last_name
  );
  payload.cryo_dest_contact_email = cryoStrOrNull(
    values.cryo_dest_contact_email
  );
  payload.cryo_dest_contact_phone_number = cryoStrOrNull(
    values.cryo_dest_contact_phone_number
  );
  payload.cryo_dest_site_process = cryoValidSiteProcess(
    values.cryo_dest_site_process
  );
}

/** Full Cryoportal country list (same options as portal country_code select, US first). */
const CRYO_COUNTRY_OPTIONS = [
  { label: 'United States', value: 'US' },
  { label: 'Afghanistan', value: 'AF' },
  { label: 'Åland Islands', value: 'AX' },
  { label: 'Albania', value: 'AL' },
  { label: 'Algeria', value: 'DZ' },
  { label: 'American Samoa', value: 'AS' },
  { label: 'Andorra', value: 'AD' },
  { label: 'Angola', value: 'AO' },
  { label: 'Anguilla', value: 'AI' },
  { label: 'Antarctica', value: 'AQ' },
  { label: 'Antigua and Barbuda', value: 'AG' },
  { label: 'Argentina', value: 'AR' },
  { label: 'Armenia', value: 'AM' },
  { label: 'Aruba', value: 'AW' },
  { label: 'Australia', value: 'AU' },
  { label: 'Austria', value: 'AT' },
  { label: 'Azerbaijan', value: 'AZ' },
  { label: 'Bahamas', value: 'BS' },
  { label: 'Bahrain', value: 'BH' },
  { label: 'Bangladesh', value: 'BD' },
  { label: 'Barbados', value: 'BB' },
  { label: 'Belarus', value: 'BY' },
  { label: 'Belgium', value: 'BE' },
  { label: 'Belize', value: 'BZ' },
  { label: 'Benin', value: 'BJ' },
  { label: 'Bermuda', value: 'BM' },
  { label: 'Bhutan', value: 'BT' },
  { label: 'Bolivia, Plurinational State of', value: 'BO' },
  { label: 'Bonaire, Sint Eustatius and Saba', value: 'BQ' },
  { label: 'Bosnia and Herzegovina', value: 'BA' },
  { label: 'Botswana', value: 'BW' },
  { label: 'Bouvet Island', value: 'BV' },
  { label: 'Brazil', value: 'BR' },
  { label: 'British Indian Ocean Territory', value: 'IO' },
  { label: 'Brunei Darussalam', value: 'BN' },
  { label: 'Bulgaria', value: 'BG' },
  { label: 'Burkina Faso', value: 'BF' },
  { label: 'Burundi', value: 'BI' },
  { label: 'Cambodia', value: 'KH' },
  { label: 'Cameroon', value: 'CM' },
  { label: 'Canada', value: 'CA' },
  { label: 'Cape Verde', value: 'CV' },
  { label: 'Cayman Islands', value: 'KY' },
  { label: 'Central African Republic', value: 'CF' },
  { label: 'Chad', value: 'TD' },
  { label: 'Chile', value: 'CL' },
  { label: 'China', value: 'CN' },
  { label: 'Christmas Island', value: 'CX' },
  { label: 'Cocos (Keeling) Islands', value: 'CC' },
  { label: 'Colombia', value: 'CO' },
  { label: 'Comoros', value: 'KM' },
  { label: 'Congo', value: 'CG' },
  { label: 'Congo, The Democratic Republic of the', value: 'CD' },
  { label: 'Cook Islands', value: 'CK' },
  { label: 'Costa Rica', value: 'CR' },
  { label: "Côte d'Ivoire", value: 'CI' },
  { label: 'Croatia', value: 'HR' },
  { label: 'Cuba', value: 'CU' },
  { label: 'Curaçao', value: 'CW' },
  { label: 'Cyprus', value: 'CY' },
  { label: 'Czech Republic', value: 'CZ' },
  { label: 'Denmark', value: 'DK' },
  { label: 'Djibouti', value: 'DJ' },
  { label: 'Dominica', value: 'DM' },
  { label: 'Dominican Republic', value: 'DO' },
  { label: 'Ecuador', value: 'EC' },
  { label: 'Egypt', value: 'EG' },
  { label: 'El Salvador', value: 'SV' },
  { label: 'Equatorial Guinea', value: 'GQ' },
  { label: 'Eritrea', value: 'ER' },
  { label: 'Estonia', value: 'EE' },
  { label: 'Ethiopia', value: 'ET' },
  { label: 'Falkland Islands (Malvinas)', value: 'FK' },
  { label: 'Faroe Islands', value: 'FO' },
  { label: 'Fiji', value: 'FJ' },
  { label: 'Finland', value: 'FI' },
  { label: 'France', value: 'FR' },
  { label: 'French Guiana', value: 'GF' },
  { label: 'French Polynesia', value: 'PF' },
  { label: 'French Southern Territories', value: 'TF' },
  { label: 'Gabon', value: 'GA' },
  { label: 'Gambia', value: 'GM' },
  { label: 'Georgia', value: 'GE' },
  { label: 'Germany', value: 'DE' },
  { label: 'Ghana', value: 'GH' },
  { label: 'Gibraltar', value: 'GI' },
  { label: 'Greece', value: 'GR' },
  { label: 'Greenland', value: 'GL' },
  { label: 'Grenada', value: 'GD' },
  { label: 'Guadeloupe', value: 'GP' },
  { label: 'Guam', value: 'GU' },
  { label: 'Guatemala', value: 'GT' },
  { label: 'Guernsey', value: 'GG' },
  { label: 'Guinea', value: 'GN' },
  { label: 'Guinea-Bissau', value: 'GW' },
  { label: 'Guyana', value: 'GY' },
  { label: 'Haiti', value: 'HT' },
  { label: 'Heard Island and McDonald Islands', value: 'HM' },
  { label: 'Holy See (Vatican City State)', value: 'VA' },
  { label: 'Honduras', value: 'HN' },
  { label: 'Hong Kong', value: 'HK' },
  { label: 'Hungary', value: 'HU' },
  { label: 'Iceland', value: 'IS' },
  { label: 'India', value: 'IN' },
  { label: 'Indonesia', value: 'ID' },
  { label: 'Iran, Islamic Republic of', value: 'IR' },
  { label: 'Iraq', value: 'IQ' },
  { label: 'Ireland', value: 'IE' },
  { label: 'Isle of Man', value: 'IM' },
  { label: 'Israel', value: 'IL' },
  { label: 'Italy', value: 'IT' },
  { label: 'Jamaica', value: 'JM' },
  { label: 'Japan', value: 'JP' },
  { label: 'Jersey', value: 'JE' },
  { label: 'Jordan', value: 'JO' },
  { label: 'Kazakhstan', value: 'KZ' },
  { label: 'Kenya', value: 'KE' },
  { label: 'Kiribati', value: 'KI' },
  { label: "Korea, Democratic People's Republic of", value: 'KP' },
  { label: 'Korea, Republic of', value: 'KR' },
  { label: 'Kuwait', value: 'KW' },
  { label: 'Kyrgyzstan', value: 'KG' },
  { label: "Lao People's Democratic Republic", value: 'LA' },
  { label: 'Latvia', value: 'LV' },
  { label: 'Lebanon', value: 'LB' },
  { label: 'Lesotho', value: 'LS' },
  { label: 'Liberia', value: 'LR' },
  { label: 'Libya', value: 'LY' },
  { label: 'Liechtenstein', value: 'LI' },
  { label: 'Lithuania', value: 'LT' },
  { label: 'Luxembourg', value: 'LU' },
  { label: 'Macao', value: 'MO' },
  { label: 'Macedonia, Republic of', value: 'MK' },
  { label: 'Madagascar', value: 'MG' },
  { label: 'Malawi', value: 'MW' },
  { label: 'Malaysia', value: 'MY' },
  { label: 'Maldives', value: 'MV' },
  { label: 'Mali', value: 'ML' },
  { label: 'Malta', value: 'MT' },
  { label: 'Marshall Islands', value: 'MH' },
  { label: 'Martinique', value: 'MQ' },
  { label: 'Mauritania', value: 'MR' },
  { label: 'Mauritius', value: 'MU' },
  { label: 'Mayotte', value: 'YT' },
  { label: 'Mexico', value: 'MX' },
  { label: 'Micronesia, Federated States of', value: 'FM' },
  { label: 'Moldova, Republic of', value: 'MD' },
  { label: 'Monaco', value: 'MC' },
  { label: 'Mongolia', value: 'MN' },
  { label: 'Montenegro', value: 'ME' },
  { label: 'Montserrat', value: 'MS' },
  { label: 'Morocco', value: 'MA' },
  { label: 'Mozambique', value: 'MZ' },
  { label: 'Myanmar', value: 'MM' },
  { label: 'Namibia', value: 'NA' },
  { label: 'Nauru', value: 'NR' },
  { label: 'Nepal', value: 'NP' },
  { label: 'Netherlands', value: 'NL' },
  { label: 'New Caledonia', value: 'NC' },
  { label: 'New Zealand', value: 'NZ' },
  { label: 'Nicaragua', value: 'NI' },
  { label: 'Niger', value: 'NE' },
  { label: 'Nigeria', value: 'NG' },
  { label: 'Niue', value: 'NU' },
  { label: 'Norfolk Island', value: 'NF' },
  { label: 'Northern Mariana Islands', value: 'MP' },
  { label: 'Norway', value: 'NO' },
  { label: 'Oman', value: 'OM' },
  { label: 'Pakistan', value: 'PK' },
  { label: 'Palau', value: 'PW' },
  { label: 'Palestine, State of', value: 'PS' },
  { label: 'Panama', value: 'PA' },
  { label: 'Papua New Guinea', value: 'PG' },
  { label: 'Paraguay', value: 'PY' },
  { label: 'Peru', value: 'PE' },
  { label: 'Philippines', value: 'PH' },
  { label: 'Pitcairn', value: 'PN' },
  { label: 'Poland', value: 'PL' },
  { label: 'Portugal', value: 'PT' },
  { label: 'Puerto Rico', value: 'PR' },
  { label: 'Qatar', value: 'QA' },
  { label: 'Réunion', value: 'RE' },
  { label: 'Romania', value: 'RO' },
  { label: 'Russian Federation', value: 'RU' },
  { label: 'Rwanda', value: 'RW' },
  { label: 'Saint Barthélemy', value: 'BL' },
  { label: 'Saint Helena, Ascension and Tristan da Cunha', value: 'SH' },
  { label: 'Saint Kitts and Nevis', value: 'KN' },
  { label: 'Saint Lucia', value: 'LC' },
  { label: 'Saint Martin (French part)', value: 'MF' },
  { label: 'Saint Pierre and Miquelon', value: 'PM' },
  { label: 'Saint Vincent and the Grenadines', value: 'VC' },
  { label: 'Samoa', value: 'WS' },
  { label: 'San Marino', value: 'SM' },
  { label: 'Sao Tome and Principe', value: 'ST' },
  { label: 'Saudi Arabia', value: 'SA' },
  { label: 'Senegal', value: 'SN' },
  { label: 'Serbia', value: 'RS' },
  { label: 'Seychelles', value: 'SC' },
  { label: 'Sierra Leone', value: 'SL' },
  { label: 'Singapore', value: 'SG' },
  { label: 'Sint Maarten (Dutch part)', value: 'SX' },
  { label: 'Slovakia', value: 'SK' },
  { label: 'Slovenia', value: 'SI' },
  { label: 'Solomon Islands', value: 'SB' },
  { label: 'Somalia', value: 'SO' },
  { label: 'South Africa', value: 'ZA' },
  { label: 'South Georgia and the South Sandwich Islands', value: 'GS' },
  { label: 'South Sudan', value: 'SS' },
  { label: 'Spain', value: 'ES' },
  { label: 'Sri Lanka', value: 'LK' },
  { label: 'Sudan', value: 'SD' },
  { label: 'Suriname', value: 'SR' },
  { label: 'Svalbard and Jan Mayen', value: 'SJ' },
  { label: 'Swaziland', value: 'SZ' },
  { label: 'Sweden', value: 'SE' },
  { label: 'Switzerland', value: 'CH' },
  { label: 'Syrian Arab Republic', value: 'SY' },
  { label: 'Taiwan', value: 'TW' },
  { label: 'Tajikistan', value: 'TJ' },
  { label: 'Tanzania, United Republic of', value: 'TZ' },
  { label: 'Thailand', value: 'TH' },
  { label: 'Timor-Leste', value: 'TL' },
  { label: 'Togo', value: 'TG' },
  { label: 'Tokelau', value: 'TK' },
  { label: 'Tonga', value: 'TO' },
  { label: 'Trinidad and Tobago', value: 'TT' },
  { label: 'Tunisia', value: 'TN' },
  { label: 'Turkey', value: 'TR' },
  { label: 'Turkmenistan', value: 'TM' },
  { label: 'Turks and Caicos Islands', value: 'TC' },
  { label: 'Tuvalu', value: 'TV' },
  { label: 'Uganda', value: 'UG' },
  { label: 'Ukraine', value: 'UA' },
  { label: 'United Arab Emirates', value: 'AE' },
  { label: 'United Kingdom', value: 'GB' },
  { label: 'United States Minor Outlying Islands', value: 'UM' },
  { label: 'Uruguay', value: 'UY' },
  { label: 'Uzbekistan', value: 'UZ' },
  { label: 'Vanuatu', value: 'VU' },
  { label: 'Venezuela, Bolivarian Republic of', value: 'VE' },
  { label: 'Viet Nam', value: 'VN' },
  { label: 'Virgin Islands, British', value: 'VG' },
  { label: 'Virgin Islands, U.S.', value: 'VI' },
  { label: 'Wallis and Futuna', value: 'WF' },
  { label: 'Western Sahara', value: 'EH' },
  { label: 'Yemen', value: 'YE' },
  { label: 'Zambia', value: 'ZM' },
  { label: 'Zimbabwe', value: 'ZW' },
];

/** Cryoportal time zone options (same values as portal time_zone select). */
const CRYO_TIMEZONE_OPTIONS = [
  {
    label: '(GMT-08:00) Pacific Time (US & Canada)',
    value: 'Pacific Time (US & Canada)',
  },
  {
    label: '(GMT-07:00) Mountain Time (US & Canada)',
    value: 'Mountain Time (US & Canada)',
  },
  {
    label: '(GMT-06:00) Central Time (US & Canada)',
    value: 'Central Time (US & Canada)',
  },
  {
    label: '(GMT-05:00) Eastern Time (US & Canada)',
    value: 'Eastern Time (US & Canada)',
  },
  {
    label: '(GMT-12:00) International Date Line West',
    value: 'International Date Line West',
  },
  { label: '(GMT-11:00) American Samoa', value: 'American Samoa' },
  { label: '(GMT-11:00) Midway Island', value: 'Midway Island' },
  { label: '(GMT-10:00) Hawaii', value: 'Hawaii' },
  { label: '(GMT-09:00) Alaska', value: 'Alaska' },
  { label: '(GMT-08:00) Tijuana', value: 'Tijuana' },
  { label: '(GMT-07:00) Arizona', value: 'Arizona' },
  { label: '(GMT-07:00) Mazatlan', value: 'Mazatlan' },
  { label: '(GMT-06:00) Central America', value: 'Central America' },
  { label: '(GMT-06:00) Chihuahua', value: 'Chihuahua' },
  { label: '(GMT-06:00) Guadalajara', value: 'Guadalajara' },
  { label: '(GMT-06:00) Mexico City', value: 'Mexico City' },
  { label: '(GMT-06:00) Monterrey', value: 'Monterrey' },
  { label: '(GMT-06:00) Saskatchewan', value: 'Saskatchewan' },
  { label: '(GMT-05:00) Bogota', value: 'Bogota' },
  { label: '(GMT-05:00) Indiana (East)', value: 'Indiana (East)' },
  { label: '(GMT-05:00) Lima', value: 'Lima' },
  { label: '(GMT-05:00) Quito', value: 'Quito' },
  {
    label: '(GMT-04:00) Atlantic Time (Canada)',
    value: 'Atlantic Time (Canada)',
  },
  { label: '(GMT-04:00) Caracas', value: 'Caracas' },
  { label: '(GMT-04:00) Georgetown', value: 'Georgetown' },
  { label: '(GMT-04:00) La Paz', value: 'La Paz' },
  { label: '(GMT-04:00) Puerto Rico', value: 'Puerto Rico' },
  { label: '(GMT-04:00) Santiago', value: 'Santiago' },
  { label: '(GMT-03:30) Newfoundland', value: 'Newfoundland' },
  { label: '(GMT-03:00) Brasilia', value: 'Brasilia' },
  { label: '(GMT-03:00) Buenos Aires', value: 'Buenos Aires' },
  { label: '(GMT-03:00) Montevideo', value: 'Montevideo' },
  { label: '(GMT-02:00) Greenland', value: 'Greenland' },
  { label: '(GMT-02:00) Mid-Atlantic', value: 'Mid-Atlantic' },
  { label: '(GMT-01:00) Azores', value: 'Azores' },
  { label: '(GMT-01:00) Cape Verde Is.', value: 'Cape Verde Is.' },
  { label: '(GMT+00:00) Casablanca', value: 'Casablanca' },
  { label: '(GMT+00:00) Dublin', value: 'Dublin' },
  { label: '(GMT+00:00) Edinburgh', value: 'Edinburgh' },
  { label: '(GMT+00:00) Lisbon', value: 'Lisbon' },
  { label: '(GMT+00:00) London', value: 'London' },
  { label: '(GMT+00:00) Monrovia', value: 'Monrovia' },
  { label: '(GMT+00:00) UTC', value: 'UTC' },
  { label: '(GMT+01:00) Amsterdam', value: 'Amsterdam' },
  { label: '(GMT+01:00) Belgrade', value: 'Belgrade' },
  { label: '(GMT+01:00) Berlin', value: 'Berlin' },
  { label: '(GMT+01:00) Bern', value: 'Bern' },
  { label: '(GMT+01:00) Bratislava', value: 'Bratislava' },
  { label: '(GMT+01:00) Brussels', value: 'Brussels' },
  { label: '(GMT+01:00) Budapest', value: 'Budapest' },
  { label: '(GMT+01:00) Copenhagen', value: 'Copenhagen' },
  { label: '(GMT+01:00) Ljubljana', value: 'Ljubljana' },
  { label: '(GMT+01:00) Madrid', value: 'Madrid' },
  { label: '(GMT+01:00) Paris', value: 'Paris' },
  { label: '(GMT+01:00) Prague', value: 'Prague' },
  { label: '(GMT+01:00) Rome', value: 'Rome' },
  { label: '(GMT+01:00) Sarajevo', value: 'Sarajevo' },
  { label: '(GMT+01:00) Skopje', value: 'Skopje' },
  { label: '(GMT+01:00) Stockholm', value: 'Stockholm' },
  { label: '(GMT+01:00) Vienna', value: 'Vienna' },
  { label: '(GMT+01:00) Warsaw', value: 'Warsaw' },
  { label: '(GMT+01:00) West Central Africa', value: 'West Central Africa' },
  { label: '(GMT+01:00) Zagreb', value: 'Zagreb' },
  { label: '(GMT+01:00) Zurich', value: 'Zurich' },
  { label: '(GMT+02:00) Athens', value: 'Athens' },
  { label: '(GMT+02:00) Bucharest', value: 'Bucharest' },
  { label: '(GMT+02:00) Cairo', value: 'Cairo' },
  { label: '(GMT+02:00) Harare', value: 'Harare' },
  { label: '(GMT+02:00) Helsinki', value: 'Helsinki' },
  { label: '(GMT+02:00) Jerusalem', value: 'Jerusalem' },
  { label: '(GMT+02:00) Kaliningrad', value: 'Kaliningrad' },
  { label: '(GMT+02:00) Kyiv', value: 'Kyiv' },
  { label: '(GMT+02:00) Pretoria', value: 'Pretoria' },
  { label: '(GMT+02:00) Riga', value: 'Riga' },
  { label: '(GMT+02:00) Sofia', value: 'Sofia' },
  { label: '(GMT+02:00) Tallinn', value: 'Tallinn' },
  { label: '(GMT+02:00) Vilnius', value: 'Vilnius' },
  { label: '(GMT+03:00) Baghdad', value: 'Baghdad' },
  { label: '(GMT+03:00) Istanbul', value: 'Istanbul' },
  { label: '(GMT+03:00) Kuwait', value: 'Kuwait' },
  { label: '(GMT+03:00) Minsk', value: 'Minsk' },
  { label: '(GMT+03:00) Moscow', value: 'Moscow' },
  { label: '(GMT+03:00) Nairobi', value: 'Nairobi' },
  { label: '(GMT+03:00) Riyadh', value: 'Riyadh' },
  { label: '(GMT+03:00) St. Petersburg', value: 'St. Petersburg' },
  { label: '(GMT+03:00) Volgograd', value: 'Volgograd' },
  { label: '(GMT+03:30) Tehran', value: 'Tehran' },
  { label: '(GMT+04:00) Abu Dhabi', value: 'Abu Dhabi' },
  { label: '(GMT+04:00) Baku', value: 'Baku' },
  { label: '(GMT+04:00) Muscat', value: 'Muscat' },
  { label: '(GMT+04:00) Samara', value: 'Samara' },
  { label: '(GMT+04:00) Tbilisi', value: 'Tbilisi' },
  { label: '(GMT+04:00) Yerevan', value: 'Yerevan' },
  { label: '(GMT+04:30) Kabul', value: 'Kabul' },
  { label: '(GMT+05:00) Almaty', value: 'Almaty' },
  { label: '(GMT+05:00) Ekaterinburg', value: 'Ekaterinburg' },
  { label: '(GMT+05:00) Islamabad', value: 'Islamabad' },
  { label: '(GMT+05:00) Karachi', value: 'Karachi' },
  { label: '(GMT+05:00) Tashkent', value: 'Tashkent' },
  { label: '(GMT+05:30) Chennai', value: 'Chennai' },
  { label: '(GMT+05:30) Kolkata', value: 'Kolkata' },
  { label: '(GMT+05:30) Mumbai', value: 'Mumbai' },
  { label: '(GMT+05:30) New Delhi', value: 'New Delhi' },
  { label: '(GMT+05:30) Sri Jayawardenepura', value: 'Sri Jayawardenepura' },
  { label: '(GMT+05:45) Kathmandu', value: 'Kathmandu' },
  { label: '(GMT+06:00) Astana', value: 'Astana' },
  { label: '(GMT+06:00) Dhaka', value: 'Dhaka' },
  { label: '(GMT+06:00) Urumqi', value: 'Urumqi' },
  { label: '(GMT+06:30) Rangoon', value: 'Rangoon' },
  { label: '(GMT+07:00) Bangkok', value: 'Bangkok' },
  { label: '(GMT+07:00) Hanoi', value: 'Hanoi' },
  { label: '(GMT+07:00) Jakarta', value: 'Jakarta' },
  { label: '(GMT+07:00) Krasnoyarsk', value: 'Krasnoyarsk' },
  { label: '(GMT+07:00) Novosibirsk', value: 'Novosibirsk' },
  { label: '(GMT+08:00) Beijing', value: 'Beijing' },
  { label: '(GMT+08:00) Chongqing', value: 'Chongqing' },
  { label: '(GMT+08:00) Hong Kong', value: 'Hong Kong' },
  { label: '(GMT+08:00) Irkutsk', value: 'Irkutsk' },
  { label: '(GMT+08:00) Kuala Lumpur', value: 'Kuala Lumpur' },
  { label: '(GMT+08:00) Perth', value: 'Perth' },
  { label: '(GMT+08:00) Singapore', value: 'Singapore' },
  { label: '(GMT+08:00) Taipei', value: 'Taipei' },
  { label: '(GMT+08:00) Ulaanbaatar', value: 'Ulaanbaatar' },
  { label: '(GMT+09:00) Osaka', value: 'Osaka' },
  { label: '(GMT+09:00) Sapporo', value: 'Sapporo' },
  { label: '(GMT+09:00) Seoul', value: 'Seoul' },
  { label: '(GMT+09:00) Tokyo', value: 'Tokyo' },
  { label: '(GMT+09:00) Yakutsk', value: 'Yakutsk' },
  { label: '(GMT+09:30) Adelaide', value: 'Adelaide' },
  { label: '(GMT+09:30) Darwin', value: 'Darwin' },
  { label: '(GMT+10:00) Brisbane', value: 'Brisbane' },
  { label: '(GMT+10:00) Canberra', value: 'Canberra' },
  { label: '(GMT+10:00) Guam', value: 'Guam' },
  { label: '(GMT+10:00) Hobart', value: 'Hobart' },
  { label: '(GMT+10:00) Melbourne', value: 'Melbourne' },
  { label: '(GMT+10:00) Port Moresby', value: 'Port Moresby' },
  { label: '(GMT+10:00) Sydney', value: 'Sydney' },
  { label: '(GMT+10:00) Vladivostok', value: 'Vladivostok' },
  { label: '(GMT+11:00) Magadan', value: 'Magadan' },
  { label: '(GMT+11:00) New Caledonia', value: 'New Caledonia' },
  { label: '(GMT+11:00) Solomon Is.', value: 'Solomon Is.' },
  { label: '(GMT+11:00) Srednekolymsk', value: 'Srednekolymsk' },
  { label: '(GMT+12:00) Auckland', value: 'Auckland' },
  { label: '(GMT+12:00) Fiji', value: 'Fiji' },
  { label: '(GMT+12:00) Kamchatka', value: 'Kamchatka' },
  { label: '(GMT+12:00) Marshall Is.', value: 'Marshall Is.' },
  { label: '(GMT+12:00) Wellington', value: 'Wellington' },
  { label: '(GMT+12:45) Chatham Is.', value: 'Chatham Is.' },
  { label: "(GMT+13:00) Nuku'alofa", value: "Nuku'alofa" },
  { label: '(GMT+13:00) Samoa', value: 'Samoa' },
  { label: '(GMT+13:00) Tokelau Is.', value: 'Tokelau Is.' },
];

const _CRYO_PRIMARY_CONTAINER_TYPE_OPTIONS = [
  { label: 'Vials', value: 'VIALS' },
  { label: 'Straws', value: 'STRAWS' },
  { label: 'Cassette', value: 'CASSETTE' },
  { label: 'Ampoules', value: 'AMPOULES' },
  { label: 'Bags', value: 'BAGS' },
  { label: 'Boxes', value: 'BOXES' },
  { label: 'Other', value: 'OTHER' },
];

const _CRYO_COMMODITY_GROUP_OPTIONS = [
  { label: 'Antibody (Other)', value: 2 },
  { label: 'Antisera (Animal)', value: 3 },
  { label: 'Bacteria (Animal)', value: 4 },
  { label: 'Biological Specimens (Animal)', value: 6 },
  { label: 'Biological Specimens (Human)', value: 7 },
  { label: 'Biological Specimens (Other)', value: 8 },
  { label: 'Blood (Animal)', value: 9 },
  { label: 'Blood (Human)', value: 10 },
  { label: 'Cell (Other)', value: 14 },
  { label: 'Cell (Human)', value: 13 },
  { label: 'Cell (Animal)', value: 12 },
  { label: 'DNA (Animal)', value: 15 },
  { label: 'DNA (Human)', value: 16 },
  { label: 'DNA (Other)', value: 17 },
  { label: 'Embryo (Animal)', value: 18 },
  { label: 'Embryo (Human)', value: 19 },
  { label: 'Embryo (Other)', value: 20 },
  { label: 'Empty Shipper (Other)', value: 21 },
  { label: 'Empty Shipper (Human)', value: 75 },
  { label: 'Medical Device (Human)', value: 77 },
  { label: 'Microsomes (Animal)', value: 24 },
  { label: 'Oocytes (Other)', value: 26 },
  { label: 'Oocytes (Animal)', value: 65 },
  { label: 'Oocytes (Human)', value: 64 },
  { label: 'Other (Other)', value: 27 },
  { label: 'Plasma (Animal)', value: 31 },
  { label: 'Plasma (Human)', value: 32 },
  { label: 'Protein (Animal)', value: 33 },
  { label: 'Protein (Human)', value: 34 },
  { label: 'Protein (Other)', value: 35 },
  { label: 'RNA (Other)', value: 58 },
  { label: 'Saline (Other)', value: 36 },
  { label: 'Saline (Human)', value: 76 },
  { label: 'Semen / Sperm (Animal)', value: 37 },
  { label: 'Semen / Sperm (Human)', value: 38 },
  { label: 'Serum (Human)', value: 41 },
  { label: 'Serum (Animal)', value: 40 },
  { label: 'Soil (Other)', value: 42 },
  { label: 'Spore (Other)', value: 43 },
  { label: 'Subcellular Fractions (Human)', value: 45 },
  { label: 'Subcellular Fractions (Animal)', value: 44 },
  { label: 'Tissue (Animal)', value: 48 },
  { label: 'Tissue (Human)', value: 49 },
  { label: 'Tissue (Other)', value: 66 },
  { label: 'Urine (Human)', value: 50 },
  { label: 'Urine (Animal)', value: 72 },
  { label: 'Vaccine (Animal)', value: 62 },
  { label: 'Virus (Other)', value: 53 },
  { label: 'Water (Other)', value: 54 },
  { label: 'Yeast (Other)', value: 55 },
];

const _CRYO_COMMODITY_CATEGORY_OPTIONS = [
  { label: 'EXEMPT - NOT SUBJECT TO DANGEROUS GOOD REGULATIONS', value: 4 },
  { label: 'EXEMPT - EXEMPT HUMAN SPECIMEN', value: 5 },
  { label: 'EXEMPT - EXEMPT ANIMAL SPECIMEN', value: 6 },
  { label: 'Not Specified in Legacy Data - legacy data', value: 9 },
  { label: 'OTHER - NONBIOLOGICAL', value: 8 },
  {
    label:
      'UN2814 - INFECTIOUS SUBSTANCE, AFFECTING HUMANS, CATEGORY A (UN2814)',
    value: 1,
  },
  {
    label:
      'UN2900 - INFECTIOUS SUBSTANCE, AFFECTING ANIMALS, CATEGORY A (UN2900)',
    value: 2,
  },
  {
    label:
      'UN3245 - GENETICALLY MODIFIED ORGANISM OR MICRO-ORGANISM, NON INFECTIOUS (UN3245)',
    value: 7,
  },
  { label: 'UN3373 - BIOLOGICAL SUBSTANCE, CATEGORY B (UN3373)', value: 3 },
];

type CryoAutocompleteOption = ReturnType<
  typeof buildCryoCompanyProfileAutocompleteOptions
>[number];

/**
 * WC-style Cryoportal site block — field order matches the Cryoportal route form:
 * Company Name → Nickname → Street Address → Street Address #2 → City →
 * State/Province → Zip/Postal Code → Country Code (dropdown) → Time Zone (dropdown) →
 * Residential → Contact First/Last → Email → Phone → Save site
 */
function CryoWcStyleSiteFormBlock({
  side,
  autocompleteOptions,
  onNameChange,
  onNameBlur,
  onNameSelect,
  onNameClear,
}: {
  side: 'origin' | 'dest';
  autocompleteOptions: CryoAutocompleteOption[];
  onNameChange: (val: string | undefined) => void;
  onNameBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onNameSelect: (val: string) => void;
  onNameClear: () => void;
}): React.ReactElement {
  const o = side === 'origin';
  const company = o ? 'cryo_origin_name' : 'cryo_dest_name';
  const nick = o ? 'cryo_origin_nickname' : 'cryo_dest_nickname';
  const first = o
    ? 'cryo_origin_contact_first_name'
    : 'cryo_dest_contact_first_name';
  const last = o
    ? 'cryo_origin_contact_last_name'
    : 'cryo_dest_contact_last_name';
  const phone = o
    ? 'cryo_origin_contact_phone_number'
    : 'cryo_dest_contact_phone_number';
  const email = o ? 'cryo_origin_contact_email' : 'cryo_dest_contact_email';
  const a1 = o
    ? 'cryo_origin_street_address_line_one'
    : 'cryo_dest_street_address_line_one';
  const a2 = o
    ? 'cryo_origin_street_address_line_two'
    : 'cryo_dest_street_address_line_two';
  const city = o ? 'cryo_origin_city' : 'cryo_dest_city';
  const state = o ? 'cryo_origin_state' : 'cryo_dest_state';
  const zip = o ? 'cryo_origin_zip_code' : 'cryo_dest_zip_code';
  const cty = o ? 'cryo_origin_country_code' : 'cryo_dest_country_code';
  const tz = o ? 'cryo_origin_time_zone' : 'cryo_dest_time_zone';
  const res = o ? 'cryo_origin_residential' : 'cryo_dest_residential';
  const _proc = o ? 'cryo_origin_site_process' : 'cryo_dest_site_process';

  const form = Form.useFormInstance();

  const req = (msg: string) => [{ required: true, message: msg }];

  return (
    <>
      {/* Company Name — always an autocomplete; shows saved templates + "Enter new address" */}
      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          <span className={styles.requiredGap}>*</span>
          Company Name
        </label>
        <Form.Item
          name={company}
          noStyle
          rules={req('Company name is required')}
          getValueFromEvent={(val: string | undefined) =>
            val === CRYO_ENTER_NEW_ADDRESS_SENTINEL ? '' : val
          }
        >
          <AutoComplete
            allowClear
            options={autocompleteOptions}
            popupClassName={WC_LONG_OPTION_SELECT_POPUP_CLASS}
            style={{ width: '100%' }}
            filterOption={(inputValue, option) => {
              const opt = option as {
                value?: string;
                cryoFilterLabel?: string;
              };
              if (opt?.value === CRYO_ENTER_NEW_ADDRESS_SENTINEL) return true;
              const q = (inputValue ?? '').toLowerCase().trim();
              if (!q) return true;
              return (opt?.cryoFilterLabel ?? '').toLowerCase().includes(q);
            }}
            placeholder='Select a saved profile or enter company name'
            onClear={onNameClear}
            onSelect={(selected) => {
              const value = selected as string;
              if (value === CRYO_ENTER_NEW_ADDRESS_SENTINEL) {
                onNameClear();
                // Use '' not undefined — setting undefined leaves the AutoComplete's
                // internal inputValue state intact, so the sentinel stays visible.
                // An empty string explicitly clears the controlled input display.
                form.setFieldValue(company, '');
                return;
              }
              if (value.startsWith(CRYO_LOCATION_TEMPLATE_PREFIX)) {
                onNameSelect(value.slice(CRYO_LOCATION_TEMPLATE_PREFIX.length));
              }
            }}
            onChange={(val: string | undefined) => {
              if (
                val === CRYO_ENTER_NEW_ADDRESS_SENTINEL ||
                (typeof val === 'string' &&
                  val.trim() === CRYO_ENTER_NEW_ADDRESS_SENTINEL)
              ) {
                onNameClear();
                form.setFieldValue(company, '');
                return;
              }
              if (
                typeof val === 'string' &&
                val.startsWith(CRYO_LOCATION_TEMPLATE_PREFIX)
              ) {
                return;
              }
              onNameChange(val);
            }}
          >
            <Input
              style={wcCompanyAutoCompleteInputStyle}
              onBlur={onNameBlur}
            />
          </AutoComplete>
        </Form.Item>
      </div>

      <div className={styles.fieldWrap}>
        <label className={styles.label}>Nickname</label>
        <Form.Item name={nick} noStyle>
          <CustomInput placeholder='Optional' height='36px' />
        </Form.Item>
      </div>

      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          <span className={styles.requiredGap}>*</span>
          Street Address
        </label>
        <Form.Item name={a1} noStyle rules={req('Street address is required')}>
          <CustomInput placeholder='Enter address line 1' height='36px' />
        </Form.Item>
      </div>

      <div className={styles.fieldWrap}>
        <label className={styles.label}>Street Address #2</label>
        <Form.Item name={a2} noStyle>
          <CustomInput
            placeholder='Room / Lab / Dept / additional information'
            height='36px'
          />
        </Form.Item>
      </div>

      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          <span className={styles.requiredGap}>*</span>
          City/Locality
        </label>
        <Form.Item name={city} noStyle rules={req('City is required')}>
          <CustomInput placeholder='Enter city' height='36px' />
        </Form.Item>
      </div>

      <div className={styles.fieldWrap}>
        <label className={styles.label}>State/Province</label>
        <Form.Item name={state} noStyle>
          <CustomInput placeholder='Enter state/province' height='36px' />
        </Form.Item>
      </div>

      <div className={styles.fieldWrap}>
        <label className={styles.label}>Zip/Postal Code</label>
        <Form.Item name={zip} noStyle>
          <CustomInput placeholder='Enter postal code' height='36px' />
        </Form.Item>
      </div>

      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          <span className={styles.requiredGap}>*</span>
          Country Code
        </label>
        <Form.Item name={cty} noStyle rules={req('Country is required')}>
          <CustomSelect
            placeholder='Select a country...'
            width='100%'
            height='36px'
            allowClear
            showSearch
            optionFilterProp='label'
            options={CRYO_COUNTRY_OPTIONS}
          />
        </Form.Item>
      </div>

      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          <span className={styles.requiredGap}>*</span>Time Zone
        </label>
        <Form.Item name={tz} noStyle rules={req('Time zone is required')}>
          <CustomSelect
            placeholder='Select a time zone...'
            width='100%'
            height='36px'
            allowClear
            showSearch
            optionFilterProp='label'
            options={CRYO_TIMEZONE_OPTIONS}
          />
        </Form.Item>
      </div>

      <div className={styles.fieldWrapMt4}>
        <Form.Item name={res} valuePropName='checked' noStyle>
          <Checkbox>Residential</Checkbox>
        </Form.Item>
      </div>

      <Row gutter={12} style={{ marginBottom: 16, marginTop: 12 }}>
        <Col span={12}>
          <label className={styles.label}>
            <span className={styles.requiredGap}>*</span>
            Contact First Name
          </label>
          <Form.Item name={first} noStyle rules={req('First name is required')}>
            <CustomInput placeholder='Enter first name' height='36px' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <label className={styles.label}>
            <span className={styles.requiredGap}>*</span>
            Contact Last Name
          </label>
          <Form.Item name={last} noStyle rules={req('Last name is required')}>
            <CustomInput placeholder='Enter last name' height='36px' />
          </Form.Item>
        </Col>
      </Row>

      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          <span className={styles.requiredGap}>*</span>
          Contact Email
        </label>
        <Form.Item
          name={email}
          noStyle
          rules={req('Contact email is required')}
        >
          <CustomInput placeholder='Enter email' height='36px' />
        </Form.Item>
      </div>

      <div className={styles.fieldWrap}>
        <label className={styles.label}>
          <span className={styles.requiredGap}>*</span>
          Contact Phone
        </label>
        <Form.Item
          name={phone}
          noStyle
          rules={req('Contact phone is required')}
        >
          <CustomInput placeholder='Enter phone number' height='36px' />
        </Form.Item>
      </div>
    </>
  );
}

/**
 * Package name: options filtered by selected temperature (WC portal behavior).
 * Forwards Form.Item field props (value, onChange, status, etc.) to CustomSelect.
 */
type WcPackageNameSelectProps = {
  form: FormInstance<BookShipmentFormValues>;
  pkgFieldName: number | string;
  popupClassName: string;
} & Omit<React.ComponentProps<typeof CustomSelect>, 'options'>;

function WcPackageNameSelect({
  form,
  pkgFieldName,
  popupClassName,
  ...fieldProps
}: WcPackageNameSelectProps): React.ReactElement {
  const pkgIndex =
    typeof pkgFieldName === 'number' ? pkgFieldName : Number(pkgFieldName);
  const temperature = Form.useWatch(
    ['wc_packages', pkgIndex, 'temperature_range_refrigerant'],
    form
  ) as string | undefined;

  const validPackageValues =
    getValidPackageNameValuesForTemperature(temperature);
  const filteredPackageOptions = worldCourierPackageNameSelectOptions.filter(
    (o) => validPackageValues.has(o.value)
  );

  return (
    <CustomSelect
      {...fieldProps}
      placeholder='Select'
      width='100%'
      height='36px'
      options={filteredPackageOptions}
      showSearch
      optionFilterProp='label'
      popupClassName={popupClassName}
    />
  );
}

/**
 * Extra supplies: same interaction pattern as CreateShipmentDrawer “Products”
 * (multi-select + selected rows with disabled name + delete).
 */
function WcExtraSuppliesLikeProducts({
  form,
  pkgFieldName,
  popupClassName,
}: {
  form: FormInstance<BookShipmentFormValues>;
  pkgFieldName: number | string;
  popupClassName: string;
}): React.ReactElement {
  const pkgIndex =
    typeof pkgFieldName === 'number' ? pkgFieldName : Number(pkgFieldName);
  const selected = Form.useWatch(
    ['wc_packages', pkgIndex, 'extra_supplies'],
    form
  ) as string[] | undefined;
  const temperature = Form.useWatch(
    ['wc_packages', pkgIndex, 'temperature_range_refrigerant'],
    form
  ) as string | undefined;
  const asArray = Array.isArray(selected)
    ? selected.filter((v) => v != null && String(v).trim() !== '')
    : [];

  const validExtraSupplyValues =
    getValidExtraSupplyValuesForTemperature(temperature);
  const filteredExtraSuppliesOptions =
    worldCourierExtraSuppliesSelectOptions.filter((o) =>
      validExtraSupplyValues.has(o.value)
    );

  const labelFor = (val: string) => {
    const o = worldCourierExtraSuppliesSelectOptions.find(
      (x) => String(x.value) === String(val)
    );
    return o?.label ?? val;
  };

  const removeOne = (val: string) => {
    const next = asArray.filter((v) => String(v) !== String(val));
    form.setFieldValue(['wc_packages', pkgIndex, 'extra_supplies'], next);
  };

  return (
    <div className={styles.listColumn}>
      <Form.Item
        className='custom-input form-input-style'
        label='Select Extra Supplies'
        name={[pkgFieldName, 'extra_supplies']}
        style={{ marginBottom: 0, maxWidth: '100%' }}
      >
        <Select
          mode='multiple'
          className='custom-select-shipment-product'
          maxTagCount={0}
          tagRender={() => <span>Select</span>}
          placeholder='Select'
          showSearch={false}
          allowClear
          options={filteredExtraSuppliesOptions}
          popupClassName={popupClassName}
          style={{
            minHeight: '36px',
            border: '0px',
            backgroundColor: '#f7f7f7',
            color: 'var(--text-secondary)',
            fontSize: '14px',
            borderRadius: '8px',
            lineHeight: '38px',
            width: '100%',
            maxWidth: '100%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        />
      </Form.Item>
      {asArray.length > 0 ? (
        <div className={styles.listRows}>
          {asArray.map((val, rowIdx) => (
            <Flex
              key={listRowKey(val, rowIdx)}
              gap={12}
              align='flex-start'
              style={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
              }}
            >
              <div className={styles.listRowText}>{labelFor(String(val))}</div>
              <button
                type='button'
                aria-label='Remove supply'
                onClick={() => removeOne(String(val))}
                className={styles.listRowRemove}
              >
                <DeleteIcon />
              </button>
            </Flex>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const listRowKey = (val: unknown, index: number): string =>
  `${String(val)}-${index}`;

const wcCompanyAutoCompleteInputStyle: React.CSSProperties = {
  height: 36,
  borderRadius: 8,
  backgroundColor: 'var(--light-gray)',
  border: 'none',
};

function WcLabel({
  text,
  required,
}: {
  text: string;
  required?: boolean;
}): React.ReactElement {
  return (
    <div className={styles.wcLabelWrap}>
      <span
        className={
          required
            ? `${styles.wcLabelText} ${styles.wcLabelTextRequired}`
            : styles.wcLabelText
        }
      >
        {text}
      </span>
      {required ? <span className={styles.wcLabelStar}>*</span> : null}
    </div>
  );
}

/**
 * Controlled Collapse: when a new panel id appears (e.g. + Package / + Item),
 * that panel opens. Removed ids are dropped from activeKey; user toggles persist.
 */
function CollapseWithAutoOpenNewPanels({
  ids,
  style,
  className,
  children,
}: {
  ids: string[];
  style?: React.CSSProperties;
  /** e.g. `wc-booking-collapse` or nested variant (see BookShipmentDrawerWcSelect.css). */
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const prevIdsRef = useRef<string[]>([]);

  useLayoutEffect(() => {
    const prev = prevIdsRef.current;
    const added = ids.filter((id) => !prev.includes(id));
    const removed = prev.filter((id) => !ids.includes(id));
    prevIdsRef.current = [...ids];
    setActiveKeys((cur) => {
      const pruned = cur.filter((id) => !removed.includes(id));
      const toAdd = added.filter((id) => !pruned.includes(id));
      return [...pruned, ...toAdd];
    });
  }, [ids]);

  return (
    <Collapse
      bordered={false}
      className={className}
      activeKey={activeKeys}
      onChange={(k) =>
        setActiveKeys(
          Array.isArray(k) ? k : k != null && k !== '' ? [String(k)] : []
        )
      }
      style={style}
    >
      {children}
    </Collapse>
  );
}

/** QuickOnline / template sentinel — treat as no selection (same as null). */
const SERVICE_TYPE_PLACEHOLDER = '-- Select a service';

const US_STATE_OPTIONS = [
  { label: 'Alabama', value: 'Alabama' },
  { label: 'Alaska', value: 'Alaska' },
  { label: 'Arizona', value: 'Arizona' },
  { label: 'Arkansas', value: 'Arkansas' },
  { label: 'California', value: 'California' },
  { label: 'Colorado', value: 'Colorado' },
  { label: 'Connecticut', value: 'Connecticut' },
  { label: 'Delaware', value: 'Delaware' },
  { label: 'Florida', value: 'Florida' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Hawaii', value: 'Hawaii' },
  { label: 'Idaho', value: 'Idaho' },
  { label: 'Illinois', value: 'Illinois' },
  { label: 'Indiana', value: 'Indiana' },
  { label: 'Iowa', value: 'Iowa' },
  { label: 'Kansas', value: 'Kansas' },
  { label: 'Kentucky', value: 'Kentucky' },
  { label: 'Louisiana', value: 'Louisiana' },
  { label: 'Maine', value: 'Maine' },
  { label: 'Maryland', value: 'Maryland' },
  { label: 'Massachusetts', value: 'Massachusetts' },
  { label: 'Michigan', value: 'Michigan' },
  { label: 'Minnesota', value: 'Minnesota' },
  { label: 'Mississippi', value: 'Mississippi' },
  { label: 'Missouri', value: 'Missouri' },
  { label: 'Montana', value: 'Montana' },
  { label: 'Nebraska', value: 'Nebraska' },
  { label: 'Nevada', value: 'Nevada' },
  { label: 'New Hampshire', value: 'New Hampshire' },
  { label: 'New Jersey', value: 'New Jersey' },
  { label: 'New Mexico', value: 'New Mexico' },
  { label: 'New York', value: 'New York' },
  { label: 'North Carolina', value: 'North Carolina' },
  { label: 'North Dakota', value: 'North Dakota' },
  { label: 'Ohio', value: 'Ohio' },
  { label: 'Oklahoma', value: 'Oklahoma' },
  { label: 'Oregon', value: 'Oregon' },
  { label: 'Pennsylvania', value: 'Pennsylvania' },
  { label: 'Rhode Island', value: 'Rhode Island' },
  { label: 'South Carolina', value: 'South Carolina' },
  { label: 'South Dakota', value: 'South Dakota' },
  { label: 'Tennessee', value: 'Tennessee' },
  { label: 'Texas', value: 'Texas' },
  { label: 'Utah', value: 'Utah' },
  { label: 'Vermont', value: 'Vermont' },
  { label: 'Virginia', value: 'Virginia' },
  { label: 'Washington', value: 'Washington' },
  { label: 'West Virginia', value: 'West Virginia' },
  { label: 'Wisconsin', value: 'Wisconsin' },
  { label: 'Wyoming', value: 'Wyoming' },
];

const COUNTRY_OPTIONS = [
  { label: 'Afghanistan', value: 'AFG' },
  { label: 'Aland Islands', value: 'ALA' },
  { label: 'Albania', value: 'ALB' },
  { label: 'Algeria', value: 'DZA' },
  { label: 'American Samoa', value: 'ASM' },
  { label: 'Andorra', value: 'AND' },
  { label: 'Angola', value: 'AGO' },
  { label: 'Anguilla', value: 'AIA' },
  { label: 'Antigua and Barbuda', value: 'ATG' },
  { label: 'Argentina', value: 'ARG' },
  { label: 'Armenia', value: 'ARM' },
  { label: 'Aruba', value: 'ABW' },
  { label: 'Australia', value: 'AUS' },
  { label: 'Austria', value: 'AUT' },
  { label: 'Azerbaijan', value: 'AZE' },
  { label: 'Bahamas', value: 'BHS' },
  { label: 'Bahrain', value: 'BHR' },
  { label: 'Bangladesh', value: 'BGD' },
  { label: 'Barbados', value: 'BRB' },
  { label: 'Belarus', value: 'BLR' },
  { label: 'Belgium', value: 'BEL' },
  { label: 'Belize', value: 'BLZ' },
  { label: 'Benin', value: 'BEN' },
  { label: 'Bermuda', value: 'BMU' },
  { label: 'Bhutan', value: 'BTN' },
  { label: 'Bolivia', value: 'BOL' },
  { label: 'Bonaire, Sint Eustatius & Saba', value: 'BES' },
  { label: 'Bosnia and Herzegovina', value: 'BIH' },
  { label: 'Botswana', value: 'BWA' },
  { label: 'Brazil', value: 'BRA' },
  { label: 'Brunei Darussalam', value: 'BRN' },
  { label: 'Bulgaria', value: 'BGR' },
  { label: 'Burkina Faso', value: 'BFA' },
  { label: 'Burundi', value: 'BDI' },
  { label: 'Cambodia', value: 'KHM' },
  { label: 'Cameroon', value: 'CMR' },
  { label: 'Canada', value: 'CAN' },
  { label: 'Canary Islands', value: 'ICA' },
  { label: 'Cape Verde', value: 'CPV' },
  { label: 'Cayman Island', value: 'CYM' },
  { label: 'Central African Republic', value: 'CAF' },
  { label: 'Chad', value: 'TCD' },
  { label: 'Chile', value: 'CHL' },
  { label: 'China', value: 'CHN' },
  { label: 'Christmas Islands', value: 'CXR' },
  { label: 'Cocos (Keeling) Island', value: 'CCK' },
  { label: 'Colombia', value: 'COL' },
  { label: 'Comoros', value: 'COM' },
  { label: 'Congo', value: 'COG' },
  { label: 'Cook Islands', value: 'COK' },
  { label: 'Costa Rica', value: 'CRI' },
  { label: 'Croatia', value: 'HRV' },
  { label: 'Curacao', value: 'CUW' },
  { label: 'Cyprus', value: 'CYP' },
  { label: 'Czech Republic', value: 'CZE' },
  { label: 'Dem Rep Of The Congo', value: 'COD' },
  { label: 'Denmark', value: 'DNK' },
  { label: 'Djibouti', value: 'DJI' },
  { label: 'Dominica', value: 'DMA' },
  { label: 'Dominican Republic', value: 'DOM' },
  { label: 'East Timor', value: 'TMP' },
  { label: 'Ecuador', value: 'ECU' },
  { label: 'Egypt', value: 'EGY' },
  { label: 'El Salvador', value: 'SLV' },
  { label: 'Equatorial Guinea', value: 'GNQ' },
  { label: 'Eritrea', value: 'ERI' },
  { label: 'Estonia', value: 'EST' },
  { label: 'Eswatini', value: 'SWZ' },
  { label: 'Ethiopia', value: 'ETH' },
  { label: 'Falkland Islands (Malvina)', value: 'FLK' },
  { label: 'Faroe Islands', value: 'FRO' },
  { label: 'Fiji', value: 'FJI' },
  { label: 'Finland', value: 'FIN' },
  { label: 'France', value: 'FRA' },
  { label: 'French Guiana', value: 'GUF' },
  { label: 'French Polynesia', value: 'PYF' },
  { label: 'Gabon', value: 'GAB' },
  { label: 'Gambia', value: 'GMB' },
  { label: 'Georgia', value: 'GEO' },
  { label: 'Germany', value: 'DEU' },
  { label: 'Ghana', value: 'GHA' },
  { label: 'Gibraltar', value: 'GIB' },
  { label: 'Greece', value: 'GRC' },
  { label: 'Greenland', value: 'GRL' },
  { label: 'Grenada', value: 'GRD' },
  { label: 'Guadeloupe', value: 'GLP' },
  { label: 'Guam', value: 'GUM' },
  { label: 'Guatemala', value: 'GTM' },
  { label: 'Guernsey', value: 'GGY' },
  { label: 'Guinea', value: 'GIN' },
  { label: 'Guinea-Bissau', value: 'GNB' },
  { label: 'Guyana', value: 'GUY' },
  { label: 'Haiti', value: 'HTI' },
  { label: 'Honduras', value: 'HND' },
  { label: 'Hong Kong', value: 'HKG' },
  { label: 'Hungary', value: 'HUN' },
  { label: 'Iceland', value: 'ISL' },
  { label: 'India', value: 'IND' },
  { label: 'Indonesia', value: 'IDN' },
  { label: 'Iraq', value: 'IRQ' },
  { label: 'Ireland', value: 'IRL' },
  { label: 'Isle of Man', value: 'IMN' },
  { label: 'Israel', value: 'ISR' },
  { label: 'Italy', value: 'ITA' },
  { label: 'Jamaica', value: 'JAM' },
  { label: 'Japan', value: 'JPN' },
  { label: 'Jersey', value: 'JEY' },
  { label: 'Jordan', value: 'JOR' },
  { label: 'Kazakhstan', value: 'KAZ' },
  { label: 'Kenya', value: 'KEN' },
  { label: 'Kiribati', value: 'KIR' },
  { label: 'Korea, Rep. of (South)', value: 'KOR' },
  { label: 'Kosovo', value: 'RKS' },
  { label: 'Kuwait', value: 'KWT' },
  { label: 'Kyrgyzstan', value: 'KGZ' },
  { label: 'Laos (Peoples Dem. Rep.)', value: 'LAO' },
  { label: 'Latvia', value: 'LVA' },
  { label: 'Lebanon', value: 'LBN' },
  { label: 'Lesotho', value: 'LSO' },
  { label: 'Liberia', value: 'LBR' },
  { label: 'Libyan Arab Jamahiriya', value: 'LBY' },
  { label: 'Liechtenstein', value: 'LIE' },
  { label: 'Lithuania', value: 'LTU' },
  { label: 'Luxembourg', value: 'LUX' },
  { label: 'Macau', value: 'MAC' },
  { label: 'Macedonia', value: 'MKD' },
  { label: 'Madagascar', value: 'MDG' },
  { label: 'Malawi', value: 'MWI' },
  { label: 'Malaysia', value: 'MYS' },
  { label: 'Maldives', value: 'MDV' },
  { label: 'Mali', value: 'MLI' },
  { label: 'Malta', value: 'MLT' },
  { label: 'Marshall Islands', value: 'MHL' },
  { label: 'Martinique', value: 'MTQ' },
  { label: 'Mauritania', value: 'MRT' },
  { label: 'Mauritius', value: 'MUS' },
  { label: 'Mayotte', value: 'MYT' },
  { label: 'Mexico', value: 'MEX' },
  { label: 'Micronesia', value: 'FSM' },
  { label: 'Moldova', value: 'MDA' },
  { label: 'Monaco', value: 'MCO' },
  { label: 'Mongolia', value: 'MNG' },
  { label: 'Montenegro', value: 'MNE' },
  { label: 'Montserrat', value: 'MSR' },
  { label: 'Morocco', value: 'MAR' },
  { label: 'Mozambique', value: 'MOZ' },
  { label: 'Myanmar', value: 'MMR' },
  { label: 'Namibia', value: 'NAM' },
  { label: 'Nauru', value: 'NRU' },
  { label: 'Nepal', value: 'NPL' },
  { label: 'Netherlands', value: 'NLD' },
  { label: 'New Caledonia', value: 'NCL' },
  { label: 'New Zealand', value: 'NZL' },
  { label: 'Nicaragua', value: 'NIC' },
  { label: 'Niger', value: 'NER' },
  { label: 'Nigeria', value: 'NGA' },
  { label: 'Niue', value: 'NIU' },
  { label: 'Norfolk Islands', value: 'NFK' },
  { label: 'Northern Mariana Islands', value: 'MNP' },
  { label: 'Norway', value: 'NOR' },
  { label: 'Oman', value: 'OMN' },
  { label: 'Pakistan', value: 'PAK' },
  { label: 'Palau', value: 'PLW' },
  { label: 'Palestine', value: 'PSE' },
  { label: 'Panama', value: 'PAN' },
  { label: 'Papua New Guinea', value: 'PNG' },
  { label: 'Paraguay', value: 'PRY' },
  { label: 'Peru', value: 'PER' },
  { label: 'Philippines', value: 'PHL' },
  { label: 'Poland', value: 'POL' },
  { label: 'Portugal', value: 'PRT' },
  { label: 'Puerto Rico', value: 'PRI' },
  { label: 'Qatar', value: 'QAT' },
  { label: "Republic of Côte d'Ivoire", value: 'CIV' },
  { label: 'Reunion', value: 'REU' },
  { label: 'Romania', value: 'ROM' },
  { label: 'Russian Federation', value: 'RUS' },
  { label: 'Rwanda', value: 'RWA' },
  { label: 'Saint Kitts and Nevis', value: 'KNA' },
  { label: 'Saint Lucia', value: 'LCA' },
  { label: 'Saint Martin', value: 'MAF' },
  { label: 'Saint Vincent & Grenadine', value: 'VCT' },
  { label: 'Samoa', value: 'WSM' },
  { label: 'San Marino', value: 'SMR' },
  { label: 'Sao Tome & Principe', value: 'STP' },
  { label: 'Saudi Arabia', value: 'SAU' },
  { label: 'Senegal', value: 'SEN' },
  { label: 'Serbia', value: 'SRB' },
  { label: 'Seychelles', value: 'SYC' },
  { label: 'Sierra Leone', value: 'SLE' },
  { label: 'Singapore', value: 'SGP' },
  { label: 'Slovakia', value: 'SVK' },
  { label: 'Slovenia', value: 'SVN' },
  { label: 'Solomon Islands', value: 'SLB' },
  { label: 'Somalia', value: 'SOM' },
  { label: 'South Africa', value: 'ZAF' },
  { label: 'South Sudan', value: 'SSD' },
  { label: 'Spain', value: 'ESP' },
  { label: 'Sri Lanka', value: 'LKA' },
  { label: 'St. Helena', value: 'SHN' },
  { label: 'St. Maarten', value: 'SXM' },
  { label: 'St. Pierre & Miquelon', value: 'SPM' },
  { label: 'Sudan', value: 'SDN' },
  { label: 'Suriname', value: 'SUR' },
  { label: 'Svalbard & Jan Mayen Isla', value: 'SJM' },
  { label: 'Sweden', value: 'SWE' },
  { label: 'Switzerland', value: 'CHE' },
  { label: 'Taiwan', value: 'TWN' },
  { label: 'Tajikistan', value: 'TJK' },
  { label: 'Tanzania', value: 'TZA' },
  { label: 'Thailand', value: 'THA' },
  { label: 'Togo', value: 'TGO' },
  { label: 'Tonga', value: 'TON' },
  { label: 'Trinidad & Tobago', value: 'TTO' },
  { label: 'Tunisia', value: 'TUN' },
  { label: 'Turkey', value: 'TUR' },
  { label: 'Turkmenistan', value: 'TKM' },
  { label: 'Turks & Caicos Islands', value: 'TCA' },
  { label: 'Tuvalu', value: 'TUV' },
  { label: 'Uganda', value: 'UGA' },
  { label: 'Ukraine', value: 'UKR' },
  { label: 'United Arab Emirates', value: 'ARE' },
  { label: 'United Kingdom', value: 'GBR' },
  { label: 'United States', value: 'USA' },
  { label: 'Uruguay', value: 'URY' },
  { label: 'US Minor Outlying Islands', value: 'UMI' },
  { label: 'Uzbekistan', value: 'UZB' },
  { label: 'Vanuatu', value: 'VUT' },
  { label: 'Venezuela', value: 'VEN' },
  { label: 'Vietnam', value: 'VNM' },
  { label: 'Virgin Island (British)', value: 'VGB' },
  { label: 'Virgin Islands (U.S.)', value: 'VIR' },
  { label: 'Wallis & Futuna Islands', value: 'WLF' },
  { label: 'Western Sahara', value: 'ESH' },
  { label: 'Yemen Arab Republic', value: 'YEM' },
  { label: 'Zambia', value: 'ZMB' },
  { label: 'Zimbabwe', value: 'ZWE' },
];

function formatTemplateSelectionLabelForDisplay(
  label: string | null | undefined
): string {
  const raw = String(label ?? '').trim();
  if (!raw) return '';
  // Backend often prefixes template_selection with "MM/DD/YY HH:mm / ".
  return raw.replace(/^\d{2}\/\d{2}\/\d{2}\s+\d{2}:\d{2}\s*\/\s*/, '');
}

function normalizeServiceTypeForForm(
  value: string | null | undefined
): string | undefined {
  const t = String(value ?? '').trim();
  if (!t) return undefined;
  if (t === SERVICE_TYPE_PLACEHOLDER) return undefined;
  return t;
}

function resolveServiceTypeForSubmit(
  values: { service_type?: string; template_selection?: string | null },
  templates: BookingTemplate[],
  skipServiceTypeSelection: boolean
): string {
  const fromForm = normalizeServiceTypeForForm(values.service_type);
  if (fromForm) return fromForm;
  if (skipServiceTypeSelection && values.template_selection) {
    const tpl = templates.find((t) => t.id === values.template_selection);
    const fromTemplate = normalizeServiceTypeForForm(tpl?.service_type);
    if (fromTemplate) return fromTemplate;
  }
  return '';
}

interface BookingTemplate {
  id: string;
  account_selection: string | null;
  template_selection: string | null;
  pickup_name: string | null;
  pickup_address: string | null;
  pickup_address2?: string | null;
  pickup_address3?: string | null;
  pickup_city: string | null;
  pickup_state: string | null;
  pickup_zip: string | null;
  pickup_country: string | null;
  pickup_person: string | null;
  pickup_phone: string | null;
  pickup_email: string | null;
  pickup_special_instructions: string | null;
  delivery_name: string | null;
  delivery_address: string | null;
  delivery_address2?: string | null;
  delivery_address3?: string | null;
  delivery_city: string | null;
  delivery_state: string | null;
  delivery_zip: string | null;
  delivery_country: string | null;
  delivery_person: string | null;
  delivery_phone: string | null;
  delivery_email: string | null;
  delivery_special_instructions: string | null;
  service_type: string;
  shipment_reference: string | null;
  emergency_contact_number: string | null;
  /** Parsed from QuickSTAT html_content on the backend */
  commodity?: string | null;
  commodity_temperature?: string | null;
  packaging?: string | null;
}

const BookShipmentDrawer: React.FC<BookShipmentDrawerProps> = ({
  onClose,
  shellShipment,
  onPromoteSuccess,
}) => {
  const [form] = Form.useForm<BookShipmentFormValues>();
  const { token: authToken, decodedUserToken } = useToken();
  const token = authToken as string;
  const decodedToken = decodedUserToken();
  const username = decodedToken?.name ?? null;
  const worldCourierEnabled = canBookWorldCourier(decodedToken?.company_id);
  const userDateFormat = decodedToken?.date_format;
  const bookingDatePickerFormat =
    userDateFormat === 'DD/MM/YYYY'
      ? 'ddd, D MMM'
      : userDateFormat === 'YYYY/MM/DD'
        ? 'ddd, YYYY MMM D'
        : 'ddd, MMM D';

  const [desiredDeliveryType, setDesiredDeliveryType] = useState<
    'asap' | 'custom'
  >('asap');
  const [templates, setTemplates] = useState<BookingTemplate[]>([]);
  const [wcBookingTemplates, setWcBookingTemplates] = useState<
    WorldCourierBookingTemplateRow[]
  >([]);
  const [cryoLocationTemplates, setCryoLocationTemplates] = useState<
    CryoLocationTemplateRow[]
  >([]);
  const [cryoLocationArchiveModalVisible, setCryoLocationArchiveModalVisible] =
    useState(false);
  const [cryoLocationArchiveId, setCryoLocationArchiveId] = useState<
    string | null
  >(null);
  const [wcArchiveModalVisible, setWcArchiveModalVisible] = useState(false);
  const [wcArchiveTemplateId, setWcArchiveTemplateId] = useState<string | null>(
    null
  );
  const [wcArchiveSide, setWcArchiveSide] = useState<
    'origin' | 'destination' | null
  >(null);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [skipAccountSelection, setSkipAccountSelection] = useState(false);
  const [skipServiceTypeSelection, setSkipServiceTypeSelection] =
    useState(false);
  const [mappedContactPhone, setMappedContactPhone] = useState<string | null>(
    null
  );
  const { couriers } = useFetchCompanyCouriers();
  const cryoportEnabled = isCryoportCompany(decodedToken?.company_id);
  const specialtyProviders = useSpecialtyProviders(token);
  const [specialtyOptions, setSpecialtyOptions] =
    useState<SpecialtyOptionsResponse | null>(null);
  const [specialtyOptionsLoading, setSpecialtyOptionsLoading] = useState(false);
  const teamsList = useSelector(
    (state: { teams: { list: Array<{ id?: string; name: string }> } }) =>
      state.teams.list
  );
  const specialtyHasValidTeams = !(
    teamsList.length === 0 ||
    (teamsList.length === 1 && teamsList[0].name.toLowerCase() === 'default')
  );
  const [specialtySelectedTeams, setSpecialtySelectedTeams] = useState<
    TeamDropdownSelectTeam[]
  >([]);
  const [specialtyAllTeams, setSpecialtyAllTeams] = useState<
    TeamDropdownSelectTeam[]
  >([]);
  const [specialtyTeamsSearch, setSpecialtyTeamsSearch] = useState('');
  const [specialtyDebouncedTeamsSearch, setSpecialtyDebouncedTeamsSearch] =
    useState('');
  const [specialtySelectedProducts, setSpecialtySelectedProducts] = useState<
    SelectedProduct[]
  >([]);
  const [specialtyRefStatus, setSpecialtyRefStatus] =
    useState<SpecialtyRefStatus>('idle');
  const [specialtyRefMatchedShipmentId, setSpecialtyRefMatchedShipmentId] =
    useState<string | null>(null);
  const specialtyRefLookupTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const specialtyBookingKeyRef = useRef<BookingKeyRef['current']>(null);
  const [specialtyCompletionOpen, setSpecialtyCompletionOpen] = useState(false);
  const [specialtyTrackingNumber, setSpecialtyTrackingNumber] = useState<
    string | null
  >(null);
  const [
    specialtyCompletionProviderTitle,
    setSpecialtyCompletionProviderTitle,
  ] = useState<string | null>(null);
  const [specialtyBookingFailure, setSpecialtyBookingFailure] =
    useState<BookingFailure | null>(null);
  const [specialtyPendingPayload, setSpecialtyPendingPayload] = useState<
    UnsavedSpecialtyBooking['payload'] | null
  >(null);
  const specialtyCompanyId = decodedToken?.company_id;
  const [specialtyUnsavedBooking, setSpecialtyUnsavedBookingState] =
    useState<UnsavedSpecialtyBooking | null>(() =>
      loadUnsavedSpecialtyBooking(specialtyCompanyId)
    );
  const setSpecialtyUnsavedBooking = (
    booking: UnsavedSpecialtyBooking | null
  ) => {
    setSpecialtyUnsavedBookingState(booking);
    storeUnsavedSpecialtyBooking(specialtyCompanyId, booking);
  };
  const [wcTemplatesFetchSettled, setWcTemplatesFetchSettled] =
    useState(!worldCourierEnabled);
  const [wcProjectSelectOptions, setWcProjectSelectOptions] = useState<
    WcProjectSelectOption[]
  >([]);
  const [wcProjectOptionsFetchSettled, setWcProjectOptionsFetchSettled] =
    useState(!worldCourierEnabled);
  const [cryoTemplatesFetchSettled, setCryoTemplatesFetchSettled] =
    useState(!cryoportEnabled);
  const prefillAppliedRef = useRef(false);
  // Reactive mirror of prefillAppliedRef so Phase B re-runs once Phase A lands.
  // A ref change never re-triggers an effect; Phase A can settle after Phase B's
  // first (bailed) run, so Phase B needs this in its dependency array or it would
  // never fire and shell template prefill would silently do nothing.
  const [phaseAApplied, setPhaseAApplied] = useState(false);
  // Phase B (WC template match after Project chosen) fires once per shell open.
  const shellTemplatePrefillAppliedRef = useRef(false);
  // Snapshot of the Phase A shell WC address fields, restored on project change
  // when the user hasn't edited the template-applied values.
  const shellWcAddressSnapshotRef = useRef<Partial<BookShipmentFormValues>>({});
  const [wcPromoteCourierId, setWcPromoteCourierId] = useState<
    string | undefined
  >(undefined);
  const [cryoPromoteCourierId, setCryoPromoteCourierId] = useState<
    string | undefined
  >(undefined);

  // Summary information modal + polling for quickstat_confirmation_screenshot_url
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [confirmationUrl, setConfirmationUrl] = useState<string | null>(null);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [confirmationError, setConfirmationError] = useState<string | null>(
    null
  );
  const [confirmationTimedOut, setConfirmationTimedOut] = useState(false);
  const [confirmationImageLoaded, setConfirmationImageLoaded] = useState(false);
  const [quickstatPortalOrderNumber, setQuickstatPortalOrderNumber] = useState<
    string | null
  >(null);
  const [quickstatCompletionOpen, setQuickstatCompletionOpen] = useState(false);
  const [wcCompletionOpen, setWcCompletionOpen] = useState(false);
  const [cryoCompletionOpen, setCryoCompletionOpen] = useState(false);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [prepareSubmitting, setPrepareSubmitting] = useState(false);
  const [pendingFinalAction, setPendingFinalAction] = useState<
    'CONFIRM' | 'CANCEL' | null
  >(null);
  /** WC bookings: summary polls for House Waybill PDF (separate from QuickStat screenshot). */
  const [summaryIsWorldCourier, setSummaryIsWorldCourier] = useState(false);
  const [wcHouseWaybillUrl, setWcHouseWaybillUrl] = useState<string | null>(
    null
  );
  const [wcHouseWaybillLoading, setWcHouseWaybillLoading] = useState(false);
  const [wcHouseWaybillError, setWcHouseWaybillError] = useState<string | null>(
    null
  );
  const [wcHouseWaybillTimedOut, setWcHouseWaybillTimedOut] = useState(false);
  const [summaryIsCryoport, setSummaryIsCryoport] = useState(false);
  const [cryoPrepareLoading, setCryoPrepareLoading] = useState(false);
  const [cryoPrepareError, setCryoPrepareError] = useState<string | null>(null);
  const [cryoPrepareTimedOut, setCryoPrepareTimedOut] = useState(false);
  const [cryoPortalOrderNumber, setCryoPortalOrderNumber] = useState<
    string | null
  >(null);
  const [cryoSummaryReady, setCryoSummaryReady] = useState(false);
  const [cryoReviewScreenshotUrl, setCryoReviewScreenshotUrl] = useState<
    string | null
  >(null);
  const [cryoReviewImageLoaded, setCryoReviewImageLoaded] = useState(false);
  /** QuickStat only: email notification matrix collapsed by default. */
  const [
    quickstatEmailNotifPrefsExpanded,
    setQuickstatEmailNotifPrefsExpanded,
  ] = useState(false);
  const pollTimeoutRef = useRef<number | null>(null);
  const wcHouseWaybillPollRef = useRef<number | null>(null);
  const cryoPreparePollRef = useRef<number | null>(null);

  const stopPolling = () => {
    if (pollTimeoutRef.current) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const stopWcHouseWaybillPolling = () => {
    if (wcHouseWaybillPollRef.current) {
      window.clearTimeout(wcHouseWaybillPollRef.current);
      wcHouseWaybillPollRef.current = null;
    }
  };

  const stopCryoPreparePolling = () => {
    if (cryoPreparePollRef.current) {
      window.clearTimeout(cryoPreparePollRef.current);
      cryoPreparePollRef.current = null;
    }
  };

  const closeSummaryModal = () => {
    setSummaryOpen(false);
    setSummaryIsWorldCourier(false);
    setSummaryIsCryoport(false);
    stopPolling();
    stopWcHouseWaybillPolling();
    stopCryoPreparePolling();
    setWcHouseWaybillUrl(null);
    setWcHouseWaybillLoading(false);
    setWcHouseWaybillError(null);
    setWcHouseWaybillTimedOut(false);
    setQuickstatPortalOrderNumber(null);
    setCryoPrepareLoading(false);
    setCryoPrepareError(null);
    setCryoPrepareTimedOut(false);
    setCryoPortalOrderNumber(null);
    setCryoSummaryReady(false);
    setCryoReviewScreenshotUrl(null);
    setCryoReviewImageLoaded(false);
  };

  const handleBookAndTraceShipment = () => {
    if (confirmSubmitting) return;
    if (summaryIsCryoport) {
      if (!bookingId) {
        message.error('Missing booking.');
        return;
      }
      setPendingFinalAction('CONFIRM');
      setConfirmSubmitting(true);
      const _ = PostCryoportBookingAction(token, bookingId, {
        action: 'CONFIRM',
      })
        .then(() => {
          message.success('Booking confirmed');
          closeSummaryModal();
          return true;
        })
        .catch(() => {
          message.error('Could not save confirmation. Please try again.');
          return false;
        })
        .finally(() => {
          setPendingFinalAction(null);
          setConfirmSubmitting(false);
        })
        .then((confirmed) => {
          if (confirmed) setCryoCompletionOpen(true);
        });
      return;
    }
    if (summaryIsWorldCourier) {
      if (!bookingId) {
        message.error('Missing booking.');
        return;
      }
      setPendingFinalAction('CONFIRM');
      setConfirmSubmitting(true);
      const _ = PostWorldCourierBookingAction(token, bookingId, {
        action: 'CONFIRM',
      })
        .then(() => {
          message.success('Booking confirmed');
          closeSummaryModal();
          return true;
        })
        .catch(() => {
          message.error('Could not save confirmation. Please try again.');
          return false;
        })
        .finally(() => {
          setPendingFinalAction(null);
          setConfirmSubmitting(false);
        })
        .then((confirmed) => {
          if (confirmed) setWcCompletionOpen(true);
        });
      return;
    }
    if (!bookingId) {
      message.error('Missing booking.');
      return;
    }
    setPendingFinalAction('CONFIRM');
    setConfirmSubmitting(true);
    PostShipmentBookingAction(token, bookingId, { action: 'CONFIRM' })
      .then(() => {
        message.success('Booking confirmed');
        closeSummaryModal();
        setQuickstatCompletionOpen(true);
      })
      .catch(() => {
        message.error('Could not save confirmation. Please try again.');
      })
      .finally(() => {
        setPendingFinalAction(null);
        setConfirmSubmitting(false);
      });
  };

  const handleCancelBooking = () => {
    if (confirmSubmitting) return;
    const bookingAlreadyFailed = summaryIsCryoport
      ? cryoPrepareTimedOut || cryoPrepareError === 'failed'
      : summaryIsWorldCourier
        ? wcHouseWaybillTimedOut || wcHouseWaybillError === 'failed'
        : confirmationTimedOut || confirmationError === 'failed';
    if (bookingAlreadyFailed) {
      closeSummaryModal();
      return;
    }
    if (summaryIsCryoport) {
      if (!bookingId) {
        closeSummaryModal();
        return;
      }
      setPendingFinalAction('CANCEL');
      setConfirmSubmitting(true);
      PostCryoportBookingAction(token, bookingId, {
        action: 'CANCEL',
        reason: 'Cancelled by user from summary modal.',
      })
        .then(() => {
          message.success('Booking cancelled');
          closeSummaryModal();
        })
        .catch(() => {
          message.error('Could not cancel booking. Please try again.');
        })
        .finally(() => {
          setPendingFinalAction(null);
          setConfirmSubmitting(false);
        });
      return;
    }
    if (summaryIsWorldCourier) {
      if (!bookingId) {
        closeSummaryModal();
        return;
      }
      setPendingFinalAction('CANCEL');
      setConfirmSubmitting(true);
      PostWorldCourierBookingAction(token, bookingId, {
        action: 'CANCEL',
        reason: 'Cancelled by user from summary modal.',
      })
        .then(() => {
          message.success('Booking cancelled');
          closeSummaryModal();
        })
        .catch(() => {
          message.error('Could not cancel booking. Please try again.');
        })
        .finally(() => {
          setPendingFinalAction(null);
          setConfirmSubmitting(false);
        });
      return;
    }
    if (!bookingId) {
      closeSummaryModal();
      return;
    }
    setPendingFinalAction('CANCEL');
    setConfirmSubmitting(true);
    PostShipmentBookingAction(token, bookingId, {
      action: 'CANCEL',
      reason: 'Cancelled by user from summary modal.',
    })
      .then(() => {
        message.success('Booking cancelled');
        closeSummaryModal();
      })
      .catch(() => {
        message.error('Could not cancel booking. Please try again.');
      })
      .finally(() => {
        setPendingFinalAction(null);
        setConfirmSubmitting(false);
      });
  };

  const courierOptions = useMemo(() => {
    const quickStatCouriers = filterQuickStatCompanyCouriers(couriers);
    return [
      ...quickStatCouriers.map((c) => ({
        label: c.courier_name,
        value: c.courier_id,
      })),
      ...(worldCourierEnabled
        ? [
            {
              label: 'World Courier',
              value: WORLD_COURIER_TRANSPORT_PROVIDER_ID,
            },
          ]
        : []),
      ...(cryoportEnabled
        ? [
            {
              label: 'Cryoport',
              value: CRYOPORT_TRANSPORT_PROVIDER_ID,
            },
          ]
        : []),
      ...specialtyProviderOptions(specialtyProviders),
    ];
  }, [couriers, worldCourierEnabled, cryoportEnabled, specialtyProviders]);

  const singleEligibleTransportProviderId = useMemo(() => {
    return courierOptions.length === 1 ? String(courierOptions[0].value) : null;
  }, [courierOptions]);

  const selectedTransportProvider = Form.useWatch('transport_provider', form);
  const specialtyProviderKey = parseSpecialtyTransportProvider(
    selectedTransportProvider
  );
  const isSpecialty = Boolean(specialtyProviderKey);
  const selectedSpecialtyProvider = useMemo(
    () =>
      specialtyProviders.find((p) => p.provider === specialtyProviderKey) ??
      null,
    [specialtyProviders, specialtyProviderKey]
  );
  const specialtyRequiresPieces =
    selectedSpecialtyProvider?.requiresPieces ??
    specialtyOptions?.requiresPieces !== false;
  const isWorldCourier =
    selectedTransportProvider === WORLD_COURIER_TRANSPORT_PROVIDER_ID;
  const isCryoport =
    selectedTransportProvider === CRYOPORT_TRANSPORT_PROVIDER_ID;
  const specialtyReference = Form.useWatch('specialty_reference', form);
  const specialtyPromoteTargetId = quotePromoteTargetId(
    false,
    shellShipment?.id,
    specialtyRefStatus === 'shell' ? specialtyRefMatchedShipmentId : null
  );
  const showSpecialtyZoomlogiFields = shouldShowQuoteTeamsProducts(
    false,
    specialtyRefStatus,
    specialtyPromoteTargetId
  );
  const {
    products: specialtyPaginatedProducts,
    getProductName: getSpecialtyProductName,
    mergeIntoLookup: mergeSpecialtyProductLookup,
    setSearch: setSpecialtyProductSearch,
    searchInputKey: specialtyProductSearchInputKey,
    loading: specialtyProductDropdownLoading,
    catalogTotalCount: specialtyProductCatalogTotalCount,
    search: specialtyProductSearch,
    handlePopupScroll: handleSpecialtyProductPopupScroll,
  } = usePaginatedProductDropdown({
    token: token ?? '',
    enabled: isSpecialty && showSpecialtyZoomlogiFields,
  });

  const wcSpecificPickupEnabled = Boolean(
    Form.useWatch('wc_specific_pickup_enabled', form)
  );
  const wcSpecificDeliveryEnabled = Boolean(
    Form.useWatch('wc_specific_delivery_enabled', form)
  );
  const wcOriginCountry = Form.useWatch('wc_origin_country', form);
  const wcDestCountry = Form.useWatch('wc_dest_country', form);

  useEffect(() => {
    if (!singleEligibleTransportProviderId) return;
    const current = form.getFieldValue('transport_provider');
    if (current !== undefined && current !== null && current !== '') return;
    form.setFieldValue('transport_provider', singleEligibleTransportProviderId);
  }, [form, singleEligibleTransportProviderId, shellShipment?.id]);

  useEffect(() => {
    const _ = GetShipmentBookingTemplates(token).then((apiRes) => {
      const res = apiRes as ApiResponse<BookingTemplate[]>;
      const data = res?.data?.data?.data ?? [];
      setTemplates(data);
    });
  }, [token]);

  useEffect(() => {
    setSpecialtyBookingFailure(null);
    setSpecialtyPendingPayload(null);
    setSpecialtySelectedTeams([]);
    setSpecialtySelectedProducts([]);
    setSpecialtyTeamsSearch('');
    setSpecialtyDebouncedTeamsSearch('');
    setSpecialtyRefStatus('idle');
    setSpecialtyRefMatchedShipmentId(null);
  }, [specialtyProviderKey]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSpecialtyDebouncedTeamsSearch(specialtyTeamsSearch);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [specialtyTeamsSearch]);

  useEffect(() => {
    if (specialtyRefLookupTimerRef.current) {
      clearTimeout(specialtyRefLookupTimerRef.current);
      specialtyRefLookupTimerRef.current = null;
    }
    if (!isSpecialty || !token) {
      setSpecialtyRefStatus('idle');
      setSpecialtyRefMatchedShipmentId(null);
      return;
    }
    const ref = String(specialtyReference ?? '').trim();
    if (!ref) {
      setSpecialtyRefStatus('idle');
      setSpecialtyRefMatchedShipmentId(null);
      return;
    }
    let cancelled = false;
    setSpecialtyRefStatus('checking');
    setSpecialtyRefMatchedShipmentId(null);
    specialtyRefLookupTimerRef.current = setTimeout(async () => {
      try {
        const res = await LookupShipmentByReference(token, ref);
        if (cancelled) return;
        const data = (
          res?.data as {
            data?: { data?: { match?: string; shipment_id?: string | null } };
          }
        )?.data?.data;
        const match = (data?.match as SpecialtyRefStatus) ?? 'none';
        setSpecialtyRefStatus(match);
        setSpecialtyRefMatchedShipmentId(data?.shipment_id ?? null);
      } catch {
        if (cancelled) return;
        setSpecialtyRefStatus('none');
        setSpecialtyRefMatchedShipmentId(null);
      }
    }, 500);
    return () => {
      cancelled = true;
      if (specialtyRefLookupTimerRef.current) {
        clearTimeout(specialtyRefLookupTimerRef.current);
        specialtyRefLookupTimerRef.current = null;
      }
    };
  }, [isSpecialty, specialtyReference, token]);

  useEffect(() => {
    if (specialtyRefStatus === 'checking' || !specialtyPromoteTargetId) return;
    setSpecialtySelectedTeams([]);
    setSpecialtySelectedProducts([]);
  }, [specialtyRefStatus, specialtyPromoteTargetId]);

  useEffect(() => {
    if (!token || !isSpecialty || !specialtyHasValidTeams) return;
    let cancelled = false;
    const teamsRequest = specialtyDebouncedTeamsSearch
      ? getTeamsDDLOFFeature(token, 'Shipment', specialtyDebouncedTeamsSearch)
      : getTeamsDDLOFFeature(token, 'Shipment');
    const _teams = teamsRequest.then((val) => {
      if (cancelled) return;
      setSpecialtyAllTeams((val || []) as TeamDropdownSelectTeam[]);
    });
    return () => {
      cancelled = true;
    };
  }, [
    specialtyDebouncedTeamsSearch,
    token,
    isSpecialty,
    specialtyHasValidTeams,
  ]);

  useEffect(() => {
    if (!token || !specialtyProviderKey) {
      setSpecialtyOptions(null);
      setSpecialtyOptionsLoading(false);
      return;
    }
    let cancelled = false;
    setSpecialtyOptionsLoading(true);
    GetSpecialtyOptions(token, specialtyProviderKey)
      .then((res) => {
        if (cancelled) return;
        const payload = unwrapSpecialtyPayload<SpecialtyOptionsResponse>(res);
        setSpecialtyOptions(payload ?? null);
      })
      .catch(() => {
        if (!cancelled) setSpecialtyOptions(null);
      })
      .finally(() => {
        if (!cancelled) setSpecialtyOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, specialtyProviderKey]);

  useEffect(() => {
    if (!isSpecialty) return;
    const prefill = shellToSpecialtyFormValues(shellShipment);
    form.setFieldsValue(prefill);
  }, [form, isSpecialty, shellShipment, specialtyProviderKey]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    GetQuickstatCompanyBookingProfile(token)
      .then((apiRes) => {
        const res = apiRes as ApiResponse<QuickstatCompanyBookingProfile>;
        if (cancelled) return;
        const profile = res?.data?.data?.data;
        const skip = Boolean(profile?.skip_account_selection_ui);
        setSkipAccountSelection(skip);
        setSkipServiceTypeSelection(
          Boolean(profile?.skip_service_type_selection)
        );
        const mapped =
          typeof profile?.mapped_contact_phone === 'string' &&
          profile.mapped_contact_phone.trim()
            ? profile.mapped_contact_phone.trim()
            : null;
        setMappedContactPhone(mapped);
        if (mapped) {
          // Mapped phone wins over any template value already in the field (e.g. if
          // the user picked a template before this profile response arrived).
          form.setFieldValue('emergency_contact_number', mapped);
        }
        if (skip) {
          setSelectedAccount('');
          form.setFieldValue('account_selection', undefined);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSkipAccountSelection(false);
          setSkipServiceTypeSelection(false);
          setMappedContactPhone(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, form]);

  const refreshWcBookingTemplates = useCallback(() => {
    if (!worldCourierEnabled || !token) {
      setWcBookingTemplates([]);
      setWcTemplatesFetchSettled(true);
      return Promise.resolve();
    }
    setWcTemplatesFetchSettled(false);
    return GetWorldCourierBookingTemplates(token)
      .then((apiRes) => {
        const res = apiRes as ApiResponse<WorldCourierBookingTemplateRow[]>;
        const raw = res?.data?.data?.data;
        setWcBookingTemplates(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        setWcBookingTemplates([]);
      })
      .finally(() => {
        setWcTemplatesFetchSettled(true);
      });
  }, [worldCourierEnabled, token]);

  const refreshWcProjectOptions = useCallback(() => {
    if (!worldCourierEnabled || !token) {
      setWcProjectSelectOptions([]);
      setWcProjectOptionsFetchSettled(true);
      return Promise.resolve();
    }
    setWcProjectOptionsFetchSettled(false);
    return GetWorldCourierProjectOptions(token)
      .then((apiRes) => {
        const res = apiRes as ApiResponse<WcProjectSelectOption[]>;
        const raw = res?.data?.data?.data;
        setWcProjectSelectOptions(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        setWcProjectSelectOptions([]);
      })
      .finally(() => {
        setWcProjectOptionsFetchSettled(true);
      });
  }, [worldCourierEnabled, token]);

  useEffect(() => {
    const _ = refreshWcProjectOptions();
  }, [refreshWcProjectOptions]);

  useEffect(() => {
    if (wcProjectSelectOptions.length !== 1) return;
    const only = wcProjectSelectOptions[0]?.value;
    if (!only) return;
    if (form.getFieldValue('wc_project')) return;
    form.setFieldValue('wc_project', only);
  }, [wcProjectSelectOptions, form]);

  useEffect(() => {
    const _ = refreshWcBookingTemplates();
  }, [refreshWcBookingTemplates]);

  const refreshCryoLocationTemplates = useCallback(() => {
    if (!cryoportEnabled || !token) {
      setCryoLocationTemplates([]);
      setCryoTemplatesFetchSettled(true);
      return Promise.resolve();
    }
    setCryoTemplatesFetchSettled(false);
    return GetCryoportLocationTemplates(token)
      .then((apiRes) => {
        const res = apiRes as ApiResponse<CryoLocationTemplateRow[]>;
        const raw = res?.data?.data?.data;
        setCryoLocationTemplates(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        setCryoLocationTemplates([]);
      })
      .finally(() => {
        setCryoTemplatesFetchSettled(true);
      });
  }, [cryoportEnabled, token]);

  useEffect(() => {
    const _ = refreshCryoLocationTemplates();
  }, [refreshCryoLocationTemplates]);

  const closeCryoLocationArchiveModal = useCallback(() => {
    setCryoLocationArchiveModalVisible(false);
    setCryoLocationArchiveId(null);
  }, []);

  const handleArchiveCryoLocation = useCallback((templateId: string) => {
    setCryoLocationArchiveId(templateId);
    setCryoLocationArchiveModalVisible(true);
  }, []);

  const confirmCryoLocationArchive = useCallback(async () => {
    const templateId = cryoLocationArchiveId;
    if (!templateId || !token) {
      closeCryoLocationArchiveModal();
      return;
    }
    try {
      await DeleteCryoportLocationTemplate(token, templateId);
      message.success('Location archived successfully.');
      await refreshCryoLocationTemplates();
      closeCryoLocationArchiveModal();
    } catch {
      message.error('Could not archive this location.');
    }
  }, [
    cryoLocationArchiveId,
    token,
    refreshCryoLocationTemplates,
    closeCryoLocationArchiveModal,
  ]);

  const saveCryoLocationTemplatesIfNeeded = useCallback(
    async (values: BookShipmentFormValues) => {
      const snapshot = cryoLocationTemplates;
      let anySaved = false;
      if (
        shouldAutoSaveCryoOriginLocation(
          snapshot as CryoLocationTemplateRowForAutosave[],
          values
        )
      ) {
        try {
          await CreateCryoportLocationTemplate(
            token,
            buildCryoOriginLocationPostBody(values)
          );
          anySaved = true;
        } catch (err) {
          console.error('Cryo origin location autosave failed', err);
          message.warning('Could not save origin location for reuse.');
        }
      }
      if (
        shouldAutoSaveCryoDestLocation(
          snapshot as CryoLocationTemplateRowForAutosave[],
          values
        )
      ) {
        try {
          await CreateCryoportLocationTemplate(
            token,
            buildCryoDestLocationPostBody(values)
          );
          anySaved = true;
        } catch (err) {
          console.error('Cryo dest location autosave failed', err);
          message.warning('Could not save destination location for reuse.');
        }
      }
      if (anySaved) {
        await refreshCryoLocationTemplates();
      }
    },
    [token, cryoLocationTemplates, refreshCryoLocationTemplates]
  );

  const resetCryoOriginFields = useCallback(() => {
    form.setFieldsValue({
      cryo_origin_site_template_id: undefined,
      cryo_origin_name: undefined,
      cryo_origin_nickname: undefined,
      cryo_origin_location_id: undefined,
      cryo_origin_latitude: undefined,
      cryo_origin_longitude: undefined,
      cryo_origin_geocode_accuracy: undefined,
      cryo_origin_street_address_line_one: undefined,
      cryo_origin_street_address_line_two: undefined,
      cryo_origin_city: undefined,
      cryo_origin_state: undefined,
      cryo_origin_zip_code: undefined,
      cryo_origin_country_code: undefined,
      cryo_origin_time_zone: undefined,
      cryo_origin_residential: false,
      cryo_origin_contact_id: undefined,
      cryo_origin_contact_first_name: undefined,
      cryo_origin_contact_last_name: undefined,
      cryo_origin_contact_email: undefined,
      cryo_origin_contact_phone_number: undefined,
      cryo_origin_site_process: undefined,
    });
  }, [form]);

  const resetCryoDestFields = useCallback(() => {
    form.setFieldsValue({
      cryo_dest_site_template_id: undefined,
      cryo_dest_name: undefined,
      cryo_dest_nickname: undefined,
      cryo_dest_location_id: undefined,
      cryo_dest_latitude: undefined,
      cryo_dest_longitude: undefined,
      cryo_dest_geocode_accuracy: undefined,
      cryo_dest_street_address_line_one: undefined,
      cryo_dest_street_address_line_two: undefined,
      cryo_dest_city: undefined,
      cryo_dest_state: undefined,
      cryo_dest_zip_code: undefined,
      cryo_dest_country_code: undefined,
      cryo_dest_time_zone: undefined,
      cryo_dest_residential: false,
      cryo_dest_contact_id: undefined,
      cryo_dest_contact_first_name: undefined,
      cryo_dest_contact_last_name: undefined,
      cryo_dest_contact_email: undefined,
      cryo_dest_contact_phone_number: undefined,
      cryo_dest_site_process: undefined,
    });
  }, [form]);

  const applyCryoLocationToOrigin = useCallback(
    (templateId: string) => {
      const loc = cryoLocationTemplates.find((t) => t.id === templateId);
      if (!loc) return;
      const s = (v: string | number | null | undefined) => {
        if (v == null) return undefined;
        const t = String(v).trim();
        return t || undefined;
      };
      const ni = (v: number | string | null | undefined) => {
        if (v == null) return undefined;
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? Math.trunc(n) : undefined;
      };
      form.setFieldsValue({
        cryo_origin_site_template_id: ni(loc.cryo_site_template_id),
        cryo_origin_name: s(loc.cryo_name) ?? s(loc.location_label),
        cryo_origin_nickname: s(loc.cryo_nickname),
        cryo_origin_location_id: ni(loc.cryo_location_id),
        cryo_origin_latitude: undefined,
        cryo_origin_longitude: undefined,
        cryo_origin_geocode_accuracy: undefined,
        cryo_origin_street_address_line_one: s(
          loc.cryo_street_address_line_one
        ),
        cryo_origin_street_address_line_two: s(
          loc.cryo_street_address_line_two
        ),
        cryo_origin_city: s(loc.cryo_city),
        cryo_origin_state: s(loc.cryo_state),
        cryo_origin_zip_code: s(loc.cryo_zip_code),
        cryo_origin_country_code: s(loc.cryo_country_code),
        cryo_origin_time_zone: s(loc.cryo_time_zone),
        cryo_origin_residential: Boolean(loc.cryo_residential),
        cryo_origin_contact_id: ni(loc.cryo_contact_id),
        cryo_origin_contact_first_name: s(loc.cryo_contact_first_name),
        cryo_origin_contact_last_name: s(loc.cryo_contact_last_name),
        cryo_origin_contact_email: s(loc.cryo_contact_email),
        cryo_origin_contact_phone_number: s(loc.cryo_contact_phone_number),
        cryo_origin_site_process: s(loc.cryo_site_process),
      });
    },
    [form, cryoLocationTemplates]
  );

  const applyCryoLocationToDestination = useCallback(
    (templateId: string) => {
      const loc = cryoLocationTemplates.find((t) => t.id === templateId);
      if (!loc) return;
      const s = (v: string | number | null | undefined) => {
        if (v == null) return undefined;
        const t = String(v).trim();
        return t || undefined;
      };
      const ni = (v: number | string | null | undefined) => {
        if (v == null) return undefined;
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? Math.trunc(n) : undefined;
      };
      form.setFieldsValue({
        cryo_dest_site_template_id: ni(loc.cryo_site_template_id),
        cryo_dest_name: s(loc.cryo_name) ?? s(loc.location_label),
        cryo_dest_nickname: s(loc.cryo_nickname),
        cryo_dest_location_id: ni(loc.cryo_location_id),
        cryo_dest_latitude: undefined,
        cryo_dest_longitude: undefined,
        cryo_dest_geocode_accuracy: undefined,
        cryo_dest_street_address_line_one: s(loc.cryo_street_address_line_one),
        cryo_dest_street_address_line_two: s(loc.cryo_street_address_line_two),
        cryo_dest_city: s(loc.cryo_city),
        cryo_dest_state: s(loc.cryo_state),
        cryo_dest_zip_code: s(loc.cryo_zip_code),
        cryo_dest_country_code: s(loc.cryo_country_code),
        cryo_dest_time_zone: s(loc.cryo_time_zone),
        cryo_dest_residential: Boolean(loc.cryo_residential),
        cryo_dest_contact_id: ni(loc.cryo_contact_id),
        cryo_dest_contact_first_name: s(loc.cryo_contact_first_name),
        cryo_dest_contact_last_name: s(loc.cryo_contact_last_name),
        cryo_dest_contact_email: s(loc.cryo_contact_email),
        cryo_dest_contact_phone_number: s(loc.cryo_contact_phone_number),
        cryo_dest_site_process: s(loc.cryo_site_process),
      });
    },
    [form, cryoLocationTemplates]
  );

  const onCryoOriginNameChange = useCallback((_v: string | undefined) => {
    // Location templates don't use a tracking ID — no deselection needed.
  }, []);

  const onCryoDestNameChange = useCallback((_v: string | undefined) => {
    // Location templates don't use a tracking ID — no deselection needed.
  }, []);

  const onCryoOriginNameBlur = useCallback(
    (_e: React.FocusEvent<HTMLInputElement>) => {},
    []
  );

  const onCryoDestNameBlur = useCallback(
    (_e: React.FocusEvent<HTMLInputElement>) => {},
    []
  );

  const cryoOriginAutocompleteOptions = useMemo(
    () =>
      buildCryoCompanyProfileAutocompleteOptions(
        cryoLocationTemplates,
        handleArchiveCryoLocation
      ),
    [cryoLocationTemplates, handleArchiveCryoLocation]
  );

  const cryoDestAutocompleteOptions = useMemo(
    () =>
      buildCryoCompanyProfileAutocompleteOptions(
        cryoLocationTemplates,
        handleArchiveCryoLocation
      ),
    [cryoLocationTemplates, handleArchiveCryoLocation]
  );

  const wcTemplatesSorted = useMemo(
    () =>
      [...wcBookingTemplates].sort((a, b) =>
        a.company_name.localeCompare(b.company_name, undefined, {
          sensitivity: 'base',
        })
      ),
    [wcBookingTemplates]
  );

  const wcProjectWatch = Form.useWatch('wc_project', form);

  /** Templates eligible for the currently selected Project. Untagged templates
   *  stay visible on every project; tagged templates only appear once their
   *  project is selected. Drives the autocomplete dropdown options (not the
   *  AutoComplete-vs-plain-input gate, which uses whether the company has any
   *  templates at all). */
  const wcTemplatesForProjectSorted = useMemo(
    () => filterWcTemplatesByProject(wcTemplatesSorted, wcProjectWatch),
    [wcTemplatesSorted, wcProjectWatch]
  );

  /** True when the current form fields for a side still equal the applied
   *  template (i.e. the user hasn't edited after the template was applied). */
  const wcSideFieldsMatchTemplate = useCallback(
    (
      side: 'origin' | 'destination',
      tpl: WorldCourierBookingTemplateRow
    ): boolean => {
      const p = side === 'origin' ? 'wc_origin' : 'wc_dest';
      // Instructions don't follow the wc_origin_/wc_dest_ prefix.
      const instructionsField =
        side === 'origin'
          ? 'wc_pickup_instructions'
          : 'wc_delivery_instructions';
      const tplInstructions =
        side === 'origin' ? tpl.pickup_instructions : tpl.delivery_instructions;
      const eq = (
        field: keyof BookShipmentFormValues,
        val: string | null | undefined
      ) =>
        String(form.getFieldValue(field) ?? '').trim() ===
        String(val ?? '').trim();
      // Must cover every field a template applies / restore clears, otherwise an
      // edited-but-unlisted field reads as "unchanged" and gets overwritten on
      // the snapshot restore during a project change.
      return (
        eq(`${p}_company_name`, tpl.company_name) &&
        eq(`${p}_contact_name`, tpl.contact_name) &&
        eq(`${p}_phone_number`, tpl.phone_number) &&
        eq(`${p}_extension`, tpl.extension) &&
        eq(`${p}_email`, tpl.email) &&
        eq(`${p}_address1`, tpl.address1) &&
        eq(`${p}_address2`, tpl.address2) &&
        eq(`${p}_city`, tpl.city) &&
        eq(`${p}_state_province`, tpl.state_province) &&
        eq(`${p}_postal_code`, tpl.postal_code) &&
        eq(`${p}_country`, coerceLegacyCountryCode(tpl.country)) &&
        eq(instructionsField, tplInstructions)
      );
    },
    [form]
  );

  /** Restore a side's WC address fields from the Phase A shell snapshot and
   *  clear the template-applied company/contact/instruction fields + id. */
  const restoreShellWcSideFromSnapshot = useCallback(
    (side: 'origin' | 'destination') => {
      const s = shellWcAddressSnapshotRef.current;
      if (side === 'origin') {
        form.setFieldsValue({
          wc_origin_template_id: undefined,
          wc_origin_company_name: undefined,
          wc_origin_contact_name: undefined,
          wc_origin_phone_number: undefined,
          wc_origin_extension: undefined,
          wc_origin_email: undefined,
          wc_pickup_instructions: undefined,
          wc_origin_address1: s.wc_origin_address1,
          wc_origin_address2: undefined,
          wc_origin_city: s.wc_origin_city,
          wc_origin_state_province: s.wc_origin_state_province,
          wc_origin_postal_code: s.wc_origin_postal_code,
          wc_origin_country: s.wc_origin_country,
        });
      } else {
        form.setFieldsValue({
          wc_dest_template_id: undefined,
          wc_dest_company_name: undefined,
          wc_dest_contact_name: undefined,
          wc_dest_phone_number: undefined,
          wc_dest_extension: undefined,
          wc_dest_email: undefined,
          wc_delivery_instructions: undefined,
          wc_dest_address1: s.wc_dest_address1,
          wc_dest_address2: undefined,
          wc_dest_city: s.wc_dest_city,
          wc_dest_state_province: s.wc_dest_state_province,
          wc_dest_postal_code: s.wc_dest_postal_code,
          wc_dest_country: s.wc_dest_country,
        });
      }
    },
    [form]
  );

  const resetWcOriginFromTemplateSelect = useCallback(() => {
    form.setFieldsValue({
      wc_origin_template_id: undefined,
      wc_origin_company_name: undefined,
      wc_origin_contact_name: undefined,
      wc_origin_phone_number: undefined,
      wc_origin_extension: undefined,
      wc_origin_email: undefined,
      wc_origin_address1: undefined,
      wc_origin_address2: undefined,
      wc_origin_city: undefined,
      wc_origin_state_province: undefined,
      wc_origin_postal_code: undefined,
      wc_origin_country: undefined,
      wc_pickup_instructions: undefined,
    });
  }, [form]);

  const resetWcDestFromTemplateSelect = useCallback(() => {
    form.setFieldsValue({
      wc_dest_template_id: undefined,
      wc_dest_company_name: undefined,
      wc_dest_contact_name: undefined,
      wc_dest_phone_number: undefined,
      wc_dest_extension: undefined,
      wc_dest_email: undefined,
      wc_dest_address1: undefined,
      wc_dest_address2: undefined,
      wc_dest_city: undefined,
      wc_dest_state_province: undefined,
      wc_dest_postal_code: undefined,
      wc_dest_country: undefined,
      wc_delivery_instructions: undefined,
    });
  }, [form]);

  const applyWcTemplateToOrigin = useCallback(
    (templateId: string) => {
      const tpl = wcBookingTemplates.find((t) => t.id === templateId);
      if (!tpl) return;
      const next: Partial<BookShipmentFormValues> = {
        wc_origin_template_id: templateId,
        wc_origin_company_name: tpl.company_name,
        wc_origin_contact_name: tpl.contact_name ?? undefined,
        wc_origin_phone_number: tpl.phone_number ?? undefined,
        wc_origin_extension: tpl.extension ?? undefined,
        wc_origin_email: tpl.email ?? undefined,
        wc_origin_address1: tpl.address1 ?? undefined,
        wc_origin_address2: tpl.address2 ?? undefined,
        wc_origin_city: tpl.city ?? undefined,
        wc_origin_state_province: tpl.state_province ?? undefined,
        wc_origin_postal_code: tpl.postal_code ?? undefined,
        wc_origin_country: coerceLegacyCountryCode(tpl.country),
        wc_pickup_instructions: tpl.pickup_instructions?.trim()
          ? tpl.pickup_instructions
          : undefined,
      };
      form.setFieldsValue(next);
    },
    [form, wcBookingTemplates]
  );

  const applyWcTemplateToDestination = useCallback(
    (templateId: string) => {
      const tpl = wcBookingTemplates.find((t) => t.id === templateId);
      if (!tpl) return;
      const next: Partial<BookShipmentFormValues> = {
        wc_dest_template_id: templateId,
        wc_dest_company_name: tpl.company_name,
        wc_dest_contact_name: tpl.contact_name ?? undefined,
        wc_dest_phone_number: tpl.phone_number ?? undefined,
        wc_dest_extension: tpl.extension ?? undefined,
        wc_dest_email: tpl.email ?? undefined,
        wc_dest_address1: tpl.address1 ?? undefined,
        wc_dest_address2: tpl.address2 ?? undefined,
        wc_dest_city: tpl.city ?? undefined,
        wc_dest_state_province: tpl.state_province ?? undefined,
        wc_dest_postal_code: tpl.postal_code ?? undefined,
        wc_dest_country: coerceLegacyCountryCode(tpl.country),
        wc_delivery_instructions: tpl.delivery_instructions?.trim()
          ? tpl.delivery_instructions
          : undefined,
      };
      form.setFieldsValue(next);
    },
    [form, wcBookingTemplates]
  );

  /** On Project change: clear/restore any template selection that is no longer
   *  compatible with the newly selected project. Menu Book preserves manually
   *  typed fields (clear id only); shell restores the Phase A address snapshot
   *  when the user hasn't edited the template-applied values, then re-matches a
   *  template under the new project for restored sides. */
  const handleWcProjectChange = useCallback(
    (value: unknown) => {
      const project = String(value ?? '').trim();

      const handleSide = (side: 'origin' | 'destination'): boolean => {
        const idField =
          side === 'origin' ? 'wc_origin_template_id' : 'wc_dest_template_id';
        const tid = form.getFieldValue(idField) as string | undefined;
        if (!tid) return false;
        const tpl = wcBookingTemplates.find((t) => t.id === tid);
        if (tpl && isWcTemplateCompatibleWithProject(tpl, project)) {
          return false;
        }
        if (shellShipment && tpl && wcSideFieldsMatchTemplate(side, tpl)) {
          restoreShellWcSideFromSnapshot(side);
          return true;
        }
        // Menu book, or user edited the template values: clear id only.
        form.setFieldValue(idField, undefined);
        return false;
      };

      const originRestored = handleSide('origin');
      const destRestored = handleSide('destination');

      if (shellShipment && project && (originRestored || destRestored)) {
        const filtered = filterWcTemplatesByProject(
          wcBookingTemplates,
          project
        );
        if (originRestored) {
          const m = findBestWcTemplateForLocation(
            shellShipment.from_location,
            filtered
          );
          if (m) applyWcTemplateToOrigin(m.templateId);
        }
        if (destRestored) {
          const m = findBestWcTemplateForLocation(
            shellShipment.to_location,
            filtered
          );
          if (m) applyWcTemplateToDestination(m.templateId);
        }
      }
    },
    [
      form,
      wcBookingTemplates,
      shellShipment,
      wcSideFieldsMatchTemplate,
      restoreShellWcSideFromSnapshot,
      applyWcTemplateToOrigin,
      applyWcTemplateToDestination,
    ]
  );

  useEffect(() => {
    prefillAppliedRef.current = false;
    setPhaseAApplied(false);
    shellTemplatePrefillAppliedRef.current = false;
    shellWcAddressSnapshotRef.current = {};
  }, [shellShipment?.id]);

  useEffect(() => {
    if (!shellShipment?.id || !token) {
      setWcPromoteCourierId(undefined);
      setCryoPromoteCourierId(undefined);
      return;
    }
    let cancelled = false;
    const _ = getCompanyCouriersDDL(token).then((list) => {
      if (cancelled) return;
      setWcPromoteCourierId(
        resolveCourierIdForTransportProvider(list, 'WORLD_COURIER')
      );
      setCryoPromoteCourierId(
        resolveCourierIdForTransportProvider(list, 'CRYOPORT')
      );
    });
    return () => {
      cancelled = true;
    };
  }, [shellShipment?.id, token]);

  // Phase A: one-time shell prefill of address-only WC fields + Cryoport
  // matching/address + reference. Never applies a WC template — WC template
  // matching is deferred to Phase B (after Project is chosen) so we don't apply
  // a wrong-site profile before the user picks Philadelphia/Allendale.
  useEffect(() => {
    if (!shellShipment || prefillAppliedRef.current) return;
    if (worldCourierEnabled && !wcTemplatesFetchSettled) return;
    if (worldCourierEnabled && !wcProjectOptionsFetchSettled) return;
    if (cryoportEnabled && !cryoTemplatesFetchSettled) return;

    const wcAddr = worldCourierEnabled
      ? shellToWcAddressOnlyFormValues(shellShipment)
      : {};
    const cryo = cryoportEnabled
      ? shellToCryoFormValues(shellShipment, cryoLocationTemplates)
      : { formValues: {} as Partial<BookShipmentFormValues> };

    if ('cryoOriginTemplateId' in cryo && cryo.cryoOriginTemplateId) {
      applyCryoLocationToOrigin(cryo.cryoOriginTemplateId);
    }
    if ('cryoDestTemplateId' in cryo && cryo.cryoDestTemplateId) {
      applyCryoLocationToDestination(cryo.cryoDestTemplateId);
    }

    const merged = { ...wcAddr, ...cryo.formValues };
    if (Object.keys(merged).length > 0) {
      form.setFieldsValue(merged);
    }

    // Snapshot the shell WC address so a later project change can restore it
    // (instead of leaving a stale wrong-site template's address in place).
    shellWcAddressSnapshotRef.current = {
      wc_origin_address1: wcAddr.wc_origin_address1,
      wc_origin_city: wcAddr.wc_origin_city,
      wc_origin_state_province: wcAddr.wc_origin_state_province,
      wc_origin_postal_code: wcAddr.wc_origin_postal_code,
      wc_origin_country: wcAddr.wc_origin_country,
      wc_dest_address1: wcAddr.wc_dest_address1,
      wc_dest_city: wcAddr.wc_dest_city,
      wc_dest_state_province: wcAddr.wc_dest_state_province,
      wc_dest_postal_code: wcAddr.wc_dest_postal_code,
      wc_dest_country: wcAddr.wc_dest_country,
    };

    prefillAppliedRef.current = true;
    setPhaseAApplied(true);
  }, [
    shellShipment,
    worldCourierEnabled,
    cryoportEnabled,
    wcTemplatesFetchSettled,
    wcProjectOptionsFetchSettled,
    cryoTemplatesFetchSettled,
    cryoLocationTemplates,
    applyCryoLocationToOrigin,
    applyCryoLocationToDestination,
    form,
  ]);

  // Phase B: once (a) Phase A applied, (b) templates loaded, and (c) a Project
  // is selected, match WC templates against the project-filtered list and apply
  // origin/dest profiles. Runs once per shell open; does not re-run on later
  // project changes (that is handled by handleWcProjectChange).
  useEffect(() => {
    if (!shellShipment || !worldCourierEnabled) return;
    if (shellTemplatePrefillAppliedRef.current) return;
    // phaseAApplied (state, not the ref) is what makes this effect re-run once
    // Phase A finishes after an earlier bailed pass — see the note on the ref.
    if (!phaseAApplied) return;
    if (!wcTemplatesFetchSettled) return;
    const project = String(form.getFieldValue('wc_project') ?? '').trim();
    if (!project) return;

    const filtered = filterWcTemplatesByProject(wcBookingTemplates, project);
    const originMatch = findBestWcTemplateForLocation(
      shellShipment.from_location,
      filtered
    );
    const destMatch = findBestWcTemplateForLocation(
      shellShipment.to_location,
      filtered
    );
    if (originMatch) applyWcTemplateToOrigin(originMatch.templateId);
    if (destMatch) applyWcTemplateToDestination(destMatch.templateId);

    shellTemplatePrefillAppliedRef.current = true;
  }, [
    shellShipment,
    worldCourierEnabled,
    phaseAApplied,
    wcTemplatesFetchSettled,
    wcProjectWatch,
    wcBookingTemplates,
    applyWcTemplateToOrigin,
    applyWcTemplateToDestination,
    form,
  ]);

  /** Clear profile picker as soon as company name diverges so override is obvious (blur keeps same rule as fallback). */
  const onWcOriginCompanyNameChange = useCallback(
    (v: string | undefined) => {
      const raw = String(v ?? '');
      if (
        raw === WC_ENTER_NEW_ADDRESS_SENTINEL ||
        raw.startsWith(WC_TEMPLATE_AUTOCOMPLETE_PREFIX)
      ) {
        return;
      }
      const trimmed = raw.trim();
      const tid = form.getFieldValue('wc_origin_template_id') as
        string | undefined;
      if (!tid) return;
      const tpl = wcBookingTemplates.find((t) => t.id === tid);
      if (!tpl) return;
      if (trimmed !== String(tpl.company_name || '').trim()) {
        form.setFieldValue('wc_origin_template_id', undefined);
      }
    },
    [form, wcBookingTemplates]
  );

  const onWcDestCompanyNameChange = useCallback(
    (v: string | undefined) => {
      const raw = String(v ?? '');
      if (
        raw === WC_ENTER_NEW_ADDRESS_SENTINEL ||
        raw.startsWith(WC_TEMPLATE_AUTOCOMPLETE_PREFIX)
      ) {
        return;
      }
      const trimmed = raw.trim();
      const tid = form.getFieldValue('wc_dest_template_id') as
        string | undefined;
      if (!tid) return;
      const tpl = wcBookingTemplates.find((t) => t.id === tid);
      if (!tpl) return;
      if (trimmed !== String(tpl.company_name || '').trim()) {
        form.setFieldValue('wc_dest_template_id', undefined);
      }
    },
    [form, wcBookingTemplates]
  );

  const onWcOriginCompanyNameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const v = e.target.value;
      const tid = form.getFieldValue('wc_origin_template_id') as
        string | undefined;
      if (!tid) return;
      const tpl = wcBookingTemplates.find((t) => t.id === tid);
      if (!tpl) return;
      if (v.trim() !== String(tpl.company_name || '').trim()) {
        form.setFieldValue('wc_origin_template_id', undefined);
      }
    },
    [form, wcBookingTemplates]
  );

  const onWcDestCompanyNameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const v = e.target.value;
      const tid = form.getFieldValue('wc_dest_template_id') as
        string | undefined;
      if (!tid) return;
      const tpl = wcBookingTemplates.find((t) => t.id === tid);
      if (!tpl) return;
      if (v.trim() !== String(tpl.company_name || '').trim()) {
        form.setFieldValue('wc_dest_template_id', undefined);
      }
    },
    [form, wcBookingTemplates]
  );

  const saveWcBookingTemplatesIfNeeded = useCallback(
    async (values: BookShipmentFormValues) => {
      const snapshot = wcBookingTemplates;
      let anySaved = false;
      if (shouldAutoSaveOriginTemplate(snapshot, values)) {
        try {
          await CreateWorldCourierBookingTemplate(
            token,
            buildWcOriginTemplatePostBody(values)
          );
          anySaved = true;
        } catch (err) {
          console.error('WC origin template autosave failed', err);
          message.warning('Could not save origin company profile for reuse.');
        }
      }
      if (shouldAutoSaveDestTemplate(snapshot, values)) {
        try {
          await CreateWorldCourierBookingTemplate(
            token,
            buildWcDestTemplatePostBody(values)
          );
          anySaved = true;
        } catch (err) {
          console.error('WC destination template autosave failed', err);
          message.warning(
            'Could not save destination company profile for reuse.'
          );
        }
      }
      if (anySaved) {
        await refreshWcBookingTemplates();
      }
    },
    [token, wcBookingTemplates, refreshWcBookingTemplates]
  );

  const closeWcArchiveModal = useCallback(() => {
    setWcArchiveModalVisible(false);
    setWcArchiveTemplateId(null);
    setWcArchiveSide(null);
  }, []);

  const handleArchiveWcTemplate = useCallback(
    (templateId: string, side: 'origin' | 'destination') => {
      setWcArchiveTemplateId(templateId);
      setWcArchiveSide(side);
      setWcArchiveModalVisible(true);
    },
    []
  );

  const wcOriginCompanyAutocompleteOptions = useMemo(
    () =>
      buildWcCompanyProfileAutocompleteOptions(
        'origin',
        wcTemplatesForProjectSorted,
        handleArchiveWcTemplate
      ),
    [wcTemplatesForProjectSorted, handleArchiveWcTemplate]
  );

  const wcDestCompanyAutocompleteOptions = useMemo(
    () =>
      buildWcCompanyProfileAutocompleteOptions(
        'destination',
        wcTemplatesForProjectSorted,
        handleArchiveWcTemplate
      ),
    [wcTemplatesForProjectSorted, handleArchiveWcTemplate]
  );

  const confirmWcArchiveTemplate = useCallback(async () => {
    const templateId = wcArchiveTemplateId;
    const side = wcArchiveSide;
    if (!templateId || !side) {
      closeWcArchiveModal();
      return;
    }
    try {
      await DeleteWorldCourierBookingTemplate(token, templateId);
      message.success('Company archived successfully.');
      await refreshWcBookingTemplates();
      const fieldName =
        side === 'origin' ? 'wc_origin_template_id' : 'wc_dest_template_id';
      if (form.getFieldValue(fieldName) === templateId) {
        if (side === 'origin') resetWcOriginFromTemplateSelect();
        else resetWcDestFromTemplateSelect();
      }
      closeWcArchiveModal();
    } catch {
      message.error('Could not archive this profile.');
    }
  }, [
    wcArchiveTemplateId,
    wcArchiveSide,
    token,
    form,
    refreshWcBookingTemplates,
    resetWcOriginFromTemplateSelect,
    resetWcDestFromTemplateSelect,
    closeWcArchiveModal,
  ]);

  /** Copy package temperature onto every line item in that package (WC portal behavior). */
  const syncWcItemTemperaturesFromPackage = useCallback(
    (
      pkgIndex: number,
      temp: unknown,
      packages: BookShipmentFormValues['wc_packages']
    ) => {
      const items = packages?.[pkgIndex]?.items;
      if (!Array.isArray(items)) return;
      items.forEach((_, itemIdx) => {
        (form.setFieldValue as (name: unknown, value: unknown) => void)(
          [
            'wc_packages',
            pkgIndex,
            'items',
            itemIdx,
            'temperature_range_refrigerant',
          ],
          temp
        );
      });
    },
    [form]
  );

  const onWorldCourierValuesChange = useCallback(
    (
      changed: Partial<BookShipmentFormValues>,
      allValues: BookShipmentFormValues
    ) => {
      const pkgs = changed.wc_packages;
      if (!pkgs || !Array.isArray(pkgs)) return;
      const allPkgs = allValues.wc_packages;
      pkgs.forEach((rowFragment, idx) => {
        if (!rowFragment || typeof rowFragment !== 'object') return;
        if (
          Object.prototype.hasOwnProperty.call(
            rowFragment,
            'temperature_range_refrigerant'
          )
        ) {
          const temp = allPkgs?.[idx]?.temperature_range_refrigerant;
          const validPkgs = getValidPackageNameValuesForTemperature(temp);
          const currentPkg = String(allPkgs?.[idx]?.package_name ?? '').trim();
          const pkgNameAfter =
            currentPkg && validPkgs.has(currentPkg) ? currentPkg : undefined;
          if (currentPkg && !pkgNameAfter) {
            (form.setFieldValue as (name: unknown, value: unknown) => void)(
              ['wc_packages', idx, 'package_name'],
              undefined
            );
          }
          if (!WC_TEMPERATURES_REQUIRING_DRY_ICE.has(String(temp ?? ''))) {
            (form.setFieldValue as (name: unknown, value: unknown) => void)(
              ['wc_packages', idx, 'dry_ice_supply'],
              undefined
            );
            (form.setFieldValue as (name: unknown, value: unknown) => void)(
              ['wc_packages', idx, 'dry_ice_weight_lbs'],
              undefined
            );
          } else if (wcPackageHasBundledDryIce(pkgNameAfter ?? '')) {
            (form.setFieldValue as (name: unknown, value: unknown) => void)(
              ['wc_packages', idx, 'dry_ice_supply'],
              WC_BUNDLED_PACKAGE_DRY_ICE_SUPPLY
            );
            (form.setFieldValue as (name: unknown, value: unknown) => void)(
              ['wc_packages', idx, 'dry_ice_weight_lbs'],
              undefined
            );
          }
          // Prune extra_supplies values that are no longer valid for the new temperature
          const validForNewTemp = getValidExtraSupplyValuesForTemperature(temp);
          const currentExtras = allPkgs?.[idx]?.extra_supplies;
          if (Array.isArray(currentExtras) && currentExtras.length > 0) {
            const pruned = currentExtras.filter((v) =>
              validForNewTemp.has(String(v ?? '').trim())
            );
            if (pruned.length !== currentExtras.length) {
              (form.setFieldValue as (name: unknown, value: unknown) => void)(
                ['wc_packages', idx, 'extra_supplies'],
                pruned
              );
            }
          }
          syncWcItemTemperaturesFromPackage(idx, temp, allPkgs);
        }
        if (Object.prototype.hasOwnProperty.call(rowFragment, 'package_name')) {
          const tempForPkg = String(
            allPkgs?.[idx]?.temperature_range_refrigerant ?? ''
          );
          if (!WC_TEMPERATURES_REQUIRING_DRY_ICE.has(tempForPkg)) {
            (form.setFieldValue as (name: unknown, value: unknown) => void)(
              ['wc_packages', idx, 'dry_ice_supply'],
              undefined
            );
            (form.setFieldValue as (name: unknown, value: unknown) => void)(
              ['wc_packages', idx, 'dry_ice_weight_lbs'],
              undefined
            );
          } else {
            const pkgName = allPkgs?.[idx]?.package_name;
            if (wcPackageHasBundledDryIce(String(pkgName ?? ''))) {
              (form.setFieldValue as (name: unknown, value: unknown) => void)(
                ['wc_packages', idx, 'dry_ice_supply'],
                WC_BUNDLED_PACKAGE_DRY_ICE_SUPPLY
              );
              (form.setFieldValue as (name: unknown, value: unknown) => void)(
                ['wc_packages', idx, 'dry_ice_weight_lbs'],
                undefined
              );
            }
          }
        }
      });
    },
    [form, syncWcItemTemperaturesFromPackage]
  );

  const accountOptions = Array.from(
    new Set(templates.map((t) => t.account_selection).filter(Boolean))
  ).map((val) => ({ label: val as string, value: val as string }));

  const templateOptions = templates
    .filter(
      (t) =>
        skipAccountSelection ||
        !selectedAccount ||
        t.account_selection === selectedAccount
    )
    .map((t) => ({
      label: formatTemplateSelectionLabelForDisplay(
        t.template_selection ?? t.id
      ),
      value: t.id,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );

  const serviceTypeOptions = Array.from(
    new Set(
      templates
        .map((t) => normalizeServiceTypeForForm(t.service_type))
        .filter((v): v is string => Boolean(v))
    )
  )
    .sort((a, b) => a.localeCompare(b))
    .map((val) => ({ label: val, value: val }));

  const handleAccountChange = (val: string) => {
    setSelectedAccount(val);
    form.setFieldValue('template_selection', undefined);
  };

  const pollForConfirmationUrl = async (targetBookingId: string) => {
    stopPolling();
    setConfirmationLoading(true);
    setConfirmationError(null);
    setConfirmationTimedOut(false);
    setConfirmationUrl(null);
    setConfirmationImageLoaded(false);
    setQuickstatPortalOrderNumber(null);

    const startedAt = Date.now();
    /** Quickstat-book automation + S3 usually takes well over 30s; cap at 3 minutes. */
    const maxMs = 180_000;
    const intervalMs = 2_000;

    let sawConfirmationUrl = false;

    const tick = async () => {
      try {
        const res = await GetShipmentBookingById(token, targetBookingId);
        const wrap = res?.data?.data as
          | {
              data?: {
                quickstat_confirmation_screenshot_url?: string | null;
                quickstat_portal_order_number?: string | null;
                booking_status?: string | null;
              };
              quickstat_confirmation_screenshot_url?: string | null;
              quickstat_portal_order_number?: string | null;
              booking_status?: string | null;
            }
          | undefined;
        const url =
          wrap?.data?.quickstat_confirmation_screenshot_url ??
          wrap?.quickstat_confirmation_screenshot_url ??
          null;
        const portalOrder =
          wrap?.data?.quickstat_portal_order_number ??
          wrap?.quickstat_portal_order_number ??
          null;

        if (portalOrder) {
          setQuickstatPortalOrderNumber(portalOrder);
        }

        if (
          wrap?.booking_status === 'failed' ||
          wrap?.data?.booking_status === 'failed'
        ) {
          setConfirmationLoading(false);
          setConfirmationError('failed');
          return;
        }

        if (url) {
          sawConfirmationUrl = true;
          setConfirmationUrl(url);
          setConfirmationLoading(false);
          // Avoid hammering GetShipmentBookingById until maxMs once we have the screenshot.
          return;
        }
      } catch {
        setConfirmationError('fetch');
      }

      if (Date.now() - startedAt >= maxMs) {
        setConfirmationLoading(false);
        if (!sawConfirmationUrl) {
          setConfirmationTimedOut(true);
        }
        return;
      }

      pollTimeoutRef.current = window.setTimeout(tick, intervalMs);
    };

    await tick();
  };

  /** World Courier only: poll booking row for House Waybill PDF URL (automation fills this). */
  const pollForWorldCourierHouseWaybillPdfUrl = async (
    targetBookingId: string
  ) => {
    stopWcHouseWaybillPolling();
    setWcHouseWaybillLoading(true);
    setWcHouseWaybillError(null);
    setWcHouseWaybillTimedOut(false);
    setWcHouseWaybillUrl(null);

    const startedAt = Date.now();
    /** WC portal + PDF + S3 + DB update can run longer than QuickStat screenshot capture. */
    const maxMs = 300_000;
    const intervalMs = 2_000;

    const tick = async () => {
      try {
        const res = await GetWorldCourierBookingById(token, targetBookingId);
        setWcHouseWaybillError(null);
        const wcWrap = res?.data?.data as
          | {
              booking_status?: string | null;
              data?: { booking_status?: string | null };
            }
          | undefined;
        const url = extractWcHouseWaybillPdfUrl(res);
        if (url) {
          setWcHouseWaybillUrl(url);
          setWcHouseWaybillLoading(false);
          return;
        }
        if (
          wcWrap?.booking_status === 'failed' ||
          wcWrap?.data?.booking_status === 'failed'
        ) {
          setWcHouseWaybillLoading(false);
          setWcHouseWaybillError('failed');
          return;
        }
      } catch {
        setWcHouseWaybillError('fetch');
      }

      if (Date.now() - startedAt >= maxMs) {
        setWcHouseWaybillLoading(false);
        setWcHouseWaybillTimedOut(true);
        return;
      }

      wcHouseWaybillPollRef.current = window.setTimeout(tick, intervalMs);
    };

    await tick();
  };

  const pollForCryoportPrepared = async (targetBookingId: string) => {
    stopCryoPreparePolling();
    setCryoPrepareLoading(true);
    setCryoPrepareError(null);
    setCryoPrepareTimedOut(false);
    setCryoPortalOrderNumber(null);
    setCryoSummaryReady(false);
    setCryoReviewScreenshotUrl(null);
    setCryoReviewImageLoaded(false);

    const startedAt = Date.now();
    const maxMs = 300_000;
    const intervalMs = 2_000;

    const tick = async () => {
      try {
        const res = await GetCryoportBookingById(token, targetBookingId);
        setCryoPrepareError(null);
        const { bookingStatus, portalOrderNumber, reviewScreenshotUrl } =
          extractCryoportPrepareState(res);
        if (portalOrderNumber) {
          setCryoPortalOrderNumber(portalOrderNumber);
        }
        if (reviewScreenshotUrl) {
          setCryoReviewScreenshotUrl(reviewScreenshotUrl);
        }
        if (bookingStatus === 'prepared') {
          setCryoPrepareLoading(false);
          setCryoSummaryReady(true);
          return;
        }
        if (bookingStatus === 'failed') {
          setCryoPrepareLoading(false);
          setCryoPrepareError('failed');
          return;
        }
      } catch {
        setCryoPrepareError('fetch');
      }

      if (Date.now() - startedAt >= maxMs) {
        setCryoPrepareLoading(false);
        setCryoPrepareTimedOut(true);
        return;
      }

      cryoPreparePollRef.current = window.setTimeout(tick, intervalMs);
    };

    await tick();
  };

  useEffect(() => {
    if (
      !summaryOpen ||
      !bookingId ||
      summaryIsWorldCourier ||
      summaryIsCryoport
    )
      return;
    const _ = pollForConfirmationUrl(bookingId);
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryOpen, bookingId, summaryIsWorldCourier, summaryIsCryoport]);

  useEffect(() => {
    if (!summaryOpen || !bookingId || !summaryIsWorldCourier) return;
    const _ = pollForWorldCourierHouseWaybillPdfUrl(bookingId);
    return () => stopWcHouseWaybillPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryOpen, bookingId, summaryIsWorldCourier]);

  useEffect(() => {
    if (!summaryOpen || !bookingId || !summaryIsCryoport) return;
    const _ = pollForCryoportPrepared(bookingId);
    return () => stopCryoPreparePolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryOpen, bookingId, summaryIsCryoport]);

  useEffect(
    () => () => {
      stopPolling();
      stopWcHouseWaybillPolling();
      stopCryoPreparePolling();
    },
    []
  );

  const handleTemplateChange = (templateId: string) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    form.setFieldsValue({
      pickup_name: tpl.pickup_name ?? undefined,
      pickup_address: tpl.pickup_address ?? undefined,
      pickup_address2: tpl.pickup_address2 ?? undefined,
      pickup_address3: tpl.pickup_address3 ?? undefined,
      pickup_city: tpl.pickup_city ?? undefined,
      pickup_state: tpl.pickup_state ?? undefined,
      pickup_zip: tpl.pickup_zip ?? undefined,
      pickup_country: tpl.pickup_country ?? undefined,
      pickup_person: tpl.pickup_person ?? undefined,
      pickup_email: tpl.pickup_email ?? undefined,
      pickup_special_instructions: tpl.pickup_special_instructions ?? undefined,
      delivery_name: tpl.delivery_name ?? undefined,
      delivery_address: tpl.delivery_address ?? undefined,
      delivery_address2: tpl.delivery_address2 ?? undefined,
      delivery_address3: tpl.delivery_address3 ?? undefined,
      delivery_city: tpl.delivery_city ?? undefined,
      delivery_state: tpl.delivery_state ?? undefined,
      delivery_zip: tpl.delivery_zip ?? undefined,
      delivery_country: tpl.delivery_country ?? undefined,
      delivery_person: tpl.delivery_person ?? undefined,
      delivery_email: tpl.delivery_email ?? undefined,
      delivery_special_instructions:
        tpl.delivery_special_instructions ?? undefined,
      pickup_phone: tpl.pickup_phone ?? undefined,
      delivery_phone: tpl.delivery_phone ?? undefined,
      service_type: normalizeServiceTypeForForm(tpl.service_type),
      commodity: tpl.commodity?.trim() ? tpl.commodity.trim() : undefined,
      commodity_temperature: matchQuickstatOptionLabel(
        tpl.commodity_temperature,
        QUICKSTAT_COMMODITY_TEMPERATURE_LABELS
      ),
      packaging: matchQuickstatOptionLabel(
        tpl.packaging,
        QUICKSTAT_PACKAGING_LABELS
      ),
      shipment_reference: tpl.shipment_reference ?? undefined,
      // Mapped users: never take template emergency. Keep whatever is already in
      // the form (prefilled mapped phone, or a user override typed before picking
      // a template). Fall back to mapped only if the field is empty.
      emergency_contact_number: mappedContactPhone
        ? (() => {
            const current = form.getFieldValue('emergency_contact_number');
            const trimmed = typeof current === 'string' ? current.trim() : '';
            return trimmed || mappedContactPhone;
          })()
        : (tpl.emergency_contact_number ?? undefined),
    });
  };

  const mergeDatetimeInputText = (
    date: Dayjs | null | undefined,
    time: Dayjs | null | undefined
  ): string | null => {
    if (!date) return null;
    const base = date.startOf('day');
    const merged = time
      ? base.hour(time.hour()).minute(time.minute()).second(0)
      : base.hour(0).minute(0).second(0);
    return merged.format('YYYY-MM-DD HH:mm');
  };

  /** Ensure bundled GDI dry-ice supply is fixed and weight omitted (matches WC portal; covers disabled Select omitting values). */
  const normalizeWcPackagesForSubmit = (
    pkgs: BookShipmentFormValues['wc_packages']
  ): BookShipmentFormValues['wc_packages'] | null | undefined => {
    if (pkgs == null) return pkgs;
    if (!Array.isArray(pkgs)) return pkgs;
    return pkgs.map((pkg) => {
      if (!pkg || typeof pkg !== 'object') return pkg;
      const rest: (typeof pkgs)[number] = { ...pkg };
      const temp = String(rest.temperature_range_refrigerant ?? '');
      const pkgName = String(rest.package_name ?? '').trim();
      const validPkgForTemp = getValidPackageNameValuesForTemperature(temp);
      if (!pkgName || !validPkgForTemp.has(pkgName)) {
        delete rest.package_name;
      }
      const ex = rest.extra_supplies;
      if (ex != null) {
        const arr = Array.isArray(ex) ? ex : [ex];
        const validForTemp = getValidExtraSupplyValuesForTemperature(temp);
        const filtered = arr
          .map((x) => String(x ?? '').trim())
          .filter((v) => v && validForTemp.has(v));
        if (filtered.length === 0) {
          delete rest.extra_supplies;
        } else {
          rest.extra_supplies = filtered;
        }
      }
      if (!WC_TEMPERATURES_REQUIRING_DRY_ICE.has(temp)) {
        delete rest.dry_ice_supply;
        delete rest.dry_ice_weight_lbs;
        return rest;
      }
      if (!wcPackageHasBundledDryIce(String(rest.package_name ?? ''))) {
        return rest;
      }
      delete rest.dry_ice_weight_lbs;
      rest.dry_ice_supply = WC_BUNDLED_PACKAGE_DRY_ICE_SUPPLY;
      return rest;
    });
  };

  const submitCryoportBooking = () => {
    if (prepareSubmitting) return;
    setPrepareSubmitting(true);
    form
      .validateFields()
      .then(() => {
        const values = form.getFieldsValue(true) as BookShipmentFormValues;

        const cryo_commodities = (values.cryo_commodities ?? []).map((row) => ({
          // Numeric Cryoportal commodity IDs are used for direct lookup; non-numeric strings
          // (e.g. "PHARMA") are kept as-is so Puppeteer can drive the Select2 text search.
          id:
            cryoPosIntOrNull(row.commodity_id) ??
            cryoStrOrNull(row.commodity_id) ??
            null,
          lot_number: cryoStrOrNull(row.lot_number) ?? '',
          quantity: Number(row.quantity) || 1,
          price_currency: cryoStrOrNull(row.price_currency) ?? 'USD',
          price: parseFloat(String(row.price_per_unit ?? 1)) || 1,
          total:
            (parseFloat(String(row.price_per_unit ?? 1)) || 1) *
            (Number(row.quantity) || 1),
          name: cryoStrOrNull(row.name) ?? '',
          description: cryoStrOrNull(row.description) ?? '',
          primary_container_type:
            cryoStrOrNull(row.primary_container_type) ?? '',
          primary_container_type_description:
            cryoStrOrNull(row.primary_container_type_description) ?? '',
          primary_container_volume:
            cryoStrOrNull(row.primary_container_volume) ?? '',
          commodity_group_id: row.commodity_group_id
            ? Number(row.commodity_group_id) || null
            : null,
          commodity_category_id: row.commodity_category_id
            ? Number(row.commodity_category_id) || null
            : null,
          commodity_harmonized_code: cryoStrOrNull(row.harmonized_code),
          country_of_origin: cryoStrOrNull(row.country_of_origin) ?? 'US',
          leg_numbers: row.leg_2 ? [2] : [],
        }));

        // Always include the 3 fixed accessories, then the selected rack.
        // High Volume rack (200) also auto-adds Safepak 900 (272).
        const cryo_accessories = [
          ...CRYO_FIXED_ACCESSORIES.map((acc) => ({
            id: acc.id,
            quantity: 1,
            label: acc.label,
            source: 'user',
          })),
          ...(values.cryo_rack_id != null
            ? [
                {
                  id: values.cryo_rack_id,
                  quantity: 1,
                  label:
                    CRYO_RACK_OPTIONS.find(
                      (r) => r.value === values.cryo_rack_id
                    )?.label ?? '',
                  source: 'user',
                },
              ]
            : []),
          ...(values.cryo_rack_id === CRYO_HIGH_VOLUME_RACK_ID
            ? [
                {
                  id: CRYO_SAFEPAK_900.id,
                  quantity: 1,
                  label: CRYO_SAFEPAK_900.label,
                  source: 'user',
                },
              ]
            : []),
        ];

        const payload: Record<string, unknown> = {
          cryo_commodities,
          cryo_accessories,
          cryo_rack_id: values.cryo_rack_id ?? null,
          cryo_delivery_date:
            values.cryo_delivery_date?.format('YYYY-MM-DD') ?? null,
          cryo_pickup_date:
            values.cryo_pickup_date?.format('YYYY-MM-DD') ?? null,
          cryo_pickup_start_time: values.cryo_pickup_start_time ?? null,
          cryo_pickup_end_time: values.cryo_pickup_end_time ?? null,
          cryo_leg3_pickup_date:
            values.cryo_leg3_pickup_date?.format('YYYY-MM-DD') ?? null,
          cryo_leg3_pickup_start_time:
            values.cryo_leg3_pickup_start_time ?? null,
          cryo_leg3_pickup_end_time: values.cryo_leg3_pickup_end_time ?? null,
          cryo_po_number: String(values.cryo_po_number ?? '').trim() || null,
          cryo_study_number:
            String(values.cryo_study_number ?? '').trim() || null,
          cryo_leg2_shipment_reference:
            String(values.cryo_leg2_shipment_reference ?? '').trim() || null,
        };
        appendCryoportRouteFieldsToPayload(payload, values);

        saveCryoLocationTemplatesIfNeeded(values)
          .catch(() => {
            // non-fatal — template save failure should not block booking
          })
          .finally(() => {
            CreateCryoportBooking(token, payload)
              .then(async (apiRes) => {
                const res = apiRes as ApiResponse<{ id?: string }>;
                const newBookingId =
                  (res?.data?.data?.data?.id as string | undefined) ??
                  undefined;
                if (!newBookingId) {
                  message.error(
                    'Booking saved, but could not read booking id from response.'
                  );
                  return;
                }
                setSummaryIsWorldCourier(false);
                setSummaryIsCryoport(true);
                setBookingId(newBookingId);
                setSummaryOpen(true);
              })
              .catch(() => {
                message.error(
                  'Failed to save Cryoport booking. Please try again.'
                );
              })
              .finally(() => {
                setPrepareSubmitting(false);
              });
          });
      })
      .catch((err: unknown) => {
        warnFirstFormValidationError(form, err);
        setPrepareSubmitting(false);
      });
  };

  /** WC: save booking and open summary to review House Waybill. */
  const submitWorldCourierBooking = () => {
    if (prepareSubmitting) return;
    setPrepareSubmitting(true);
    form
      .validateFields()
      .then((values) => {
        const wcProject = String(values.wc_project ?? '').trim();
        const ocn = String(values.wc_origin_company_name || '').trim();
        const otid = values.wc_origin_template_id as string | undefined;
        if (otid) {
          const oTpl = wcBookingTemplates.find((t) => t.id === otid);
          if (
            !oTpl ||
            ocn !== String(oTpl.company_name || '').trim() ||
            !isWcTemplateCompatibleWithProject(oTpl, wcProject)
          ) {
            // Clear stale/incompatible template id only; keep field values so the
            // booking proceeds with whatever address the form currently holds.
            form.setFieldValue('wc_origin_template_id', undefined);
          }
        }
        const dcn = String(values.wc_dest_company_name || '').trim();
        const dtid = values.wc_dest_template_id as string | undefined;
        if (dtid) {
          const dTpl = wcBookingTemplates.find((t) => t.id === dtid);
          if (
            !dTpl ||
            dcn !== String(dTpl.company_name || '').trim() ||
            !isWcTemplateCompatibleWithProject(dTpl, wcProject)
          ) {
            form.setFieldValue('wc_dest_template_id', undefined);
          }
        }

        const sp = Boolean(values.wc_specific_pickup_enabled);
        const sd = Boolean(values.wc_specific_delivery_enabled);
        const earliestPickup = mergeDatetimeInputText(
          values.wc_earliest_pickup_date,
          values.wc_earliest_pickup_time
        );
        const latestPickup = mergeDatetimeInputText(
          values.wc_latest_pickup_date,
          values.wc_latest_pickup_time
        );
        const earliestDelivery = mergeDatetimeInputText(
          values.wc_earliest_delivery_date,
          values.wc_earliest_delivery_time
        );
        const latestDelivery = mergeDatetimeInputText(
          values.wc_latest_delivery_date,
          values.wc_latest_delivery_time
        );
        const payload = {
          wc_origin_company_name: values.wc_origin_company_name ?? null,
          wc_origin_contact_name: values.wc_origin_contact_name ?? null,
          wc_origin_phone_number: values.wc_origin_phone_number ?? null,
          wc_origin_extension: values.wc_origin_extension ?? null,
          wc_origin_email: values.wc_origin_email ?? null,
          wc_origin_address1: values.wc_origin_address1 ?? null,
          wc_origin_address2: values.wc_origin_address2 ?? null,
          wc_origin_city: values.wc_origin_city ?? null,
          wc_origin_state_province: values.wc_origin_state_province ?? null,
          wc_origin_postal_code: values.wc_origin_postal_code ?? null,
          wc_origin_country: values.wc_origin_country ?? null,
          wc_pickup_instructions: values.wc_pickup_instructions ?? null,
          wc_earliest_pickup_input_text: earliestPickup,
          wc_latest_pickup_input_text: sp ? null : latestPickup,
          wc_specific_pickup_enabled: sp,
          wc_specific_pickup_input_text: sp ? earliestPickup : null,
          wc_origin_all_email_notifications: Boolean(
            values.wc_origin_all_email_notifications
          ),
          wc_dest_company_name: values.wc_dest_company_name ?? null,
          wc_dest_contact_name: values.wc_dest_contact_name ?? null,
          wc_dest_phone_number: values.wc_dest_phone_number ?? null,
          wc_dest_extension: values.wc_dest_extension ?? null,
          wc_dest_email: values.wc_dest_email ?? null,
          wc_dest_address1: values.wc_dest_address1 ?? null,
          wc_dest_address2: values.wc_dest_address2 ?? null,
          wc_dest_city: values.wc_dest_city ?? null,
          wc_dest_state_province: values.wc_dest_state_province ?? null,
          wc_dest_postal_code: values.wc_dest_postal_code ?? null,
          wc_dest_country: values.wc_dest_country ?? null,
          wc_delivery_instructions: values.wc_delivery_instructions ?? null,
          wc_earliest_delivery_input_text: earliestDelivery,
          wc_latest_delivery_input_text: sd ? null : latestDelivery,
          wc_specific_delivery_enabled: sd,
          wc_specific_delivery_input_text: sd ? earliestDelivery : null,
          wc_dest_all_email_notifications: Boolean(
            values.wc_dest_all_email_notifications
          ),
          wc_packages: normalizeWcPackagesForSubmit(values.wc_packages) ?? null,
          wc_charge_code: String(values.wc_charge_code ?? '').trim() || null,
          wc_study: String(values.wc_study ?? '').trim() || null,
          wc_project: String(values.wc_project ?? '').trim(),
          wc_reference_code:
            String(values.wc_reference_code ?? '').trim() || null,
          wc_additional_notes:
            String(values.wc_additional_notes ?? '').trim() || null,
        };

        CreateWorldCourierBooking(token, payload)
          .then(async (apiRes) => {
            const res = apiRes as ApiResponse<{ id?: string }>;
            const newBookingId =
              (res?.data?.data?.data?.id as string | undefined) ?? undefined;
            if (!newBookingId) {
              message.error(
                'Booking saved, but could not read booking id from response.'
              );
              return;
            }
            await saveWcBookingTemplatesIfNeeded(values);
            setSummaryIsCryoport(false);
            setSummaryIsWorldCourier(true);
            setBookingId(newBookingId);
            setSummaryOpen(true);
          })
          .catch(() => {
            message.error(
              'Failed to save World Courier booking. Please try again.'
            );
          })
          .finally(() => {
            setPrepareSubmitting(false);
          });
      })
      .catch((err: unknown) => {
        warnFirstFormValidationError(form, err);
        setPrepareSubmitting(false);
      });
  };

  const isPromoteConflict = (err: unknown): boolean => {
    const status = (err as { status?: number } | undefined)?.status;
    if (status === 409) return true;
    const msg = formatQuoteApiError(err).toLowerCase();
    return (
      msg.includes('already has a courier') ||
      msg.includes('cannot be promoted again') ||
      msg.includes('already promoted')
    );
  };

  const findSavedSpecialtyBooking = (
    booking: UnsavedSpecialtyBooking,
    shipmentId: string | null | undefined
  ) =>
    findSavedBooking(
      shipmentId,
      { courierId: booking.courierId, trackingNumber: booking.trackingNumber },
      (id) => GetSingleShipment(token, id, true, false, 'none')
    );

  /** The company's shipment using this reference, or null when it cannot be checked. */
  const lookupReference = async (
    reference: string
  ): Promise<{ match: string; shipmentId: string | null } | null> => {
    try {
      const res = await LookupShipmentByReference(token, reference);
      const found = (
        res?.data as {
          data?: { data?: { match?: string; shipment_id?: string | null } };
        }
      )?.data?.data;
      return found?.match
        ? { match: found.match, shipmentId: found.shipment_id ?? null }
        : null;
    } catch {
      return null;
    }
  };

  /** Saves a carrier booking that already succeeded (promote or create); never books again. */
  const persistSpecialtyBooking = async (
    booking: UnsavedSpecialtyBooking,
    isRetry: boolean
  ): Promise<boolean> => {
    const { trackingNumber, linkCode, courierId, payload, promoteTargetId } =
      booking;
    try {
      if (promoteTargetId) {
        const alreadySaved =
          isRetry &&
          (await findSavedSpecialtyBooking(booking, promoteTargetId));
        if (!alreadySaved) {
          try {
            await PromoteShipment(token, promoteTargetId, {
              courier_id: courierId,
              courier_tracking_id: trackingNumber,
              ...(linkCode ? { link_code: linkCode } : {}),
            });
          } catch (err) {
            if (!isPromoteConflict(err)) throw err;
            if (!(await findSavedSpecialtyBooking(booking, promoteTargetId))) {
              throw new Error(
                'This shipment already has a different booking saved.'
              );
            }
          }
        }
      } else if (!(
        isRetry &&
        (await findSavedSpecialtyBooking(
          booking,
          (await lookupReference(payload.reference || trackingNumber))
            ?.shipmentId
        ))
      )) {
        const shipper = payload.shipper;
        const recipient = payload.recipient;
        const addr = (party?: typeof shipper) =>
          [
            party?.street,
            party?.street2,
            party?.city,
            party?.state,
            party?.postalCode,
            party?.country,
          ]
            .filter(Boolean)
            .join(', ');
        const teams =
          Array.isArray(booking.teams) && booking.teams.length
            ? booking.teams
            : undefined;
        const products =
          Array.isArray(booking.products) && booking.products.length
            ? booking.products
            : undefined;
        const shipmentBody = {
          reference_number: payload.reference || trackingNumber,
          courier: courierId,
          courier_tracking_id: trackingNumber,
          ...(linkCode ? { link_code: linkCode } : {}),
          ...(addr(shipper)
            ? {
                from_location: {
                  name: shipper.company || shipper.name || 'Pickup',
                  address: addr(shipper),
                  geocode_if_missing: true,
                },
              }
            : {}),
          ...(addr(recipient)
            ? {
                to_location: {
                  name: recipient.company || recipient.name || 'Delivery',
                  address: addr(recipient),
                  geocode_if_missing: true,
                },
              }
            : {}),
          ...(payload.readyAt
            ? { customer_preferred_pickup_date: payload.readyAt }
            : {}),
          ...(teams ? { teams } : {}),
        };
        try {
          await CreateNewShipment(token, {
            ...shipmentBody,
            ...(products ? { products } : {}),
          });
        } catch (err) {
          // The carrier already booked: never strand it on optional products.
          if (
            !products ||
            !isIncompatibleProductTemperatureError(
              formatQuoteApiError(err),
              err
            )
          ) {
            throw err;
          }
          await CreateNewShipment(token, shipmentBody);
          message.warning(
            `Saved ${trackingNumber} without products: the selected products have incompatible temperature ranges. Add compatible products on the shipment.`
          );
        }
      }
      setSpecialtyUnsavedBooking(null);
      onPromoteSuccess?.();
      return true;
    } catch (err) {
      setSpecialtyUnsavedBooking(booking);
      message.error(
        `Booked as ${trackingNumber}, but saving the shipment failed. ${formatQuoteApiError(err)} Retry the save; do not book again.`
      );
      return false;
    }
  };

  const completeSpecialtyBooking = (
    trackingNumber: string,
    providerTitle?: string | null
  ) => {
    setSpecialtyTrackingNumber(trackingNumber);
    setSpecialtyCompletionProviderTitle(providerTitle?.trim() || null);
    setSpecialtyCompletionOpen(true);
  };

  const specialtyZoomlogiTeams = () =>
    specialtySelectedTeams.map((t) => t.id).filter(Boolean);
  const specialtyZoomlogiProducts = () =>
    productsPayloadFromSelectedRows(specialtySelectedProducts);

  const updateSpecialtyProductQuantity = (id: string, delta: number) => {
    const { next, error } = applyProductQuantityDelta(
      specialtySelectedProducts,
      id,
      delta
    );
    if (error === 'min') {
      message.warning(MIN_QUANTITY_MESSAGE);
      return;
    }
    if (error === 'max') {
      message.warning(MAX_QUANTITY_MESSAGE);
      return;
    }
    setSpecialtySelectedProducts(next);
  };
  const removeSpecialtyProduct = (id: string | number) => {
    setSpecialtySelectedProducts((prev) =>
      prev.filter((p) => p.id !== String(id))
    );
  };

  const retrySpecialtySave = async () => {
    if (prepareSubmitting || !specialtyUnsavedBooking) return;
    setPrepareSubmitting(true);
    try {
      // Keep the snapshot's teams/products — editable state is not hydrated after reload.
      const booking = specialtyUnsavedBooking;
      if (await persistSpecialtyBooking(booking, true)) {
        completeSpecialtyBooking(booking.trackingNumber, booking.providerTitle);
      }
    } finally {
      setPrepareSubmitting(false);
    }
  };

  const submitSpecialtyBooking = (confirmDuplicate = false) => {
    if (prepareSubmitting || specialtyUnsavedBooking) return;
    if (!specialtyProviderKey || !selectedSpecialtyProvider) {
      message.warning('Select a specialty transport provider.');
      return;
    }
    setPrepareSubmitting(true);
    form
      .validateFields()
      .then(async (values) => {
        let payload;
        try {
          payload = buildSpecialtyBookPayload(
            specialtyProviderKey,
            values as SpecialtyFormValues,
            specialtyRequiresPieces
          );
        } catch (err) {
          message.warning(
            err instanceof Error ? err.message : 'Invalid package details'
          );
          return;
        }
        if (confirmDuplicate) {
          if (
            !specialtyPendingPayload ||
            JSON.stringify(specialtyPendingPayload) !== JSON.stringify(payload)
          ) {
            setSpecialtyBookingFailure(null);
            setSpecialtyPendingPayload(null);
            message.warning(
              'The booking details changed after the warning. Review them and book again.'
            );
            return;
          }
          payload = { ...payload, confirmDuplicate: true };
        } else {
          setSpecialtyPendingPayload(payload);
          setSpecialtyBookingFailure(null);
        }
        let promoteTargetId = quotePromoteTargetId(
          false,
          shellShipment?.id,
          null
        );
        if (!promoteTargetId && payload.reference) {
          const existing = await lookupReference(payload.reference);
          if (!existing) {
            message.error(
              'Could not check whether this reference is already used. Try again.'
            );
            return;
          }
          if (existing.match === 'booked') {
            message.error(
              `Reference ${payload.reference} is already used by a booked shipment. Use a different reference.`
            );
            return;
          }
          if (existing.match === 'shell') {
            promoteTargetId = existing.shipmentId;
          }
        }
        const attempt = await attemptBooking(
          specialtyBookingKeyRef,
          payload,
          (idempotencyKey) =>
            BookSpecialtyShipment(token, payload, idempotencyKey).then(
              (res) => {
                const data = unwrapSpecialtyPayload<{
                  trackingNumber?: string | null;
                  linkCode?: string | null;
                }>(res);
                if (!data?.trackingNumber) {
                  throw new Error(
                    'Booking succeeded without a tracking number.'
                  );
                }
                return data;
              }
            )
        );
        if (attempt.outcome === 'failed') {
          setSpecialtyBookingFailure(attempt.failure);
          const providerLabel =
            selectedSpecialtyProvider.title || specialtyProviderKey;
          message.error(
            bookingFailureMessage(attempt.failure, providerLabel) ??
              formatQuoteApiError(attempt.error)
          );
          return;
        }
        setSpecialtyBookingFailure(null);
        setSpecialtyPendingPayload(null);
        const trackingNumber = attempt.data.trackingNumber as string;
        const linkCode =
          typeof attempt.data.linkCode === 'string'
            ? attempt.data.linkCode
            : undefined;
        const booking: UnsavedSpecialtyBooking = {
          trackingNumber,
          linkCode,
          courierId: selectedSpecialtyProvider.courierId,
          providerTitle:
            selectedSpecialtyProvider.title || specialtyProviderKey,
          promoteTargetId,
          payload,
          // Teams/Products are CreateNewShipment-only (hidden on promote paths).
          ...(promoteTargetId
            ? {}
            : {
                teams: specialtyZoomlogiTeams(),
                products: specialtyZoomlogiProducts(),
              }),
        };
        // Recorded before saving so a closed tab mid-save can still be recovered.
        storeUnsavedSpecialtyBooking(specialtyCompanyId, booking);
        const saved = await persistSpecialtyBooking(booking, false);
        if (saved) {
          completeSpecialtyBooking(trackingNumber, booking.providerTitle);
        }
      })
      .catch((err: unknown) => {
        warnFirstFormValidationError(form, err);
      })
      .finally(() => {
        setPrepareSubmitting(false);
      });
  };

  const submitBooking = (saveTemplate: boolean) => {
    if (prepareSubmitting) return;
    if (isSpecialty) {
      if (specialtyUnsavedBooking) {
        const _ = retrySpecialtySave();
      } else {
        submitSpecialtyBooking(false);
      }
      return;
    }
    if (isCryoport) {
      submitCryoportBooking();
      return;
    }
    if (isWorldCourier) {
      submitWorldCourierBooking();
      return;
    }
    setPrepareSubmitting(true);
    form
      .validateFields()
      .then((values) => {
        const payload = {
          transport_provider: values.transport_provider ?? null,
          account_selection: values.account_selection ?? null,
          template_selection: values.template_selection ?? null,
          save_template: saveTemplate,
          pickup_name: values.pickup_name ?? null,
          pickup_address: values.pickup_address ?? null,
          pickup_address2: values.pickup_address2 ?? null,
          pickup_address3: values.pickup_address3 ?? null,
          pickup_city: values.pickup_city ?? null,
          pickup_state: values.pickup_state ?? null,
          pickup_zip: values.pickup_zip ?? null,
          pickup_country: values.pickup_country ?? null,
          pickup_person: values.pickup_person ?? null,
          pickup_phone: values.pickup_phone ?? null,
          pickup_email: values.pickup_email ?? null,
          pickup_special_instructions:
            values.pickup_special_instructions ?? null,
          delivery_name: values.delivery_name ?? null,
          delivery_address: values.delivery_address ?? null,
          delivery_address2: values.delivery_address2 ?? null,
          delivery_address3: values.delivery_address3 ?? null,
          delivery_city: values.delivery_city ?? null,
          delivery_state: values.delivery_state ?? null,
          delivery_zip: values.delivery_zip ?? null,
          delivery_country: values.delivery_country ?? null,
          delivery_person: values.delivery_person ?? null,
          delivery_phone: values.delivery_phone ?? null,
          delivery_email: values.delivery_email ?? null,
          delivery_special_instructions:
            values.delivery_special_instructions ?? null,
          shipment_ready_input_text: mergeDatetimeInputText(
            values.shipment_ready_date,
            values.shipment_ready_time
          ),
          desired_delivery_type: desiredDeliveryType,
          desired_delivery_input_text:
            desiredDeliveryType === 'custom'
              ? mergeDatetimeInputText(
                  values.desired_delivery_date,
                  values.desired_delivery_time
                )
              : null,
          package_packout_input_text: mergeDatetimeInputText(
            values.package_packout_date,
            values.package_packout_time
          ),
          service_type: resolveServiceTypeForSubmit(
            values,
            templates,
            skipServiceTypeSelection
          ),
          commodity: String(values.commodity ?? '').trim(),
          commodity_temperature:
            values.commodity_temperature &&
            String(values.commodity_temperature).trim()
              ? String(values.commodity_temperature).trim()
              : null,
          packaging:
            values.packaging && String(values.packaging).trim()
              ? String(values.packaging).trim()
              : null,
          shipment_reference: values.shipment_reference ?? null,
          emergency_contact_number: values.emergency_contact_number ?? null,
          email_notification_preferences: emailNotificationPreferencesForApi(
            values.email_notification_preferences
          ),
        };

        CreateShipmentBooking(token, payload)
          .then((apiRes) => {
            const res = apiRes as ApiResponse<{ id?: string }>;
            const newBookingId =
              (res?.data?.data?.data?.id as string | undefined) ?? undefined;
            if (!newBookingId) {
              message.error(
                'Booking created, but could not find booking id in response.'
              );
              return;
            }

            setSummaryIsWorldCourier(false);
            setSummaryIsCryoport(false);
            setBookingId(newBookingId);
            setSummaryOpen(true);
          })
          .catch(() => {
            message.error(
              'Failed to create shipment booking. Please try again.'
            );
          })
          .finally(() => {
            setPrepareSubmitting(false);
          });
      })
      .catch((err: unknown) => {
        warnFirstFormValidationError(form, err);
        setPrepareSubmitting(false);
      });
  };

  const bookTrackShipmentDisabled =
    !bookingId ||
    confirmationLoading ||
    !confirmationImageLoaded ||
    confirmSubmitting;

  /** WC summary primary: wait for House Waybill poll and avoid double-submit while PATCH runs. */
  const wcSummaryBookTrackDisabled = wcHouseWaybillLoading || confirmSubmitting;

  const cryoSummaryBookTrackDisabled =
    cryoPrepareLoading ||
    !cryoSummaryReady ||
    confirmSubmitting ||
    (!!cryoReviewScreenshotUrl && !cryoReviewImageLoaded);

  return (
    <Form
      form={form}
      layout='vertical'
      scrollToFirstError
      initialValues={{
        email_notification_preferences: emptyEmailNotificationPreferences(),
        wc_specific_pickup_enabled: false,
        wc_specific_delivery_enabled: false,
        wc_origin_all_email_notifications: true,
        wc_dest_all_email_notifications: true,
        wc_packages: [
          {
            items: [{ contains_dangerous_goods: false }],
            extra_supplies: [],
          },
        ],
        cryo_commodities: [
          {
            quantity: 1,
            price_currency: 'USD',
            country_of_origin: 'US',
            leg_2: true,
          },
        ],
        cryo_accessories: [],
      }}
      onValuesChange={(changed, allValues) => {
        if (!isWorldCourier) return;
        onWorldCourierValuesChange(changed, allValues);
      }}
    >
      <div className={styles.padBottom40}>
        <Modal
          title='Summary information'
          open={summaryOpen}
          onCancel={handleCancelBooking}
          closable={!confirmSubmitting}
          maskClosable={false}
          footer={
            <Flex justify='flex-end' align='center' gap={16}>
              <Button
                type='text'
                loading={confirmSubmitting && pendingFinalAction === 'CANCEL'}
                disabled={confirmSubmitting}
                onClick={handleCancelBooking}
                style={{
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  padding: '4px 8px',
                  height: 'auto',
                }}
              >
                Cancel
              </Button>
              {summaryIsCryoport ? (
                <Button
                  type='primary'
                  loading={
                    confirmSubmitting && pendingFinalAction === 'CONFIRM'
                  }
                  disabled={cryoSummaryBookTrackDisabled}
                  onClick={handleBookAndTraceShipment}
                  style={{
                    height: 40,
                    minWidth: 200,
                    paddingInline: 20,
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: 14,
                    ...(cryoSummaryBookTrackDisabled
                      ? {
                          backgroundColor: 'var(--readonly-field-bg)',
                          color: 'var(--text-tertiary)',
                          border: '1px solid var(--divider-border)',
                          boxShadow: 'none',
                          opacity: 1,
                          cursor: 'not-allowed',
                        }
                      : {
                          backgroundColor: 'var(--text-primary)',
                          color: 'var(--white)',
                          border: 'none',
                        }),
                  }}
                >
                  Book and track shipment
                </Button>
              ) : summaryIsWorldCourier ? (
                <Button
                  type='primary'
                  loading={
                    confirmSubmitting && pendingFinalAction === 'CONFIRM'
                  }
                  disabled={wcSummaryBookTrackDisabled}
                  onClick={handleBookAndTraceShipment}
                  style={{
                    height: 40,
                    minWidth: 200,
                    paddingInline: 20,
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: 14,
                    ...(wcSummaryBookTrackDisabled
                      ? {
                          backgroundColor: '#f5f5f5',
                          color: 'rgba(0, 0, 0, 0.45)',
                          border: '1px solid #d9d9d9',
                          boxShadow: 'none',
                          opacity: 1,
                          cursor: 'not-allowed',
                        }
                      : {
                          backgroundColor: 'var(--text-primary)',
                          color: 'var(--white)',
                          border: 'none',
                        }),
                  }}
                >
                  Book and track shipment
                </Button>
              ) : (
                <Button
                  type='primary'
                  loading={
                    confirmSubmitting && pendingFinalAction === 'CONFIRM'
                  }
                  disabled={bookTrackShipmentDisabled}
                  onClick={handleBookAndTraceShipment}
                  style={{
                    height: 40,
                    minWidth: 200,
                    paddingInline: 20,
                    borderRadius: 999,
                    fontWeight: 600,
                    fontSize: 14,
                    ...(bookTrackShipmentDisabled
                      ? {
                          backgroundColor: '#f5f5f5',
                          color: 'rgba(0, 0, 0, 0.45)',
                          border: '1px solid #d9d9d9',
                          boxShadow: 'none',
                          opacity: 1,
                          cursor: 'not-allowed',
                        }
                      : {
                          backgroundColor: 'var(--text-primary)',
                          color: 'var(--white)',
                          border: 'none',
                        }),
                  }}
                >
                  Book and track shipment
                </Button>
              )}
            </Flex>
          }
          width={980}
          centered
          destroyOnClose
        >
          {summaryIsCryoport && (
            <div className={styles.pad24}>
              {cryoPrepareLoading && (
                <div className={styles.padLoading}>
                  <Spin size='large' />
                  <div className={styles.mt12}>
                    <Text type='secondary'>
                      Preparing Cryoport draft… (can take several minutes)
                    </Text>
                  </div>
                </div>
              )}
              {!cryoPrepareLoading && (
                <div className={styles.mt8}>
                  <Text strong>Cryoportal order: </Text>
                  <Text>
                    {cryoPortalOrderNumber ||
                      '(number will appear when automation finishes)'}
                  </Text>
                </div>
              )}
              {!cryoPrepareLoading && cryoReviewScreenshotUrl && (
                <div className={styles.mt20}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Review summary
                  </Text>
                  <div className={styles.previewScroll}>
                    <Image
                      src={cryoReviewScreenshotUrl}
                      alt='Cryoportal review'
                      onLoad={() => setCryoReviewImageLoaded(true)}
                      onError={() => setCryoReviewImageLoaded(false)}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        display: 'block',
                      }}
                      fallback=''
                    />
                  </div>
                </div>
              )}
              {!cryoPrepareLoading &&
                (cryoPrepareError || cryoPrepareTimedOut) && (
                  <div className={styles.mt16}>
                    <Text type='danger'>
                      Draft is not ready yet or automation failed.
                    </Text>
                  </div>
                )}
            </div>
          )}

          {summaryIsWorldCourier && (
            <div className={styles.pad24}>
              {wcHouseWaybillLoading && (
                <div className={styles.padLoading}>
                  <Spin size='large' />
                  <div className={styles.mt12}>
                    <Text type='secondary'>
                      Loading House Waybill… (can take up to 30 seconds)
                    </Text>
                  </div>
                </div>
              )}
              {!wcHouseWaybillLoading && wcHouseWaybillUrl && (
                <div className={styles.mt16}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    House Waybill
                  </Text>
                  <div className={styles.waybillFrame}>
                    <iframe
                      title='House Waybill PDF'
                      src={wcHouseWaybillUrl}
                      className={styles.waybillIframe}
                    />
                  </div>
                  <div className={styles.mt8}>
                    <Link
                      href={wcHouseWaybillUrl}
                      target='_blank'
                      rel='noopener'
                    >
                      Open House Waybill in new tab
                    </Link>
                  </div>
                </div>
              )}
              {!wcHouseWaybillLoading &&
                !wcHouseWaybillUrl &&
                (wcHouseWaybillError || wcHouseWaybillTimedOut) && (
                  <div className={styles.mt16}>
                    <Text type='danger'>
                      House Waybill is not available yet or could not be loaded.
                    </Text>
                    <div className={styles.mt8}>
                      <Text type='secondary'>
                        If automation is still running, try opening this booking
                        again later from your records. You can close this dialog
                        with Book and track shipment.
                      </Text>
                    </div>
                  </div>
                )}
            </div>
          )}

          {!summaryIsWorldCourier &&
            !summaryIsCryoport &&
            confirmationLoading && (
              <div className={styles.padLoading48}>
                <Spin size='large' />
                <div className={styles.mt12}>
                  <Text type='secondary'>
                    Generating confirmation… (can take up to 30 seconds)
                  </Text>
                </div>
              </div>
            )}

          {!summaryIsWorldCourier &&
            !summaryIsCryoport &&
            !confirmationLoading &&
            confirmationUrl && (
              <div className={styles.previewScrollCenter}>
                <Image
                  src={confirmationUrl}
                  alt='Quickstat confirmation'
                  onLoad={() => setConfirmationImageLoaded(true)}
                  onError={() => setConfirmationImageLoaded(false)}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'inline-block',
                    objectFit: 'contain',
                  }}
                  fallback=''
                />
                {quickstatPortalOrderNumber ? (
                  <div className={styles.mt16}>
                    <Text strong>QuickOnline order number: </Text>
                    <Text>{quickstatPortalOrderNumber}</Text>
                  </div>
                ) : null}
              </div>
            )}

          {!summaryIsWorldCourier &&
            !summaryIsCryoport &&
            !confirmationLoading &&
            !confirmationUrl &&
            (confirmationError || confirmationTimedOut) && (
              <div className={styles.pad24}>
                <Text type='danger'>Submission preparation failed.</Text>
                <div className={styles.mt8}>
                  <Text type='secondary'>
                    Cancel this dialog and resubmit your shipment.
                  </Text>
                </div>
              </div>
            )}
        </Modal>

        {quickstatCompletionOpen && bookingId && token ? (
          <QuickstatBookingCompletedModal
            key={bookingId}
            open={true}
            onClose={() => {
              setQuickstatCompletionOpen(false);
              onClose();
            }}
            bookingId={bookingId}
            token={token}
            companyName={decodedToken?.company_name ?? ''}
          />
        ) : null}

        <SpecialtyBookingCompletedModal
          open={specialtyCompletionOpen}
          trackingNumber={specialtyTrackingNumber}
          providerTitle={specialtyCompletionProviderTitle}
          onClose={() => {
            setSpecialtyCompletionOpen(false);
            setSpecialtyCompletionProviderTitle(null);
            onClose();
          }}
        />

        {wcCompletionOpen && bookingId && token ? (
          <WorldCourierBookingCompletedModal
            key={bookingId}
            open={true}
            onClose={() => {
              setWcCompletionOpen(false);
              onClose();
            }}
            bookingId={bookingId}
            token={token}
            shellShipmentId={shellShipment?.id}
            courierId={wcPromoteCourierId}
            onPromoteSuccess={onPromoteSuccess}
          />
        ) : null}

        {cryoCompletionOpen && bookingId && token ? (
          <CryoportBookingCompletedModal
            key={bookingId}
            open={true}
            onClose={() => {
              setCryoCompletionOpen(false);
              onClose();
            }}
            bookingId={bookingId}
            token={token}
            shellShipmentId={shellShipment?.id}
            courierId={cryoPromoteCourierId}
            onPromoteSuccess={onPromoteSuccess}
          />
        ) : null}

        <CustomDeleteModal
          visible={wcArchiveModalVisible}
          onClose={closeWcArchiveModal}
          onConfirm={() => {
            const _ = confirmWcArchiveTemplate();
          }}
          title='Archive'
          message="You're about to archive 1 company."
          cancelText='Cancel'
          confirmText='Archive'
          confirmColor='var(--danger)'
        />

        <CustomDeleteModal
          visible={cryoLocationArchiveModalVisible}
          onClose={closeCryoLocationArchiveModal}
          onConfirm={() => {
            const _ = confirmCryoLocationArchive();
          }}
          title='Archive'
          message="You're about to archive 1 location."
          cancelText='Cancel'
          confirmText='Archive'
          confirmColor='var(--danger)'
        />

        <CustomTitle
          text='Book new shipment'
          fontSize='24px'
          fontWeight='700'
          lineHeight='32px'
          marginBottom={24}
        />

        {/* ── Transport Provider ── */}
        <div className={styles.fieldWrap}>
          <label
            className={styles.label}
          >{`${themeT('transportProvider', theme.themeType)}`}</label>
          <Form.Item name='transport_provider' noStyle>
            <CustomSelect
              placeholder='Select'
              width='100%'
              height='36px'
              options={courierOptions}
            />
          </Form.Item>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {isSpecialty ? (
          <SpecialtyBookingFields
            provider={specialtyProviderKey}
            options={specialtyOptions}
            optionsLoading={specialtyOptionsLoading}
            requiresPieces={specialtyRequiresPieces}
            dateFormat={bookingDatePickerFormat}
            zoomlogiFields={
              showSpecialtyZoomlogiFields ? (
                <SpecialtyZoomlogiFields
                  token={token ?? ''}
                  form={form}
                  teamLabel={themeT('team', theme.themeType)}
                  showTeams={specialtyHasValidTeams}
                  allTeams={specialtyAllTeams}
                  selectedTeams={specialtySelectedTeams}
                  onTeamsChange={setSpecialtySelectedTeams}
                  teamsSearch={specialtyTeamsSearch}
                  onTeamsSearch={setSpecialtyTeamsSearch}
                  selectedProducts={specialtySelectedProducts}
                  onProductsChange={setSpecialtySelectedProducts}
                  getProductName={getSpecialtyProductName}
                  mergeIntoLookup={mergeSpecialtyProductLookup}
                  paginatedProducts={specialtyPaginatedProducts}
                  productCatalogTotalCount={specialtyProductCatalogTotalCount}
                  productDropdownLoading={specialtyProductDropdownLoading}
                  productSearchInputKey={specialtyProductSearchInputKey}
                  productSearch={specialtyProductSearch}
                  setProductSearch={setSpecialtyProductSearch}
                  handleProductPopupScroll={handleSpecialtyProductPopupScroll}
                  updateProductQuantity={updateSpecialtyProductQuantity}
                  removeProduct={removeSpecialtyProduct}
                />
              ) : undefined
            }
          />
        ) : isWorldCourier ? (
          <>
            <div className={styles.fieldWrap}>
              <WcLabel text='Project' required />
              <Form.Item
                name='wc_project'
                rules={[{ required: true, message: 'Select a project' }]}
                noStyle
              >
                <CustomSelect
                  placeholder={
                    wcProjectOptionsFetchSettled
                      ? 'Select a project…'
                      : 'Loading projects…'
                  }
                  width='100%'
                  height='36px'
                  disabled={!wcProjectOptionsFetchSettled}
                  options={wcProjectSelectOptions}
                  onChange={handleWcProjectChange}
                />
              </Form.Item>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <CustomTitle
              text='Pickup'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Company Name <span className={styles.required}>*</span>
              </label>
              {wcTemplatesSorted.length > 0 ? (
                <Form.Item
                  name='wc_origin_company_name'
                  rules={[{ required: true, message: 'Enter company name' }]}
                  noStyle
                  normalize={wcCompanyNameAutocompleteNormalize}
                  getValueFromEvent={wcCompanyNameAutocompleteGetValueFromEvent}
                >
                  <AutoComplete
                    allowClear
                    options={wcOriginCompanyAutocompleteOptions}
                    popupClassName={WC_LONG_OPTION_SELECT_POPUP_CLASS}
                    style={{ width: '100%' }}
                    filterOption={(inputValue, option) => {
                      const o = option as {
                        value?: string;
                        wcFilterLabel?: string;
                      };
                      if (o?.value === WC_ENTER_NEW_ADDRESS_SENTINEL)
                        return true;
                      const q = (inputValue ?? '').toLowerCase().trim();
                      if (!q) return true;
                      const label = o?.wcFilterLabel ?? '';
                      return label.toLowerCase().includes(q);
                    }}
                    placeholder='Select a saved profile or enter company name'
                    onClear={() => resetWcOriginFromTemplateSelect()}
                    onSelect={(selected) => {
                      const value = selected as string;
                      if (value === WC_ENTER_NEW_ADDRESS_SENTINEL) {
                        resetWcOriginFromTemplateSelect();
                        queueMicrotask(() => {
                          form.setFieldValue(
                            'wc_origin_company_name',
                            undefined
                          );
                        });
                        return;
                      }
                      if (value.startsWith(WC_TEMPLATE_AUTOCOMPLETE_PREFIX)) {
                        applyWcTemplateToOrigin(
                          value.slice(WC_TEMPLATE_AUTOCOMPLETE_PREFIX.length)
                        );
                      }
                    }}
                    onChange={(val: string | undefined) => {
                      if (
                        val === WC_ENTER_NEW_ADDRESS_SENTINEL ||
                        (typeof val === 'string' &&
                          val.trim() === WC_ENTER_NEW_ADDRESS_SENTINEL)
                      ) {
                        return;
                      }
                      onWcOriginCompanyNameChange(val);
                    }}
                  >
                    <Input
                      style={wcCompanyAutoCompleteInputStyle}
                      onBlur={onWcOriginCompanyNameBlur}
                    />
                  </AutoComplete>
                </Form.Item>
              ) : (
                <Form.Item
                  name='wc_origin_company_name'
                  rules={[{ required: true, message: 'Enter company name' }]}
                  noStyle
                >
                  <CustomInput
                    placeholder='Enter company name'
                    height='36px'
                    onChange={(e) =>
                      onWcOriginCompanyNameChange(e.target.value)
                    }
                    onBlur={onWcOriginCompanyNameBlur}
                  />
                </Form.Item>
              )}
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Contact Name <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='wc_origin_contact_name'
                rules={[{ required: true, message: 'Enter contact name' }]}
                noStyle
              >
                <CustomInput placeholder='Enter contact name' height='36px' />
              </Form.Item>
            </div>

            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className={styles.label}>
                  Phone Number <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='wc_origin_phone_number'
                  rules={[{ required: true, message: 'Enter phone number' }]}
                  noStyle
                >
                  <CustomInput placeholder='Enter phone number' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label className={styles.label}>Extension</label>
                <Form.Item name='wc_origin_extension' noStyle>
                  <CustomInput placeholder='Optional' height='36px' />
                </Form.Item>
              </Col>
            </Row>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Email Address <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='wc_origin_email'
                rules={[
                  { required: true, message: 'Enter email' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
                noStyle
              >
                <CustomInput placeholder='Enter email' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Address Line 1 <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='wc_origin_address1'
                rules={[{ required: true, message: 'Enter address line 1' }]}
                noStyle
              >
                <CustomInput placeholder='Enter address line 1' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Address Line 2</label>
              <Form.Item name='wc_origin_address2' noStyle>
                <CustomInput placeholder='Optional' height='36px' />
              </Form.Item>
            </div>

            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className={styles.label}>
                  City <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='wc_origin_city'
                  rules={[{ required: true, message: 'Enter city' }]}
                  noStyle
                >
                  <CustomInput placeholder='Enter city' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label className={styles.label}>
                  State/Province <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='wc_origin_state_province'
                  rules={[{ required: true, message: 'Enter state/province' }]}
                  noStyle
                >
                  {wcOriginCountry === 'USA' ? (
                    <CustomSelect
                      placeholder='Select'
                      width='100%'
                      height='36px'
                      options={US_STATE_OPTIONS}
                      showSearch
                      optionFilterProp='label'
                    />
                  ) : (
                    <CustomInput
                      placeholder='Enter state/province'
                      height='36px'
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className={styles.label}>
                  Postal Code <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='wc_origin_postal_code'
                  rules={[{ required: true, message: 'Enter postal code' }]}
                  noStyle
                >
                  <CustomInput placeholder='Enter postal code' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label className={styles.label}>
                  Country <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='wc_origin_country'
                  rules={[{ required: true, message: 'Select country' }]}
                  noStyle
                >
                  <CustomSelect
                    placeholder='Select'
                    width='100%'
                    height='36px'
                    options={COUNTRY_OPTIONS}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Pick-Up Instructions</label>
              <Form.Item name='wc_pickup_instructions' noStyle>
                <TextArea
                  rows={4}
                  style={{
                    borderRadius: 8,
                    backgroundColor: 'var(--light-gray)',
                    border: 'none',
                    resize: 'none',
                  }}
                />
              </Form.Item>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <CustomTitle
              text='Pick Up Details'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Earliest Pick-Up Date/Time{' '}
                <span className={styles.required}>*</span>
              </label>
              <Flex gap={8}>
                <Form.Item
                  name='wc_earliest_pickup_date'
                  rules={[{ required: true, message: 'Required' }]}
                  noStyle
                >
                  <CustomDatePicker dateFormat={bookingDatePickerFormat} />
                </Form.Item>
                <Form.Item
                  name='wc_earliest_pickup_time'
                  rules={[{ required: true, message: 'Select time' }]}
                  noStyle
                >
                  <CustomTimePicker minuteStep={15} />
                </Form.Item>
              </Flex>
            </div>

            {!wcSpecificPickupEnabled ? (
              <div className={styles.fieldWrap}>
                <label className={styles.label}>
                  Latest Pick-Up Date/Time{' '}
                  <span className={styles.required}>*</span>
                </label>
                <Flex gap={8}>
                  <Form.Item
                    name='wc_latest_pickup_date'
                    rules={[{ required: true, message: 'Required' }]}
                    noStyle
                  >
                    <CustomDatePicker dateFormat={bookingDatePickerFormat} />
                  </Form.Item>
                  <Form.Item
                    name='wc_latest_pickup_time'
                    dependencies={[...WC_PICKUP_SCHEDULE_ORDER_DEPS]}
                    rules={[
                      { required: true, message: 'Select time' },
                      wcPickupWindowOrderRule(),
                    ]}
                    noStyle
                  >
                    <CustomTimePicker minuteStep={15} />
                  </Form.Item>
                </Flex>
              </div>
            ) : null}

            <div className={styles.fieldWrapMt8}>
              <Form.Item
                name='wc_specific_pickup_enabled'
                valuePropName='checked'
                noStyle
              >
                <Checkbox>Specific Pick-up Date/Time</Checkbox>
              </Form.Item>
            </div>

            {/* When specific is enabled, we reuse wc_earliest_pickup_* as the single required datetime. */}

            <Divider style={{ margin: '16px 0' }} />

            <CustomTitle
              text='Origin Email Notification'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <Flex align='center' justify='space-between' gap={12}>
                <Text style={{ fontSize: 14 }}>All notifications</Text>
                <Form.Item
                  name='wc_origin_all_email_notifications'
                  valuePropName='checked'
                  noStyle
                >
                  <Switch />
                </Form.Item>
              </Flex>
              <div className={styles.mt8}>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  Email notifications that the origin will receive about this
                  order&apos;s progress
                </Text>
              </div>
            </div>

            <Divider style={{ margin: '24px 0 16px' }} />

            <CustomTitle
              text='Delivery'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Company Name <span className={styles.required}>*</span>
              </label>
              {wcTemplatesSorted.length > 0 ? (
                <Form.Item
                  name='wc_dest_company_name'
                  rules={[{ required: true, message: 'Enter company name' }]}
                  noStyle
                  normalize={wcCompanyNameAutocompleteNormalize}
                  getValueFromEvent={wcCompanyNameAutocompleteGetValueFromEvent}
                >
                  <AutoComplete
                    allowClear
                    options={wcDestCompanyAutocompleteOptions}
                    popupClassName={WC_LONG_OPTION_SELECT_POPUP_CLASS}
                    style={{ width: '100%' }}
                    filterOption={(inputValue, option) => {
                      const o = option as {
                        value?: string;
                        wcFilterLabel?: string;
                      };
                      if (o?.value === WC_ENTER_NEW_ADDRESS_SENTINEL)
                        return true;
                      const q = (inputValue ?? '').toLowerCase().trim();
                      if (!q) return true;
                      const label = o?.wcFilterLabel ?? '';
                      return label.toLowerCase().includes(q);
                    }}
                    placeholder='Select a saved profile or enter company name'
                    onClear={() => resetWcDestFromTemplateSelect()}
                    onSelect={(selected) => {
                      const value = selected as string;
                      if (value === WC_ENTER_NEW_ADDRESS_SENTINEL) {
                        resetWcDestFromTemplateSelect();
                        queueMicrotask(() => {
                          form.setFieldValue('wc_dest_company_name', undefined);
                        });
                        return;
                      }
                      if (value.startsWith(WC_TEMPLATE_AUTOCOMPLETE_PREFIX)) {
                        applyWcTemplateToDestination(
                          value.slice(WC_TEMPLATE_AUTOCOMPLETE_PREFIX.length)
                        );
                      }
                    }}
                    onChange={(val: string | undefined) => {
                      if (
                        val === WC_ENTER_NEW_ADDRESS_SENTINEL ||
                        (typeof val === 'string' &&
                          val.trim() === WC_ENTER_NEW_ADDRESS_SENTINEL)
                      ) {
                        return;
                      }
                      onWcDestCompanyNameChange(val);
                    }}
                  >
                    <Input
                      style={wcCompanyAutoCompleteInputStyle}
                      onBlur={onWcDestCompanyNameBlur}
                    />
                  </AutoComplete>
                </Form.Item>
              ) : (
                <Form.Item
                  name='wc_dest_company_name'
                  rules={[{ required: true, message: 'Enter company name' }]}
                  noStyle
                >
                  <CustomInput
                    placeholder='Enter company name'
                    height='36px'
                    onChange={(e) => onWcDestCompanyNameChange(e.target.value)}
                    onBlur={onWcDestCompanyNameBlur}
                  />
                </Form.Item>
              )}
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Contact Name <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='wc_dest_contact_name'
                rules={[{ required: true, message: 'Enter contact name' }]}
                noStyle
              >
                <CustomInput placeholder='Enter contact name' height='36px' />
              </Form.Item>
            </div>

            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className={styles.label}>
                  Phone Number <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='wc_dest_phone_number'
                  rules={[{ required: true, message: 'Enter phone number' }]}
                  noStyle
                >
                  <CustomInput placeholder='Enter phone number' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label className={styles.label}>Extension</label>
                <Form.Item name='wc_dest_extension' noStyle>
                  <CustomInput placeholder='Optional' height='36px' />
                </Form.Item>
              </Col>
            </Row>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Email Address <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='wc_dest_email'
                rules={[
                  { required: true, message: 'Enter email' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
                noStyle
              >
                <CustomInput placeholder='Enter email' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Address Line 1 <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='wc_dest_address1'
                rules={[{ required: true, message: 'Enter address line 1' }]}
                noStyle
              >
                <CustomInput placeholder='Enter address line 1' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Address Line 2</label>
              <Form.Item name='wc_dest_address2' noStyle>
                <CustomInput placeholder='Optional' height='36px' />
              </Form.Item>
            </div>

            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className={styles.label}>
                  City <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='wc_dest_city'
                  rules={[{ required: true, message: 'Enter city' }]}
                  noStyle
                >
                  <CustomInput placeholder='Enter city' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label className={styles.label}>
                  State/Province <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='wc_dest_state_province'
                  rules={[{ required: true, message: 'Enter state/province' }]}
                  noStyle
                >
                  {wcDestCountry === 'USA' ? (
                    <CustomSelect
                      placeholder='Select'
                      width='100%'
                      height='36px'
                      options={US_STATE_OPTIONS}
                      showSearch
                      optionFilterProp='label'
                    />
                  ) : (
                    <CustomInput
                      placeholder='Enter state/province'
                      height='36px'
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className={styles.label}>
                  Postal Code <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='wc_dest_postal_code'
                  rules={[{ required: true, message: 'Enter postal code' }]}
                  noStyle
                >
                  <CustomInput placeholder='Enter postal code' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label className={styles.label}>
                  Country <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='wc_dest_country'
                  rules={[{ required: true, message: 'Select country' }]}
                  noStyle
                >
                  <CustomSelect
                    placeholder='Select'
                    width='100%'
                    height='36px'
                    options={COUNTRY_OPTIONS}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Delivery Instructions</label>
              <Form.Item name='wc_delivery_instructions' noStyle>
                <TextArea
                  rows={4}
                  style={{
                    borderRadius: 8,
                    backgroundColor: 'var(--light-gray)',
                    border: 'none',
                    resize: 'none',
                  }}
                />
              </Form.Item>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <CustomTitle
              text='Delivery Details'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Earliest Delivery Date/Time{' '}
                <span className={styles.required}>*</span>
              </label>
              <Flex gap={8}>
                <Form.Item
                  name='wc_earliest_delivery_date'
                  rules={[{ required: true, message: 'Required' }]}
                  noStyle
                >
                  <CustomDatePicker dateFormat={bookingDatePickerFormat} />
                </Form.Item>
                <Form.Item
                  name='wc_earliest_delivery_time'
                  rules={[{ required: true, message: 'Select time' }]}
                  noStyle
                >
                  <CustomTimePicker minuteStep={15} />
                </Form.Item>
              </Flex>
            </div>

            {!wcSpecificDeliveryEnabled ? (
              <div className={styles.fieldWrap}>
                <label className={styles.label}>
                  Latest Delivery Date/Time{' '}
                  <span className={styles.required}>*</span>
                </label>
                <Flex gap={8}>
                  <Form.Item
                    name='wc_latest_delivery_date'
                    rules={[{ required: true, message: 'Required' }]}
                    noStyle
                  >
                    <CustomDatePicker dateFormat={bookingDatePickerFormat} />
                  </Form.Item>
                  <Form.Item
                    name='wc_latest_delivery_time'
                    dependencies={[...WC_DELIVERY_SCHEDULE_ORDER_DEPS]}
                    rules={[
                      { required: true, message: 'Select time' },
                      wcDeliveryWindowOrderRule(),
                    ]}
                    noStyle
                  >
                    <CustomTimePicker minuteStep={15} />
                  </Form.Item>
                </Flex>
              </div>
            ) : null}

            <div className={styles.fieldWrapMt8}>
              <Form.Item
                name='wc_specific_delivery_enabled'
                valuePropName='checked'
                noStyle
              >
                <Checkbox>Specific Delivery Date/Time</Checkbox>
              </Form.Item>
            </div>

            {/* When specific is enabled, we reuse wc_earliest_delivery_* as the single required datetime. */}

            <Divider style={{ margin: '16px 0' }} />

            <CustomTitle
              text='Destination Email Notifications'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <Flex align='center' justify='space-between' gap={12}>
                <Text style={{ fontSize: 14 }}>All notifications</Text>
                <Form.Item
                  name='wc_dest_all_email_notifications'
                  valuePropName='checked'
                  noStyle
                >
                  <Switch />
                </Form.Item>
              </Flex>
              <div className={styles.mt8}>
                <Text type='secondary' style={{ fontSize: 12 }}>
                  Email notifications that this destination will receive about
                  this order&apos;s progress
                </Text>
              </div>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <CustomTitle
              text='Shipment Details'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <Form.List name='wc_packages'>
              {(pkgFields, { add: addPackage, remove: removePackage }) => (
                <>
                  <CollapseWithAutoOpenNewPanels
                    ids={pkgFields.map((p) => String(p.name))}
                    className={WC_BOOKING_COLLAPSE_CLASS}
                    style={{ marginBottom: 16 }}
                  >
                    {pkgFields.map((pkgField, pkgIdx) => (
                      <Panel
                        key={String(pkgField.name)}
                        header={
                          <Text
                            strong
                            style={{
                              fontSize: 15,
                              lineHeight: '22px',
                              margin: 0,
                            }}
                          >
                            {`Package ${pkgIdx + 1}`}
                          </Text>
                        }
                        extra={
                          pkgFields.length > 1 ? (
                            <Button
                              type='text'
                              className='wc-booking-collapse-remove'
                              onClick={(e) => {
                                e.stopPropagation();
                                removePackage(pkgField.name);
                              }}
                            >
                              Remove
                            </Button>
                          ) : null
                        }
                      >
                        <Row
                          gutter={12}
                          align='top'
                          style={{ marginBottom: 16 }}
                        >
                          <Col span={12}>
                            <WcLabel
                              text='Temperature Range / Refrigerant'
                              required
                            />
                            <Form.Item
                              name={[
                                pkgField.name,
                                'temperature_range_refrigerant',
                              ]}
                              rules={[
                                {
                                  required: true,
                                  message:
                                    'Select temperature range / refrigerant',
                                },
                              ]}
                              noStyle
                            >
                              <CustomSelect
                                placeholder='Select'
                                width='100%'
                                height='36px'
                                options={
                                  worldCourierTemperatureRangeSelectOptions
                                }
                                showSearch
                                optionFilterProp='label'
                                popupClassName={
                                  WC_LONG_OPTION_SELECT_POPUP_CLASS
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <WcLabel text='Number of External Boxes' required />
                            <Form.Item
                              name={[pkgField.name, 'external_boxes_count']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Enter number of boxes',
                                },
                              ]}
                              noStyle
                            >
                              <CustomInput
                                placeholder='Enter number'
                                height='36px'
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <div className={styles.fieldWrap}>
                          <label className={styles.label}>
                            Package Name{' '}
                            <span className={styles.required}>*</span>
                          </label>
                          <Form.Item
                            name={[pkgField.name, 'package_name']}
                            rules={[
                              {
                                required: true,
                                message: 'Select package name',
                              },
                            ]}
                            noStyle
                          >
                            <WcPackageNameSelect
                              form={form}
                              pkgFieldName={pkgField.name}
                              popupClassName={WC_LONG_OPTION_SELECT_POPUP_CLASS}
                            />
                          </Form.Item>
                        </div>

                        <Form.Item
                          noStyle
                          dependencies={[
                            [
                              'wc_packages',
                              pkgField.name,
                              'temperature_range_refrigerant',
                            ],
                            ['wc_packages', pkgField.name, 'package_name'],
                          ]}
                        >
                          {() => {
                            const temp = form.getFieldValue([
                              'wc_packages',
                              pkgField.name,
                              'temperature_range_refrigerant',
                            ]);
                            const pkgName = form.getFieldValue([
                              'wc_packages',
                              pkgField.name,
                              'package_name',
                            ]);
                            if (
                              !WC_TEMPERATURES_REQUIRING_DRY_ICE.has(
                                String(temp ?? '')
                              )
                            ) {
                              return null;
                            }
                            const dryIceBundledPackage =
                              wcPackageHasBundledDryIce(String(pkgName ?? ''));
                            return (
                              <Row gutter={12} style={{ marginBottom: 16 }}>
                                <Col span={dryIceBundledPackage ? 24 : 12}>
                                  <WcLabel
                                    text='Select Dry Ice Supply'
                                    required
                                  />
                                  <Form.Item
                                    name={[pkgField.name, 'dry_ice_supply']}
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Select dry ice supply',
                                      },
                                    ]}
                                    noStyle
                                  >
                                    <CustomSelect
                                      placeholder='Select'
                                      width='100%'
                                      height='36px'
                                      options={
                                        worldCourierDryIceSupplySelectOptions
                                      }
                                      showSearch={!dryIceBundledPackage}
                                      optionFilterProp='label'
                                      popupClassName={
                                        WC_LONG_OPTION_SELECT_POPUP_CLASS
                                      }
                                      disabled={dryIceBundledPackage}
                                      allowClear={!dryIceBundledPackage}
                                    />
                                  </Form.Item>
                                </Col>
                                {!dryIceBundledPackage ? (
                                  <Col span={12}>
                                    <WcLabel
                                      text='Dry Ice Weight (Whole Number)'
                                      required
                                    />
                                    <Form.Item
                                      name={[
                                        pkgField.name,
                                        'dry_ice_weight_lbs',
                                      ]}
                                      rules={[
                                        {
                                          required: true,
                                          message: 'Enter dry ice weight',
                                        },
                                        {
                                          validator: async (_, value) => {
                                            const s = String(
                                              value ?? ''
                                            ).trim();
                                            if (!s) {
                                              return Promise.reject(
                                                new Error(
                                                  'Enter dry ice weight'
                                                )
                                              );
                                            }
                                            if (!/^\d+$/.test(s)) {
                                              return Promise.reject(
                                                new Error(
                                                  'Use a whole number (lbs)'
                                                )
                                              );
                                            }
                                            const n = Number(s);
                                            if (n < 1 || n > 99999999) {
                                              return Promise.reject(
                                                new Error(
                                                  'Weight must be between 1 and 99999999 lbs'
                                                )
                                              );
                                            }
                                            return Promise.resolve();
                                          },
                                        },
                                      ]}
                                      noStyle
                                    >
                                      <CustomInput
                                        placeholder='Weight (lbs)'
                                        height='36px'
                                      />
                                    </Form.Item>
                                  </Col>
                                ) : null}
                              </Row>
                            );
                          }}
                        </Form.Item>

                        <Row gutter={12}>
                          <Col span={24}>
                            <WcExtraSuppliesLikeProducts
                              form={form}
                              pkgFieldName={pkgField.name}
                              popupClassName={WC_LONG_OPTION_SELECT_POPUP_CLASS}
                            />
                          </Col>
                        </Row>

                        <div className={styles.fieldWrap}>
                          <label className={styles.label}>
                            Send Temperature Readings To
                          </label>
                          <Form.Item
                            name={[
                              pkgField.name,
                              'send_temperature_readings_to',
                            ]}
                            noStyle
                          >
                            <CustomInput
                              placeholder='Enter email'
                              height='36px'
                            />
                          </Form.Item>
                        </div>

                        <div className={styles.fieldWrap}>
                          <label className={styles.label}>
                            Instructions for Temperature Probe Upon Arrival
                          </label>
                          <Form.Item
                            name={[
                              pkgField.name,
                              'temperature_probe_instructions',
                            ]}
                            noStyle
                          >
                            <TextArea
                              rows={4}
                              style={{
                                borderRadius: 8,
                                backgroundColor: 'var(--light-gray)',
                                border: 'none',
                                resize: 'none',
                              }}
                            />
                          </Form.Item>
                        </div>

                        <Divider style={{ margin: '16px 0' }} />

                        <CustomTitle
                          text='Items'
                          fontSize='18px'
                          fontWeight='700'
                          lineHeight='24px'
                          marginBottom={16}
                        />

                        <Form.List name={[pkgField.name, 'items']}>
                          {(fields, { add, remove }) => (
                            <>
                              <CollapseWithAutoOpenNewPanels
                                ids={fields.map((f) => String(f.name))}
                                className={WC_BOOKING_COLLAPSE_NESTED_CLASS}
                                style={{ marginBottom: 16 }}
                              >
                                {fields.map((field, idx) => (
                                  <Panel
                                    key={String(field.name)}
                                    header={
                                      <Text
                                        strong
                                        style={{
                                          fontSize: 14,
                                          lineHeight: '22px',
                                          margin: 0,
                                        }}
                                      >
                                        {`Item ${idx + 1}`}
                                      </Text>
                                    }
                                    extra={
                                      fields.length > 1 ? (
                                        <Button
                                          type='text'
                                          className='wc-booking-collapse-remove'
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            remove(field.name);
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      ) : null
                                    }
                                  >
                                    <Row
                                      gutter={12}
                                      style={{ marginBottom: 16 }}
                                    >
                                      <Col span={16}>
                                        <label className={styles.label}>
                                          Item Name{' '}
                                          <span className={styles.required}>
                                            *
                                          </span>
                                        </label>
                                        <Form.Item
                                          name={[field.name, 'item_name']}
                                          rules={[
                                            {
                                              required: true,
                                              message: 'Please select an item',
                                            },
                                          ]}
                                          noStyle
                                        >
                                          <CustomSelect
                                            placeholder='Search item name'
                                            width='100%'
                                            height='36px'
                                            options={
                                              worldCourierItemNameSelectOptions
                                            }
                                            showSearch
                                            optionFilterProp='label'
                                            popupClassName={
                                              WC_LONG_OPTION_SELECT_POPUP_CLASS
                                            }
                                          />
                                        </Form.Item>
                                      </Col>
                                      <Col span={8}>
                                        <label className={styles.label}>
                                          Quantity{' '}
                                          <span className={styles.required}>
                                            *
                                          </span>
                                        </label>
                                        <Form.Item
                                          name={[field.name, 'quantity']}
                                          rules={[
                                            {
                                              required: true,
                                              message: 'Enter quantity',
                                            },
                                          ]}
                                          noStyle
                                        >
                                          <CustomInput
                                            placeholder='0'
                                            height='36px'
                                          />
                                        </Form.Item>
                                      </Col>
                                    </Row>

                                    <div className={styles.fieldWrap}>
                                      <label className={styles.label}>
                                        Description
                                      </label>
                                      <Form.Item
                                        name={[field.name, 'description']}
                                        noStyle
                                      >
                                        <TextArea
                                          rows={3}
                                          style={{
                                            borderRadius: 8,
                                            backgroundColor:
                                              'var(--light-gray)',
                                            border: 'none',
                                            resize: 'none',
                                          }}
                                        />
                                      </Form.Item>
                                    </div>

                                    <Row
                                      gutter={12}
                                      style={{ marginBottom: 16 }}
                                    >
                                      <Col span={8}>
                                        <label className={styles.label}>
                                          Weight{' '}
                                          <span className={styles.required}>
                                            *
                                          </span>
                                        </label>
                                        <Form.Item
                                          name={[field.name, 'item_weight_lbs']}
                                          rules={[
                                            {
                                              required: true,
                                              message: 'Required',
                                            },
                                          ]}
                                          noStyle
                                        >
                                          <CustomInput
                                            placeholder='lbs'
                                            height='36px'
                                          />
                                        </Form.Item>
                                      </Col>
                                      <Col span={16}>
                                        <label className={styles.label}>
                                          Temperature Range / Refrigerant
                                        </label>
                                        <Form.Item
                                          name={[
                                            field.name,
                                            'temperature_range_refrigerant',
                                          ]}
                                          noStyle
                                          hidden
                                        >
                                          <Input type='hidden' />
                                        </Form.Item>
                                        <Form.Item
                                          noStyle
                                          shouldUpdate={(prev, cur) =>
                                            prev?.wc_packages?.[
                                              pkgField.name as number
                                            ]?.temperature_range_refrigerant !==
                                            cur?.wc_packages?.[
                                              pkgField.name as number
                                            ]?.temperature_range_refrigerant
                                          }
                                        >
                                          {() => {
                                            const pkgTemp = form.getFieldValue([
                                              'wc_packages',
                                              pkgField.name,
                                              'temperature_range_refrigerant',
                                            ]) as string | undefined;
                                            const displayLabel =
                                              worldCourierTemperatureRangeSelectOptions.find(
                                                (o) => o.value === pkgTemp
                                              )?.label ??
                                              (pkgTemp ? String(pkgTemp) : '');
                                            return (
                                              <CustomInput
                                                readOnly
                                                tabIndex={-1}
                                                placeholder='Set on package above'
                                                width='100%'
                                                height='36px'
                                                value={
                                                  displayLabel || undefined
                                                }
                                              />
                                            );
                                          }}
                                        </Form.Item>
                                      </Col>
                                    </Row>

                                    <Row
                                      gutter={12}
                                      style={{ marginBottom: 16 }}
                                    >
                                      <Col span={12}>
                                        <label className={styles.label}>
                                          Harmonized Tariff Code
                                        </label>
                                        <Form.Item
                                          name={[
                                            field.name,
                                            'harmonized_tariff_code',
                                          ]}
                                          noStyle
                                        >
                                          <CustomInput
                                            placeholder='HTS'
                                            height='36px'
                                          />
                                        </Form.Item>
                                      </Col>
                                      <Col span={12}>
                                        <label className={styles.label}>
                                          Declared Value for Customs
                                        </label>
                                        <Form.Item
                                          name={[
                                            field.name,
                                            'declared_value_usd',
                                          ]}
                                          noStyle
                                        >
                                          <CustomInput
                                            placeholder='USD'
                                            height='36px'
                                          />
                                        </Form.Item>
                                      </Col>
                                    </Row>

                                    <div className={styles.fieldWrapFlush}>
                                      <label className={styles.label}>
                                        Does this item contain dangerous goods?
                                      </label>
                                      <Flex align='center' gap={12}>
                                        <Form.Item
                                          name={[
                                            field.name,
                                            'contains_dangerous_goods',
                                          ]}
                                          valuePropName='checked'
                                          noStyle
                                        >
                                          <Switch />
                                        </Form.Item>
                                        <Text
                                          type='secondary'
                                          style={{ fontSize: 12 }}
                                        >
                                          Toggle on if yes
                                        </Text>
                                      </Flex>
                                    </div>
                                  </Panel>
                                ))}
                              </CollapseWithAutoOpenNewPanels>

                              <Button
                                type='primary'
                                onClick={() => {
                                  const pkgTemp = form.getFieldValue([
                                    'wc_packages',
                                    pkgField.name,
                                    'temperature_range_refrigerant',
                                  ]);
                                  add({
                                    contains_dangerous_goods: false,
                                    ...(pkgTemp != null && pkgTemp !== ''
                                      ? {
                                          temperature_range_refrigerant:
                                            pkgTemp,
                                        }
                                      : {}),
                                  });
                                }}
                                style={{
                                  height: 40,
                                  paddingInline: 20,
                                  borderRadius: 999,
                                  fontWeight: 600,
                                  backgroundColor: 'var(--text-primary)',
                                  color: 'var(--white)',
                                  border: 'none',
                                  boxShadow: 'none',
                                }}
                              >
                                + Item
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </Panel>
                    ))}
                  </CollapseWithAutoOpenNewPanels>

                  <Button
                    type='primary'
                    onClick={() =>
                      addPackage({
                        items: [{ contains_dangerous_goods: false }],
                        extra_supplies: [],
                      })
                    }
                    style={{
                      height: 40,
                      paddingInline: 20,
                      borderRadius: 999,
                      fontWeight: 600,
                      backgroundColor: 'var(--text-primary)',
                      color: 'var(--white)',
                      border: 'none',
                      boxShadow: 'none',
                    }}
                  >
                    + Package
                  </Button>
                </>
              )}
            </Form.List>

            <Divider style={{ margin: '24px 0 16px' }} />

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Reference Code (optional)</label>
              <Form.Item name='wc_reference_code' noStyle>
                <CustomInput
                  placeholder='Reference code…'
                  height='36px'
                  maxLength={60}
                  showCount
                />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Charge Code (optional)</label>
              <Form.Item name='wc_charge_code' noStyle>
                <CustomInput
                  placeholder='Charge code…'
                  height='36px'
                  maxLength={50}
                  showCount
                />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Study (optional)</label>
              <Form.Item name='wc_study' noStyle>
                <CustomInput
                  placeholder='Study…'
                  height='36px'
                  maxLength={50}
                  showCount
                />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Additional Notes (optional)
              </label>
              <Form.Item name='wc_additional_notes' noStyle>
                <TextArea
                  rows={4}
                  maxLength={255}
                  showCount
                  placeholder='Please include any relevant details or requests that can assist us in processing your order efficiently.'
                  style={{
                    borderRadius: 8,
                    backgroundColor: 'var(--light-gray)',
                    border: 'none',
                    resize: 'vertical',
                  }}
                  styles={{
                    textarea: {
                      borderRadius: 8,
                      backgroundColor: 'var(--light-gray)',
                      color: 'var(--text-primary)',
                      border: 'none',
                      resize: 'vertical',
                    },
                  }}
                />
              </Form.Item>
            </div>

            <Divider style={{ margin: '24px 0 16px' }} />
          </>
        ) : isCryoport ? (
          <>
            <CustomTitle
              text='Pickup'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <CryoWcStyleSiteFormBlock
              side='origin'
              autocompleteOptions={cryoOriginAutocompleteOptions}
              onNameChange={onCryoOriginNameChange}
              onNameBlur={onCryoOriginNameBlur}
              onNameSelect={(id) => {
                applyCryoLocationToOrigin(id);
              }}
              onNameClear={resetCryoOriginFields}
            />

            <Divider style={{ margin: '16px 0' }} />

            <CustomTitle
              text='Delivery'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <CryoWcStyleSiteFormBlock
              side='dest'
              autocompleteOptions={cryoDestAutocompleteOptions}
              onNameChange={onCryoDestNameChange}
              onNameBlur={onCryoDestNameBlur}
              onNameSelect={(id) => {
                applyCryoLocationToDestination(id);
              }}
              onNameClear={resetCryoDestFields}
            />

            <Divider style={{ margin: '16px 0' }} />

            <CustomTitle
              text='Shippers & Commodities'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <CryoShipperFormBlock />

            <Divider style={{ margin: '24px 0 16px' }} />

            <CustomTitle
              text='Documentation'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <label className={styles.label}>PO number</label>
              <Form.Item name='cryo_po_number' noStyle>
                <CustomInput placeholder='Optional' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Study number</label>
              <Form.Item name='cryo_study_number' noStyle>
                <CustomInput placeholder='Optional' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Leg 2 shipment reference</label>
              <Form.Item name='cryo_leg2_shipment_reference' noStyle>
                <CustomInput placeholder='Optional' height='36px' />
              </Form.Item>
            </div>

            <Divider style={{ margin: '24px 0 16px' }} />

            <CustomTitle
              text='Schedule'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Delivery Date <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='cryo_delivery_date'
                rules={[{ required: true, message: 'Required' }]}
                noStyle
              >
                <CustomDatePicker
                  style={{ width: '100%', height: '36px' }}
                  dateFormat={bookingDatePickerFormat}
                />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Pickup Date (Leg 2) <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='cryo_pickup_date'
                rules={[{ required: true, message: 'Required' }]}
                noStyle
              >
                <CustomDatePicker
                  style={{ width: '100%', height: '36px' }}
                  dateFormat={bookingDatePickerFormat}
                />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Pickup Between (Leg 2)</label>
              <div className={styles.rowGap8Center}>
                <Form.Item
                  name='cryo_pickup_start_time'
                  initialValue='12:00'
                  noStyle
                >
                  <CustomSelect
                    options={CRYO_PICKUP_TIME_OPTIONS}
                    width='100%'
                    height='36px'
                  />
                </Form.Item>
                <span className={styles.rangeDash}>–</span>
                <Form.Item
                  name='cryo_pickup_end_time'
                  initialValue='16:00'
                  noStyle
                >
                  <CustomSelect
                    options={CRYO_PICKUP_TIME_OPTIONS}
                    width='100%'
                    height='36px'
                  />
                </Form.Item>
              </div>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Pickup Date (Leg 3) <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='cryo_leg3_pickup_date'
                rules={[{ required: true, message: 'Required' }]}
                noStyle
              >
                <CustomDatePicker
                  style={{ width: '100%', height: '36px' }}
                  dateFormat={bookingDatePickerFormat}
                />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Pickup Between (Leg 3)</label>
              <div className={styles.rowGap8Center}>
                <Form.Item
                  name='cryo_leg3_pickup_start_time'
                  initialValue='12:00'
                  noStyle
                >
                  <CustomSelect
                    options={CRYO_PICKUP_TIME_OPTIONS}
                    width='100%'
                    height='36px'
                  />
                </Form.Item>
                <span className={styles.rangeDash}>–</span>
                <Form.Item
                  name='cryo_leg3_pickup_end_time'
                  initialValue='16:00'
                  noStyle
                >
                  <CustomSelect
                    options={CRYO_PICKUP_TIME_OPTIONS}
                    width='100%'
                    height='36px'
                  />
                </Form.Item>
              </div>
            </div>

            <Divider style={{ margin: '24px 0 16px' }} />
          </>
        ) : (
          <>
            {/* ── Account & Template ── */}
            {!skipAccountSelection ? (
              <div className={styles.fieldWrap}>
                <label className={styles.label}>
                  Account selection <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='account_selection'
                  rules={[
                    { required: true, message: 'Please select an account' },
                  ]}
                  noStyle
                >
                  <CustomSelect
                    placeholder='Select'
                    width='100%'
                    height='36px'
                    options={accountOptions}
                    onChange={(val) => handleAccountChange(val as string)}
                  />
                </Form.Item>
              </div>
            ) : null}

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Template selection <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='template_selection'
                rules={[
                  { required: true, message: 'Please select a template' },
                ]}
                noStyle
              >
                <CustomSelect
                  placeholder={
                    skipAccountSelection || selectedAccount
                      ? 'Select'
                      : 'Select an account first'
                  }
                  width='100%'
                  height='36px'
                  options={templateOptions}
                  disabled={!skipAccountSelection && !selectedAccount}
                  onChange={(val) => handleTemplateChange(val as string)}
                />
              </Form.Item>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* ══════════ PICKUP ══════════ */}
            <CustomTitle
              text='Pickup'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Pickup name</label>
              <Form.Item name='pickup_name' noStyle>
                <CustomInput placeholder='Enter pickup name' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Pickup address</label>
              <Form.Item name='pickup_address' noStyle>
                <CustomInput placeholder='Enter pickup address' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Address line 2</label>
              <Form.Item name='pickup_address2' noStyle>
                <CustomInput placeholder='Optional' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Address line 3</label>
              <Form.Item name='pickup_address3' noStyle>
                <CustomInput placeholder='Optional' height='36px' />
              </Form.Item>
            </div>

            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className={styles.label}>City</label>
                <Form.Item name='pickup_city' noStyle>
                  <CustomInput placeholder='Enter city' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label className={styles.label}>State</label>
                <Form.Item name='pickup_state' noStyle>
                  <CustomInput placeholder='Enter state' height='36px' />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className={styles.label}>Zip code</label>
                <Form.Item name='pickup_zip' noStyle>
                  <CustomInput placeholder='Enter zip code' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label className={styles.label}>Country</label>
                <Form.Item name='pickup_country' noStyle>
                  <CustomInput placeholder='Enter country' height='36px' />
                </Form.Item>
              </Col>
            </Row>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Person to contact</label>
              <Form.Item
                name='pickup_person'
                initialValue={username ?? ''}
                noStyle
              >
                <CustomInput height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Phone number</label>
              <Form.Item name='pickup_phone' noStyle>
                <CustomInput placeholder='Enter phone number' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Email</label>
              <Form.Item name='pickup_email' noStyle>
                <CustomInput height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Special instructions</label>
              <Form.Item name='pickup_special_instructions' noStyle>
                <TextArea
                  rows={4}
                  style={{
                    borderRadius: 8,
                    backgroundColor: 'var(--light-gray)',
                    border: 'none',
                    resize: 'none',
                  }}
                />
              </Form.Item>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* ══════════ DELIVERY ══════════ */}
            <CustomTitle
              text='Delivery'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Delivery name</label>
              <Form.Item name='delivery_name' noStyle>
                <CustomInput placeholder='Enter delivery name' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Delivery address</label>
              <Form.Item name='delivery_address' noStyle>
                <CustomInput
                  placeholder='Enter delivery address'
                  height='36px'
                />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Address line 2</label>
              <Form.Item name='delivery_address2' noStyle>
                <CustomInput placeholder='Optional' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Address line 3</label>
              <Form.Item name='delivery_address3' noStyle>
                <CustomInput placeholder='Optional' height='36px' />
              </Form.Item>
            </div>

            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className={styles.label}>City</label>
                <Form.Item name='delivery_city' noStyle>
                  <CustomInput placeholder='Enter city' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label className={styles.label}>State</label>
                <Form.Item name='delivery_state' noStyle>
                  <CustomInput placeholder='Enter state' height='36px' />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className={styles.label}>Zip code</label>
                <Form.Item name='delivery_zip' noStyle>
                  <CustomInput placeholder='Enter zip code' height='36px' />
                </Form.Item>
              </Col>
              <Col span={12}>
                <label className={styles.label}>Country</label>
                <Form.Item name='delivery_country' noStyle>
                  <CustomInput placeholder='Enter country' height='36px' />
                </Form.Item>
              </Col>
            </Row>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Person to contact</label>
              <Form.Item
                name='delivery_person'
                initialValue={username ?? ''}
                noStyle
              >
                <CustomInput height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Phone number</label>
              <Form.Item name='delivery_phone' noStyle>
                <CustomInput placeholder='Enter phone number' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Email</label>
              <Form.Item name='delivery_email' noStyle>
                <CustomInput height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Special instructions</label>
              <Form.Item name='delivery_special_instructions' noStyle>
                <TextArea
                  rows={4}
                  style={{
                    borderRadius: 8,
                    backgroundColor: 'var(--light-gray)',
                    border: 'none',
                    resize: 'none',
                  }}
                />
              </Form.Item>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* ══════════ SERVICE INFORMATION ══════════ */}
            <CustomTitle
              text='Service information'
              fontSize='18px'
              fontWeight='700'
              lineHeight='24px'
              marginBottom={16}
            />

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Shipment ready time <span className={styles.required}>*</span>
              </label>
              <Flex gap={8}>
                <Form.Item
                  name='shipment_ready_date'
                  rules={[{ required: true, message: 'Required' }]}
                  noStyle
                >
                  <CustomDatePicker dateFormat={bookingDatePickerFormat} />
                </Form.Item>
                <Form.Item name='shipment_ready_time' noStyle>
                  <CustomTimePicker />
                </Form.Item>
              </Flex>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Desired delivery time</label>
              <Radio.Group
                value={desiredDeliveryType}
                onChange={(e) => setDesiredDeliveryType(e.target.value)}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <Radio value='asap' style={{ fontSize: 14 }}>
                  Best possible delivery (ASAP)
                </Radio>
                <Flex align='center' gap={8}>
                  <Radio value='custom' style={{ fontSize: 14 }} />
                  <Form.Item name='desired_delivery_date' noStyle>
                    <CustomDatePicker
                      disabled={desiredDeliveryType !== 'custom'}
                      dateFormat={bookingDatePickerFormat}
                    />
                  </Form.Item>
                  <Form.Item name='desired_delivery_time' noStyle>
                    <CustomTimePicker
                      disabled={desiredDeliveryType !== 'custom'}
                    />
                  </Form.Item>
                </Flex>
              </Radio.Group>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Package packout date</label>
              <Flex gap={8}>
                <Form.Item name='package_packout_date' noStyle>
                  <CustomDatePicker dateFormat={bookingDatePickerFormat} />
                </Form.Item>
                <Form.Item name='package_packout_time' noStyle>
                  <CustomTimePicker />
                </Form.Item>
              </Flex>
            </div>

            {!skipServiceTypeSelection && (
              <div className={styles.fieldWrap}>
                <label className={styles.label}>
                  Service type <span className={styles.required}>*</span>
                </label>
                <Form.Item
                  name='service_type'
                  rules={[
                    { required: true, message: 'Please select a service type' },
                  ]}
                  noStyle
                >
                  <CustomSelect
                    placeholder='Select'
                    width='100%'
                    height='36px'
                    options={serviceTypeOptions}
                  />
                </Form.Item>
              </div>
            )}

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Commodity <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='commodity'
                rules={[
                  {
                    validator: (_, value) => {
                      if (value != null && String(value).trim().length > 0) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Enter commodity'));
                    },
                  },
                ]}
                noStyle
              >
                <CustomInput placeholder='Enter commodity' height='36px' />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>
                Commodity temperature <span className={styles.required}>*</span>
              </label>
              <Form.Item
                name='commodity_temperature'
                rules={[
                  {
                    required: true,
                    message: 'Please select a commodity temperature',
                  },
                ]}
                noStyle
              >
                <CustomSelect
                  placeholder='Select'
                  width='100%'
                  height='36px'
                  options={commodityTemperatureSelectOptions}
                  showSearch
                  optionFilterProp='label'
                />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Packaging</label>
              <Form.Item name='packaging' noStyle>
                <CustomSelect
                  placeholder='Select'
                  width='100%'
                  height='36px'
                  options={packagingSelectOptions}
                  showSearch
                  optionFilterProp='label'
                  allowClear
                />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Shipment reference</label>
              <Form.Item name='shipment_reference' noStyle>
                <CustomInput
                  placeholder='Enter shipment reference'
                  height='36px'
                />
              </Form.Item>
            </div>

            <div className={styles.fieldWrap}>
              <label className={styles.label}>Emergency contact number</label>
              <Form.Item name='emergency_contact_number' noStyle>
                <CustomInput
                  placeholder='Enter emergency contact number'
                  height='36px'
                />
              </Form.Item>
            </div>

            <Divider style={{ margin: '24px 0 16px' }} />

            <div
              role='button'
              tabIndex={0}
              aria-expanded={quickstatEmailNotifPrefsExpanded}
              aria-controls='quickstat-email-notif-prefs-panel'
              onClick={() => setQuickstatEmailNotifPrefsExpanded((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setQuickstatEmailNotifPrefsExpanded((v) => !v);
                }
              }}
              className={
                quickstatEmailNotifPrefsExpanded
                  ? `${styles.prefsHeader} ${styles.prefsHeaderExpanded}`
                  : styles.prefsHeader
              }
            >
              <Text
                strong
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: '24px',
                  margin: 0,
                }}
              >
                Email notification preferences
              </Text>
              {quickstatEmailNotifPrefsExpanded ? (
                <ChevronUpIcon size={16} color='var(--text-secondary)' />
              ) : (
                <ChevronDownIcon size={16} color='var(--text-secondary)' />
              )}
            </div>
            {!quickstatEmailNotifPrefsExpanded && (
              <Text
                type='secondary'
                style={{ display: 'block', fontSize: 13, marginBottom: 8 }}
              >
                Optional — expand to configure notification emails
              </Text>
            )}
            <div
              id='quickstat-email-notif-prefs-panel'
              className={
                quickstatEmailNotifPrefsExpanded
                  ? `${styles.prefsPanel} ${styles.prefsPanelExpanded}`
                  : styles.prefsPanel
              }
            >
              <Row gutter={[4, 8]} style={{ marginBottom: 4 }}>
                <Col span={9}>
                  <Text strong style={{ fontSize: 12 }}>
                    Event
                  </Text>
                </Col>
                <Col span={5} style={{ textAlign: 'center' }}>
                  <Text strong style={{ fontSize: 12 }}>
                    Myself
                  </Text>
                </Col>
                <Col span={5} style={{ textAlign: 'center' }}>
                  <Text strong style={{ fontSize: 12 }}>
                    Shipper
                  </Text>
                </Col>
                <Col span={5} style={{ textAlign: 'center' }}>
                  <Text strong style={{ fontSize: 12 }}>
                    Consignee
                  </Text>
                </Col>
              </Row>
              {QUICKSTAT_EMAIL_NOTIFICATION_ROWS.map((row) => (
                <Row
                  key={row.key}
                  gutter={[4, 8]}
                  align='middle'
                  style={{ padding: '3px 0' }}
                >
                  <Col span={9}>
                    <Text style={{ fontSize: 13 }}>{row.label}</Text>
                  </Col>
                  <Col span={5} style={{ textAlign: 'center' }}>
                    <Form.Item
                      name={[
                        'email_notification_preferences',
                        row.key,
                        'myself',
                      ]}
                      valuePropName='checked'
                      noStyle
                    >
                      <Checkbox />
                    </Form.Item>
                  </Col>
                  <Col span={5} style={{ textAlign: 'center' }}>
                    {row.myselfOnly ? (
                      <Text type='secondary'>—</Text>
                    ) : (
                      <Form.Item
                        name={[
                          'email_notification_preferences',
                          row.key,
                          'shipper',
                        ]}
                        valuePropName='checked'
                        noStyle
                      >
                        <Checkbox />
                      </Form.Item>
                    )}
                  </Col>
                  <Col span={5} style={{ textAlign: 'center' }}>
                    {row.myselfOnly ? (
                      <Text type='secondary'>—</Text>
                    ) : (
                      <Form.Item
                        name={[
                          'email_notification_preferences',
                          row.key,
                          'consignee',
                        ]}
                        valuePropName='checked'
                        noStyle
                      >
                        <Checkbox />
                      </Form.Item>
                    )}
                  </Col>
                </Row>
              ))}
            </div>

            <Divider style={{ margin: '24px 0 16px' }} />
          </>
        )}

        {/* ── Actions ── */}
        <Row gutter={[0, 8]}>
          <Col span={24}>
            <CustomButton
              text={
                isSpecialty
                  ? specialtyUnsavedBooking
                    ? 'Retry save'
                    : 'Book shipment'
                  : 'See confirmation'
              }
              width='100%'
              height='48px'
              bgColor='#000'
              color='#fff'
              borderRadius='8px'
              border='none'
              fontSize='14px'
              fontWeight='400'
              disabled={prepareSubmitting}
              onClick={() => submitBooking(false)}
            />
          </Col>

          {specialtyUnsavedBooking && (
            <Col span={24}>
              <Typography.Text type='warning'>
                Booked with {specialtyUnsavedBooking.providerTitle} as{' '}
                {specialtyUnsavedBooking.trackingNumber}, but not saved yet.
                Retry the save instead of booking again.
              </Typography.Text>
              <Flex gap={8} style={{ marginTop: 8 }}>
                {!isSpecialty && (
                  <Button
                    disabled={prepareSubmitting}
                    onClick={() => {
                      const _ = retrySpecialtySave();
                    }}
                  >
                    Retry save
                  </Button>
                )}
                <Button
                  type='link'
                  disabled={prepareSubmitting}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Dismiss the unsaved booking?',
                      content: `The ${specialtyUnsavedBooking.providerTitle} booking ${specialtyUnsavedBooking.trackingNumber} stays with the carrier but will not be saved here. Record or cancel it with the carrier first.`,
                      okText: 'Dismiss',
                      onOk: () => setSpecialtyUnsavedBooking(null),
                    });
                  }}
                >
                  Dismiss
                </Button>
              </Flex>
            </Col>
          )}

          {isSpecialty &&
            !specialtyUnsavedBooking &&
            specialtyBookingFailure &&
            needsConfirmation(specialtyBookingFailure) && (
              <Col span={24}>
                <CustomButton
                  text='Book anyway'
                  width='100%'
                  height='48px'
                  bgColor='var(--gray-300)'
                  color='var(--text-primary)'
                  borderRadius='8px'
                  border='1px solid #D0D0D0'
                  fontSize='14px'
                  fontWeight='400'
                  disabled={prepareSubmitting}
                  onClick={() => submitSpecialtyBooking(true)}
                />
              </Col>
            )}

          {!isSpecialty && !isWorldCourier && !isCryoport && (
            <Col span={24}>
              <CustomButton
                text='See confirmation and update template'
                width='100%'
                height='48px'
                bgColor='var(--gray-300)'
                color='var(--text-primary)'
                borderRadius='8px'
                border='1px solid #D0D0D0'
                fontSize='14px'
                fontWeight='400'
                disabled={prepareSubmitting}
                onClick={() => submitBooking(true)}
              />
            </Col>
          )}
          <Col span={24} style={{ textAlign: 'center' }}>
            <Button
              type='text'
              onClick={onClose}
              block
              style={{
                fontWeight: 400,
                color: 'var(--text-primary)',
                fontSize: 14,
                height: 'auto',
                padding: '8px 8px',
                boxShadow: 'none',
              }}
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </div>
    </Form>
  );
};

export default BookShipmentDrawer;
